import { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo, Fragment } from "react";
import type { CSSProperties } from "react";
import type { Credentials, ConnectionInfo, StateMap } from "./types";
import { buildClient, type WPClient, type LsgRedirect } from "./lib/wpClient";
import { SchemaSetupDialog } from "./components/SchemaSetupDialog";
import { SidebarSettings } from "./components/SidebarSettings";
import { loadState, saveState, setApproved, setIgnored, setDone, clearState, getCounts } from "./lib/auditState";
import { executeFixById as runFix, metaKeysFor, describeConnection, type FixResult } from "./lib/fixExecutor";
import { setSuppressLsgTags } from "./lib/tagPolicy";
import { AuditSection, REFINED_TABS } from "./components/AuditSection";
import { ContentDrawer } from "./components/ContentDrawer";
import { BulkPageSchema } from "./components/BulkPageSchema";
import { BulkInternalLinks } from "./components/BulkInternalLinks";
import { BulkMetaData } from "./components/BulkMetaData";
import { SitemapTab, type SitemapRow, type SitemapEditField } from "./components/SitemapTab";
import { SitemapBulkEditDialog, type BulkEditValues } from "./components/SitemapBulkEditDialog";
import { EditPageDialog } from "./components/EditPageDialog";
import { GbpReviewManager } from "./components/GbpReviewManager";
import { ServiceAreaBuildout } from "./components/ServiceAreaBuildout";
import { ConversationalSeo } from "./components/ConversationalSeo";
import { LowHangingKw } from "./components/LowHangingKw";
import { CannibalizationTools } from "./components/CannibalizationTools";
import { SchemaHistory } from "./components/SchemaHistory";
import { LlmMentionTracking } from "./components/LlmMentionTracking";
import { OrganicRankTracker } from "./components/OrganicRankTracker";
import { LlmVisibilityScanner } from "./components/LlmVisibilityScanner";
import { CitationCheck } from "./components/CitationCheck";
import { HistoryTab } from "./components/HistoryTab";
import { NicheAdsScraper } from "./components/NicheAdsScraper";
import { TopicalCoverageEngine } from "./components/TopicalCoverageEngine";
import { RedirectionManager } from "./components/RedirectionManager";
import { PowerToolHeader } from "./components/PowerToolHeader";
import { CredentialsBar } from "./components/CredentialsBar";
import { loadAhrefsKey, loadAnthropicKey, loadDataForSeoCredentials } from "./components/SettingsModal";
import type { AuthUser } from "./lib/authClient";
import { recordHistory, listSites, getSite, type HistoryAction, type SiteSummary } from "./lib/authClient";
import { scheduleFlush, flushNow, siteKey, getActiveSiteKey, setActiveSiteKeyLocal, setActiveSiteServer, setSiteJobActive } from "./lib/siteData";
import { startCrawlJob, subscribeCrawlJob, listActiveJobs, cancelCrawlJob, resumeCrawlJob, pauseCrawlJobBeacon } from "./lib/crawlJobs";
import { DialogHost } from "./components/DialogHost";
import { showAlert, showConfirm } from "./lib/dialog";
import { loadHidden, saveHidden, toggleHidden, stripHiddenCards, type HiddenMap } from "./lib/hiddenFixes";

interface SectionAnalysis {
  id: string;
  priority: "critical" | "high" | "medium" | "low";
  summary: string;
  keyFindings: string[];
  topActions: string[];
}

// ── HTML section imports ──────────────────────────────────────
import { HTML_COVER } from "./sections/cover";
import { HTML_NAV } from "./sections/nav";
import { HTML_OVERVIEW } from "./sections/sec_overview";
import { HTML_COMPETITORS } from "./sections/sec_competitors";
import { HTML_TOPPAGES } from "./sections/sec_toppages";
import { HTML_ANCHORS } from "./sections/sec_anchors";
import { HTML_REFDOMAINS } from "./sections/sec_refdomains";
import { HTML_ONPAGE } from "./sections/sec_onpage";
import { HTML_SPEED } from "./sections/sec_speed";
import { HTML_SCHEMA } from "./sections/sec_schema";
import { HTML_ENTITIES } from "./sections/sec_entities";
import { HTML_INTERNAL } from "./sections/sec_internal";
import { HTML_URLS } from "./sections/sec_urls";
import { HTML_CITATIONS } from "./sections/sec_citations";
import { HTML_GOVLINKS } from "./sections/sec_govlinks";
import { HTML_FAQS } from "./sections/sec_faqs";
import { HTML_GAPS } from "./sections/sec_gaps";
import { HTML_GBP } from "./sections/sec_gbp";
import { HTML_CANNIBAL } from "./sections/sec_cannibal";
import { HTML_ROADMAP } from "./sections/sec_roadmap";

// Content Drawer — a home for content/pages created with the tool (placeholder for now).
const HTML_CONTENT_DRAWER = `
<div class="sh"><h2>Content Drawer</h2><p>Pages &amp; content created with the tool</p></div>
<div class="sub">
  <div class="note note-b">The Content Drawer holds the pages this tool has published to your WordPress site. Publish pages from any tab (Top Pages, Pages &amp; Meta, FAQs…) and they'll be collected here for easy reference.</div>
</div>`;

const SECTIONS: { id: string; label: string; html: string }[] = [
  { id: "overview",    label: "Overview",         html: HTML_OVERVIEW    },
  { id: "competitors", label: "Competitors",      html: HTML_COMPETITORS },
  { id: "toppages",    label: "Top Pages",        html: HTML_TOPPAGES    },
  { id: "anchors",     label: "Anchor Text",      html: HTML_ANCHORS     },
  { id: "refdomains",  label: "Ref Domains",      html: HTML_REFDOMAINS  },
  { id: "onpage",      label: "On-Page SEO",      html: HTML_ONPAGE      },
  { id: "speed",       label: "Page Speed",       html: HTML_SPEED       },
  { id: "schema",      label: "Schema",           html: HTML_SCHEMA      },
  { id: "entities",    label: "NLP Entities",     html: HTML_ENTITIES    },
  { id: "internal",    label: "Internal Links",   html: HTML_INTERNAL    },
  { id: "urls",        label: "Pages & Meta",     html: HTML_URLS        },
  { id: "citations",   label: "Citations",        html: HTML_CITATIONS   },
  { id: "govlinks",    label: "Gov/Local Links",  html: HTML_GOVLINKS    },
  { id: "faqs",        label: "PAA FAQs",         html: HTML_FAQS        },
  { id: "gaps",        label: "Keyword Gap",      html: HTML_GAPS        },
  { id: "gbp",         label: "GBP Optimization", html: HTML_GBP         },
  { id: "cannibal",    label: "Cannibalization",  html: HTML_CANNIBAL    },
  { id: "roadmap",     label: "90-Day Roadmap",   html: HTML_ROADMAP     },
  { id: "contentdrawer", label: "Content Drawer", html: HTML_CONTENT_DRAWER },
  // Direct-access tool tabs — duplicates of the bulk sections, no audit HTML.
  { id: "expressschema",   label: "Express Schema",         html: "" },
  { id: "expressinternal", label: "Express Internal Links", html: "" },
  { id: "expressmeta",     label: "Express Meta Data",      html: "" },
  { id: "sitemap",         label: "Quick Edit Sitemap",     html: "" },
  { id: "redirectionmanager", label: "Redirection Manager",   html: "" },
  { id: "lowhangingkw",    label: "Low-Hanging KW Insertion", html: "" },
  { id: "llmtracking",     label: "LLM Mention Tracking",     html: "" },
  { id: "organicrank",     label: "Organic Rank Tracker",     html: "" },
  { id: "llmvisibility",   label: "LLM Visibility Scanner",   html: "" },
  { id: "gbpreviews",      label: "GBP Review Manager",       html: "" },
  { id: "servicearea",     label: "Service Area Buildout",    html: "" },
  { id: "citationcheck",   label: "Indexed Citations Check",   html: "" },
  { id: "conversational",  label: "Conversational SEO",       html: "" },
  { id: "nicheadsscraper", label: "Niche Ads Scraper",        html: "" },
  { id: "topicalcoverage", label: "Topical Coverage Engine",  html: "" },
  // History is rendered from a dedicated footer button (above Settings), NOT the sidebar tab map, but
  // it needs a SECTIONS entry so `activeSec = SECTIONS.find(...)` resolves and the render switch branches.
  { id: "history",         label: "History",                  html: "" },
];

// The audit tabs a full audit generates (everything except the Power Tools tabs) — used to
// compute the Run Full Audit progress percentage from the count of completed tabs.
const AUDIT_TAB_IDS = SECTIONS.filter(s => s.id !== "contentdrawer" && s.id !== "sitemap" && s.id !== "llmtracking" && s.id !== "organicrank" && s.id !== "llmvisibility" && s.id !== "gbpreviews" && s.id !== "servicearea" && s.id !== "citationcheck" && s.id !== "conversational" && s.id !== "redirectionmanager" && s.id !== "history" && s.id !== "nicheadsscraper" && s.id !== "topicalcoverage" && !s.id.startsWith("express")).map(s => s.id);
const AUDIT_TAB_COUNT = AUDIT_TAB_IDS.length;

// Power Tools tabs — the standalone tools under the "Power Tools" heading. They get the
// tinted-tile treatment (icon + Oswald caps) in the sidebar to set them apart from audit tabs.
const POWER_TOOL_IDS = new Set(["contentdrawer", "sitemap", "lowhangingkw", "redirectionmanager", "llmtracking", "organicrank", "llmvisibility", "gbpreviews", "servicearea", "citationcheck", "conversational", "nicheadsscraper", "topicalcoverage"]);
const isPowerTool = (id: string) => POWER_TOOL_IDS.has(id) || id.startsWith("express");
// One monoline icon per Power Tool, shown in its sidebar tile.
function ToolIcon({ id }: { id: string }) {
  const P: Record<string, JSX.Element> = {
    contentdrawer: <path d="M3 8h18M6 8l1.2-3.2A2 2 0 0 1 9 3.5h6a2 2 0 0 1 1.8 1.3L18 8M4 8v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />,
    express: <polygon points="13 2 4 14 11 14 10.5 22 20 9 12.5 9 13 2" />,
    sitemap: <><rect x="9" y="3" width="6" height="5" rx="1" /><rect x="3" y="16" width="6" height="5" rx="1" /><rect x="15" y="16" width="6" height="5" rx="1" /><path d="M12 8v3M6 16v-2h12v2" /></>,
    lowhangingkw: <><circle cx="8" cy="15" r="4" /><path d="M11 12l7-7 3 3M16 7l2 2" /></>,
    redirectionmanager: <><polyline points="15 4 20 9 15 14" /><path d="M20 9H9a5 5 0 0 0-5 5v6" /></>,
    llmtracking: <><path d="M12 3l1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7z" /><path d="M18.5 14l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" /></>,
    organicrank: <><polyline points="3 17 9 11 13 15 21 7" /><polyline points="15 7 21 7 21 13" /></>,
    llmvisibility: <><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>,
    gbpreviews: <polygon points="12 3 14.3 8.6 20.5 9.1 15.8 13.2 17.2 19.2 12 15.9 6.8 19.2 8.2 13.2 3.5 9.1 9.7 8.6" />,
    servicearea: <><path d="M12 21s6.5-5.8 6.5-11A6.5 6.5 0 0 0 5.5 10c0 5.2 6.5 11 6.5 11z" /><circle cx="12" cy="9.7" r="2.3" /></>,
    citationcheck: <><circle cx="12" cy="12" r="8.5" /><path d="M8.5 12.2l2.4 2.4 4.6-4.8" /></>,
    conversational: <path d="M20.5 14.5a2 2 0 0 1-2 2H8l-4.5 3.5v-14a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />,
    nicheadsscraper: <><path d="M3 10v4a1 1 0 0 0 1 1h3l5 4V5L7 9H4a1 1 0 0 0-1 1z" /><path d="M16 9a4 4 0 0 1 0 6" /><path d="M19 6a8 8 0 0 1 0 12" /></>,
    topicalcoverage: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M17.5 14v7M14 17.5h7" /></>,
  };
  const icon = id.startsWith("express") ? P.express : (P[id] ?? P.express);
  return (
    <svg className="snav-tool-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{icon}</svg>
  );
}

// The audit tabs that currently have real, saved content. This is the GROUND TRUTH for
// progress + whether there's anything left to resume — far more reliable than a job's
// progress.doneIds, which can be stale/inflated across re-runs and resumes.
function savedAuditTabIds(): string[] {
  let sections: Record<string, string> = {};
  try { sections = JSON.parse(localStorage.getItem("full_audit_sections") ?? "{}") as Record<string, string>; } catch { /* ignore */ }
  return AUDIT_TAB_IDS.filter(id => (sections[id]?.trim().length ?? 0) > 50);
}

// Prepend a live-fetched block (e.g. Citation Comparison, LLM citations) into a section's
// HTML idempotently: first remove any prior copy of it (matched by its sub-section labels —
// which Claude never generates), then insert the fresh blocks at the top. Without this,
// running the enrichment more than once (full audit + tab crawl, resume, re-run) prepends
// duplicate sub-sections.
function injectLiveBlock(current: string, html: string, labelRes: RegExp[]): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = current;
  tmp.querySelectorAll<HTMLElement>(".sub").forEach(sub => {
    const t = sub.querySelector(".sub-lbl")?.textContent ?? "";
    if (labelRes.some(re => re.test(t))) sub.remove();
  });
  const wrap = document.createElement("div");
  wrap.innerHTML = html.trim();
  const first = tmp.firstChild;
  Array.from(wrap.childNodes).forEach(n => tmp.insertBefore(n, first));
  return tmp.innerHTML;
}

// Swap the .sub whose label matches `labelRe` for the live block, keeping its position in the
// section. With no matching .sub, the block is prepended like injectLiveBlock.
function replaceLiveBlock(current: string, html: string, labelRe: RegExp): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = current;
  const subs = Array.from(tmp.querySelectorAll<HTMLElement>(".sub")).filter(sub => labelRe.test(sub.querySelector(".sub-lbl")?.textContent ?? ""));
  if (!subs.length) return injectLiveBlock(current, html, [labelRe]);
  const wrap = document.createElement("div");
  wrap.innerHTML = html.trim();
  Array.from(wrap.childNodes).forEach(n => subs[0].parentNode!.insertBefore(n, subs[0]));
  subs.forEach(sub => sub.remove());
  return tmp.innerHTML;
}

// Id of the currently-running full-audit job, mirrored to localStorage so the unload
// handler (and logout) can pause it even after the React refs are gone.
const ACTIVE_AUDIT_JOB_KEY = "lsg_active_audit_job";

// Collect the app's CSS for a SELF-CONTAINED export. Copying <link>/<style> TAGS breaks the
// HTML export: in production the app CSS is a linked /assets/*.css file, so a downloaded HTML
// opened from disk can't resolve it and renders unstyled. Instead we read the actual CSS rules
// from each same-origin stylesheet (dev <style> AND the prod linked sheet) and inline them as
// <style> blocks. Cross-origin sheets (e.g. Google Fonts) can't be read — keep them as a <link>
// (resolves when opened online).
function collectExportStyles(): string {
  const parts: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = Array.from(sheet.cssRules).map(r => r.cssText).join("\n");
      if (rules) parts.push(`<style>${rules}</style>`);
    } catch {
      if (sheet.href) parts.push(`<link rel="stylesheet" href="${sheet.href}">`);
    }
  }
  return parts.join("\n");
}

// ── Custom Competitors mode helpers ───────────────────────────────────────────
// The cover's "Custom Competitors" toggle flips competitor sourcing between the keyword
// SERP search (default) and a user-entered list of up to 5 competitor domains. The mode is
// persisted per-site under seo_competitor_mode (localStorage bucket is swapped per site).
function getCompetitorMode(): "keyword" | "custom" {
  try { return localStorage.getItem("seo_competitor_mode") === "custom" ? "custom" : "keyword"; } catch { return "keyword"; }
}
function competitorBtnLabel(): string {
  return getCompetitorMode() === "custom" ? "Save Competitors" : "Get Competitors";
}
// Read the up-to-5 custom competitor domain inputs from the cover, trimmed + non-empty.
function readCustomCompetitors(): string[] {
  const out: string[] = [];
  for (let i = 1; i <= 5; i++) {
    const v = (document.getElementById(`cover-comp-${i}`) as HTMLInputElement | null)?.value?.trim() ?? "";
    if (v) out.push(v);
  }
  return out;
}

// Merge tag-name lists case-insensitively, preserving existing order + casing and appending
// only the genuinely new names — so bulk-adding tags never duplicates ones a page already has.
function mergeTags(existing: string[], added: string[]): string[] {
  const seen = new Set(existing.map(t => t.toLowerCase()));
  const out = [...existing];
  for (const t of added) {
    const k = t.toLowerCase();
    if (k && !seen.has(k)) { seen.add(k); out.push(t); }
  }
  return out;
}

// Build export HTML for the Cannibalization tab's tool sections (Keyword Cannibalization +
// SERP Flickering), which are rendered by a live-only React component and so aren't in the
// exported section HTML. Rebuilt here from the persisted results (lsg_cannibal_results), and
// only included when those results belong to the site being exported. Uses the same cd-*
// classes as the live component (now inlined into exports).
function buildCannibalizationExportHtml(currentSiteKey: string): string {
  interface CPage { page: string; clicks: number; impressions: number; position: number | null }
  interface CQuery { query: string; totalImpressions: number; totalClicks: number; pages: CPage[] }
  interface FPage { page: string; status: string; daysVisible: number; impressions: number; avgPosition: number | null }
  let stored: { siteKey?: string; cannibalization?: CQuery[]; flux?: FPage[] } | null = null;
  try { stored = JSON.parse(localStorage.getItem("lsg_cannibal_results") ?? "null"); } catch { return ""; }
  if (!stored) return "";
  if (currentSiteKey && stored.siteKey && stored.siteKey !== currentSiteKey) return ""; // other site's data
  const cannibalization = stored.cannibalization ?? [];
  const flux = stored.flux ?? [];
  if (!cannibalization.length && !flux.length) return "";

  const esc = (s: unknown) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const path = (u: string) => { try { return new URL(u).pathname || "/"; } catch { return u; } };
  const num = (n: number) => (typeof n === "number" ? n : 0).toLocaleString();
  const tone: Record<string, string> = {
    "Dropped Out": "color:#b91c1c;background:#fee2e2;border:1px solid #fecaca",
    "Newly Appeared": "color:#166534;background:#dcfce7;border:1px solid #bbf7d0",
    "Intermittent": "color:#9a3412;background:#ffedd5;border:1px solid #fed7aa",
  };
  const cards = cannibalization.map(q => `<div style="border:1px solid #e2e8f0;border-radius:8px;background:#fff;overflow:hidden;margin-bottom:12px">
    <div style="background:#def5e7;border-bottom:1px solid #c7ecd6;padding:9px 13px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
      <span style="font-size:13px;font-weight:700;color:#0f172a">${esc(q.query)}</span>
      <span style="font-size:11px;color:#166534;font-weight:600">${q.pages.length} pages · ${num(q.totalImpressions)} impressions · ${num(q.totalClicks)} clicks</span></div>
    <table class="cd-history" style="margin:0;table-layout:fixed;width:100%"><colgroup><col/><col style="width:120px"/><col style="width:120px"/><col style="width:100px"/></colgroup>
      <thead><tr><th>Competing Page</th><th>Avg Position</th><th>Impressions</th><th>Clicks</th></tr></thead>
      <tbody>${q.pages.map(pg => `<tr><td>${esc(path(pg.page))}</td><td style="color:#ea580c;font-weight:700">${pg.position ?? "—"}</td><td>${num(pg.impressions)}</td><td>${num(pg.clicks)}</td></tr>`).join("")}</tbody></table></div>`).join("");
  const rows = flux.map(p => `<tr><td>${esc(path(p.page))}</td><td><span style="font-size:10.5px;font-weight:700;border-radius:10px;padding:2px 9px;white-space:nowrap;${tone[p.status] ?? ""}">${esc(p.status)}</span></td><td>${p.daysVisible} / 30</td><td>${num(p.impressions)}</td><td style="color:#ea580c;font-weight:700">${p.avgPosition ?? "—"}</td></tr>`).join("");

  return `<div style="margin-top:24px">
    <div class="cd-section"><div class="cd-section-hd"><span class="cd-hd-brand">Keyword Cannibalization</span></div>
      <p class="cd-help">Search queries where two or more of your pages rank — they compete with each other, splitting clicks. Consider consolidating or differentiating the competing pages.</p>
      ${cannibalization.length ? cards : '<p class="cd-help">No cannibalized keywords found.</p>'}</div>
    <div class="cd-section"><div class="cd-section-hd"><span class="cd-hd-brand">SERP Flickering — Last 30 Days</span></div>
      <p class="cd-help">Pages whose Search Console visibility is unstable over the last 30 days — dropping out of the results and reappearing rather than ranking steadily.</p>
      ${flux.length ? `<table class="cd-history"><thead><tr><th>Page</th><th>Status</th><th>Days Visible</th><th>Impressions</th><th>Avg Position</th></tr></thead><tbody>${rows}</tbody></table>` : '<p class="cd-help">No flickering pages found.</p>'}</div>
  </div>`;
}

// Build the Sitemap tab's export body (a plain table) from the stored rows — used by the
// per-tab save (PDF/HTML) since the Sitemap tab has no section HTML of its own.
function buildSitemapExportHtml(rows: SitemapRow[]): string {
  const esc = (s: unknown) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const cols = ["Page ID", "Page Title", "URL", "Page/Post Tags", "Meta Title", "Meta Description", "H1", "H2", "H3", "H4", "H5", "H6", "Word Count", "Status Code"];
  const head = cols.map(h => `<th>${h}</th>`).join("");
  const body = rows.map(r =>
    `<tr><td>${r.id}</td><td>${esc(r.title)}</td>` +
    `<td><a href="${esc(r.url)}">${esc(r.url)}</a></td>` +
    `<td>${esc(r.tags.join(", "))}</td><td>${esc(r.metaTitle)}</td><td>${esc(r.metaDescription)}</td>` +
    `<td>${r.h1}</td><td>${r.h2}</td><td>${r.h3}</td><td>${r.h4}</td><td>${r.h5}</td><td>${r.h6}</td>` +
    `<td>${r.wordCount}</td><td>${r.statusCode ?? "ERR"}</td></tr>`,
  ).join("");
  return `<div class="sub"><div class="sub-lbl">Sitemap — ${rows.length} page${rows.length === 1 ? "" : "s"}</div><div class="tw"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div></div>`;
}

function buildSitemapCsv(rows: SitemapRow[]): string {
  const cols = ["Page ID", "Page Title", "URL", "Page/Post Tags", "Meta Title", "Meta Description", "H1", "H2", "H3", "H4", "H5", "H6", "Word Count", "Status Code"];
  // Quote every field and escape embedded quotes — RFC 4180.
  const cell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const line = (vals: unknown[]) => vals.map(cell).join(",");
  const out = [line(cols)];
  for (const r of rows) {
    out.push(line([
      r.id, r.title, r.url, r.tags.join(", "), r.metaTitle, r.metaDescription,
      r.h1, r.h2, r.h3, r.h4, r.h5, r.h6, r.wordCount, r.statusCode ?? "ERR",
    ]));
  }
  return out.join("\r\n");
}

interface AuditToast { type: "success" | "error" | "info"; message: string }

// Shown while an audit or section crawl is actively running
const PLACEHOLDER_HTML = `
<style>@keyframes seo-spin{to{transform:rotate(360deg)}} @keyframes seo-pulse{0%,100%{opacity:.6}50%{opacity:1}}</style>
<div class="placeholder-section" style="display:flex;align-items:center;justify-content:center;min-height:340px;background:#f8fafc;">
  <div style="text-align:center;padding:48px 32px;max-width:400px;">
    <div style="margin:0 auto 28px;width:56px;height:56px;">
      <svg class="ps-spinner" style="animation:seo-spin 1.1s linear infinite;width:56px;height:56px;" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle class="ps-spin-track" cx="28" cy="28" r="22" stroke="#e2e8f0" stroke-width="4"/>
        <path class="ps-spin-arc" d="M28 6 A22 22 0 0 1 50 28" stroke="#4b5320" stroke-width="4" stroke-linecap="round"/>
      </svg>
    </div>
    <div class="ps-title" style="font-family:'Inter','Mulish',sans-serif;font-size:19px;font-weight:700;color:#0f172a;margin-bottom:10px;letter-spacing:-.3px;animation:seo-pulse 1.8s ease-in-out infinite">Generating Section Data…</div>
    <div class="ps-desc" style="font-family:'Inter','Mulish',sans-serif;font-size:13px;color:#64748b;line-height:1.7;margin-bottom:24px;">Pulling live data and building your tab. This usually takes 30–90 seconds.</div>
    <div class="ps-badge" style="display:inline-flex;align-items:center;gap:8px;background:rgba(75,83,32,.08);border:1.5px solid rgba(75,83,32,.25);border-radius:20px;padding:7px 18px;">
      <span class="ps-badge-dot" style="width:8px;height:8px;border-radius:50%;background:#4b5320;display:inline-block;flex-shrink:0;animation:seo-pulse 1.2s ease-in-out infinite"></span>
      <span class="ps-badge-txt" style="font-family:'Inter','Mulish',sans-serif;font-size:11px;font-weight:700;color:#4b5320;letter-spacing:.3px;text-transform:uppercase;">Live Crawl Running</span>
    </div>
  </div>
</div>`;

// Shown when data has been cleared and nothing is running
const EMPTY_SECTION_HTML = `
<div class="empty-section" style="display:flex;align-items:center;justify-content:center;min-height:340px;background:#f8fafc;">
  <div style="text-align:center;padding:48px 32px;max-width:400px;">
    <div style="font-size:64px;margin-bottom:24px;line-height:1;">📊</div>
    <div class="es-title" style="font-family:'Inter','Mulish',sans-serif;font-size:20px;font-weight:700;color:#0f172a;margin-bottom:10px;letter-spacing:-.3px;">No Data Yet</div>
    <div class="es-desc" style="font-family:'Inter','Mulish',sans-serif;font-size:13px;color:#64748b;line-height:1.7;margin-bottom:24px;">
      Click <strong style="color:#f97316;font-weight:700;">Run Crawl</strong> to generate live data for this tab using your retrieved competitors.
    </div>
    <div class="es-badge" style="display:inline-flex;align-items:center;gap:8px;background:#fff7ed;border:1.5px solid #fed7aa;border-radius:20px;padding:7px 18px;">
      <span style="width:8px;height:8px;border-radius:50%;background:#f97316;display:inline-block;flex-shrink:0;"></span>
      <span style="font-family:'Inter','Mulish',sans-serif;font-size:11px;font-weight:700;color:#c2410c;letter-spacing:.3px;text-transform:uppercase;">Awaiting Crawl</span>
    </div>
  </div>
</div>`;

// WordPress credentials are kept in sessionStorage so the app password the user
// entered stays filled in across refreshes within the same browser session
// (and is cleared when the tab closes). The app does NOT auto-reconnect — the
// user clicks Connect — but they don't have to retype the password.
const WP_CREDS_KEY = "seo_wp_creds";
// Set when the user explicitly clicks Disconnect, so a refresh does NOT silently auto-reconnect.
// Cleared on any successful (re)connect. Session-scoped, like the credentials themselves.
const WP_DISCONNECTED_KEY = "seo_wp_disconnected";
const WP_CREDS_DEFAULT: Credentials = { siteUrl: "https://testing.teenproof.com/", username: "grclaudeseo", appPassword: "" };

function loadCreds(): Credentials {
  try {
    const raw = sessionStorage.getItem(WP_CREDS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Credentials>;
      return {
        siteUrl:     typeof p.siteUrl     === "string" ? p.siteUrl     : WP_CREDS_DEFAULT.siteUrl,
        username:    typeof p.username    === "string" ? p.username    : WP_CREDS_DEFAULT.username,
        appPassword: typeof p.appPassword === "string" ? p.appPassword : "",
      };
    }
  } catch { /* ignore malformed storage */ }
  return { ...WP_CREDS_DEFAULT };
}

function saveCreds(c: Credentials) {
  try { sessionStorage.setItem(WP_CREDS_KEY, JSON.stringify(c)); } catch { /* ignore */ }
}

// Drop the per-browser WordPress connection state (session-scoped credentials + the explicit
// "disconnected" flag). Called on an explicit login/logout so a different account signing in on the
// same tab never inherits the previous account's WP connection and silently auto-connects to a site
// it doesn't own. (Refresh-resume keeps its own creds — it doesn't call this.)
export function clearWpConnection(): void {
  try { sessionStorage.removeItem(WP_CREDS_KEY); } catch { /* ignore */ }
  try { sessionStorage.removeItem(WP_DISCONNECTED_KEY); } catch { /* ignore */ }
}

interface AppProps {
  user: AuthUser;
  onLogout: () => void;
  sitesRefreshToken: number;
  onSwitchSite: (key: string, opts?: { siteUrl?: string; label?: string }) => void;
  onSitesChanged: () => void;
  onUserPatch: (patch: Partial<AuthUser>) => void; // merge server-confirmed account changes into the canonical user
}

export default function App({ user: authUser, onLogout, sitesRefreshToken, onSwitchSite, onSitesChanged, onUserPatch }: AppProps) {
  // Mirror the account's "hide LSG marker tags" setting into the tag-policy module so the page-
  // creation paths (fixExecutor) read it synchronously. Only white-label accounts can enable it.
  useEffect(() => {
    setSuppressLsgTags(!!authUser?.whiteLabel && !!authUser?.hideWpTags);
  }, [authUser?.whiteLabel, authUser?.hideWpTags]);

  const [activeSection, setActiveSection] = useState("overview");
  const [showSidebarSettings, setShowSidebarSettings] = useState(false);
  const [settingsInitialCat, setSettingsInitialCat] = useState<"profile" | "savedsites" | "apis" | "wpintegration" | "data" | undefined>(undefined);
  // True once the server reports a newer build than the one this page loaded with → shows
  // the "Update available — Please Refresh" prompt above Settings in the sidebar.
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Light/dark theme — recolors the cover + sidebar only (tab content stays as-is).
  const [theme, setTheme] = useState<"light" | "dark">(() => (localStorage.getItem("seo_theme") === "dark" ? "dark" : "light"));
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("seo_theme", theme); } catch { /* ignore */ }
  }, [theme]);
  const [toast, setToast] = useState<FixResult & { fixId: string } | null>(null);
  const [auditToast, setAuditToast] = useState<AuditToast | null>(null);
  const [auditRunning, setAuditRunning] = useState(false);
  // A full audit that was paused (tab closed / logged out) and can be resumed. The button
  // morphs into "Resume Audit"; the resumable job id lives in auditResumeIdRef.
  const [auditPaused, setAuditPaused] = useState(false);
  const auditResumeIdRef = useRef<string | null>(null);
  // Run Full Audit stays greyed out until a Get Competitors crawl has populated the site +
  // competitor data (seo_full_metrics) the audit depends on. Seeded from that key on load.
  const [competitorsReady, setCompetitorsReady] = useState<boolean>(() => {
    try { return !!localStorage.getItem("seo_full_metrics"); } catch { return false; }
  });
  // While a full audit runs, the cover credentials panel grows (the Stop Audit button
  // appears below Run Full Audit). Flag it on <html> so CSS can lower the cover divider
  // + everything under it by 15px to keep clear of the panel; reverts when the audit ends.
  useEffect(() => {
    // Running OR paused both grow the panel (an extra button under Run Full Audit), so the
    // cover divider must drop in both states.
    document.documentElement.setAttribute("data-audit-running", (auditRunning || auditPaused) ? "true" : "false");
  }, [auditRunning, auditPaused]);

  // Pause a running full audit when the tab is closed/refreshed, so it doesn't keep
  // burning API calls headless — what's crawled is already saved, and on return the
  // button shows Resume. sendBeacon survives the unload; pagehide is the reliable hook.
  useEffect(() => {
    const onHide = () => {
      const id = (() => { try { return localStorage.getItem(ACTIVE_AUDIT_JOB_KEY); } catch { return null; } })();
      if (id) pauseCrawlJobBeacon(id);
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  // ── Sitemap tab state (per-site; hydrated from the AUDIT_KEYS blob) ──
  const [sitemapRows, setSitemapRows] = useState<SitemapRow[]>(() => {
    // Default parent/slug for rows cached before those fields existed.
    try { return (JSON.parse(localStorage.getItem("seo_sitemap_rows") ?? "[]") as SitemapRow[]).map(r => ({ ...r, parent: r.parent ?? 0, slug: r.slug ?? "" })); } catch { return []; }
  });
  const [sitemapCrawling, setSitemapCrawling] = useState(false);
  const [sitemapProgress, setSitemapProgress] = useState<{ done: number; total: number } | null>(null);
  const [sitemapCrawledAt, setSitemapCrawledAt] = useState(() => { try { return localStorage.getItem("seo_sitemap_crawled_at") ?? ""; } catch { return ""; } });
  const [sitemapSelected, setSitemapSelected] = useState<Set<string>>(new Set());   // rowKeys checked
  const [sitemapRefreshing, setSitemapRefreshing] = useState<Set<string>>(new Set()); // rowKeys mid-refresh
  const [sitemapRedirects, setSitemapRedirects] = useState<LsgRedirect[]>([]);       // active redirects → the "Redirected" column
  const [sitemapSearch, setSitemapSearch] = useState("");                           // keyword filter text
  const [sitemapSearchField, setSitemapSearchField] = useState<"title" | "metaTitle" | "tag" | "url" | "status">("title"); // which column the filter searches
  const [sitemapExclude, setSitemapExclude] = useState("");                         // comma-delimited exclude terms
  const [sitemapBulkOpen, setSitemapBulkOpen] = useState(false);                    // Bulk Edit dialog open
  const [sitemapCustomUrlsOpen, setSitemapCustomUrlsOpen] = useState(false);        // "Edit Custom URLs" dialog open
  const [sitemapBulkSaving, setSitemapBulkSaving] = useState(false);                // Bulk/Parent Edit writes in flight
  const [sitemapParentEdit, setSitemapParentEdit] = useState<string | null>(null);  // row key for single-page Parent Page Edit
  const [sitemapEditPage, setSitemapEditPage] = useState<string | null>(null);      // row key for the full Edit Page dialog
  const sitemapAbortRef = useRef(false);

  // ── "Update available" detection ──────────────────────────────
  // Record the server's build id on load, then poll (and re-check on window focus). When it
  // changes — a new version was pushed to GitHub and pulled/built by the server — show the
  // "Update available — Please Refresh" prompt above Settings in the sidebar.
  useEffect(() => {
    let alive = true;
    let baseline: string | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    const check = async () => {
      try {
        const r = await fetch("/api/version", { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json() as { version?: string };
        if (!data.version || !alive) return;
        if (baseline === null) { baseline = data.version; return; } // first read = the version we loaded with
        if (data.version !== baseline) {
          setUpdateAvailable(true);
          if (timer) { clearInterval(timer); timer = null; } // no need to keep polling once detected
        }
      } catch { /* offline / dev with no dist — ignore */ }
    };
    void check();
    timer = setInterval(() => { void check(); }, 60_000);
    const onFocus = () => { void check(); };
    window.addEventListener("focus", onFocus);
    return () => { alive = false; if (timer) clearInterval(timer); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Section IDs that have finished generating in the CURRENT full-audit run.
  // While an audit runs, only tabs NOT in this set show the "Generating…" placeholder,
  // so completed tabs are viewable immediately as each section streams in.
  const [auditDoneTabs, setAuditDoneTabs] = useState<Set<string>>(new Set());
  type ApiTestResult = { ok: boolean; message: string };
  type AuditConfirmState =
    | { type: "missing_keys"; missing: string[] }
    | { type: "testing" }
    | { type: "preflight"; allPassed: boolean; ahrefs: ApiTestResult; claude: ApiTestResult; dataforseo: ApiTestResult }
    | { type: "duration" };
  const [auditConfirm, setAuditConfirm] = useState<AuditConfirmState | null>(null);
  // "seo_last_tab_crawl" is updated by Run Crawl; "cover_last_audit_date" is updated by Run Full Audit only.
  // The toolbar shows whichever is more recent.
  const [auditDate, setAuditDate] = useState<string>(
    () => localStorage.getItem("seo_last_tab_crawl") || localStorage.getItem("cover_last_audit_date") || ""
  );
  // Per-tab last-crawl dates: { [sectionId]: "Month D, YYYY" }. The toolbar shows
  // the active tab's own date so each tab reflects when IT was last run.
  const [tabCrawlDates, setTabCrawlDates] = useState<Record<string, string>>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("seo_tab_crawl_dates") ?? "{}");
      if (stored && Object.keys(stored).length) return stored;
    } catch { /* ignore */ }
    // Migration: if no per-tab dates yet but a full audit ran, seed every tab with it.
    const full = localStorage.getItem("cover_last_audit_date");
    if (full) { const seed: Record<string, string> = {}; for (const s of SECTIONS) seed[s.id] = full; return seed; }
    return {};
  });
  const markTabsCrawled = (ids: string[], date: string) => {
    setTabCrawlDates(prev => {
      const next = { ...prev };
      for (const id of ids) next[id] = date;
      try { localStorage.setItem("seo_tab_crawl_dates", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    scheduleFlush(); // persist the per-site report after any crawl completes
  };
  // After an audit/competitors run, adopt the audited domain as the active site bucket
  // (handles ad-hoc audits of a freshly-typed URL), then flush the report to the server.
  const commitActiveAndFlush = (siteUrl?: string) => {
    if (siteUrl) {
      const key = siteKey(siteUrl);
      if (key && key !== getActiveSiteKey()) {
        setActiveSiteKeyLocal(key);
        void setActiveSiteServer(key).catch(() => {});
        onSitesChanged();
      }
    }
    scheduleFlush();
  };
  // Safety net: flush the active report when the tab is hidden/closed.
  useEffect(() => {
    const onHide = () => { void flushNow(); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") onHide(); });
    return () => { window.removeEventListener("pagehide", onHide); };
  }, []);
  const [state, setState] = useState<StateMap>(loadState);
  const [sectionAnalyses, setSectionAnalyses] = useState<SectionAnalysis[]>(() => {
    try { return JSON.parse(localStorage.getItem("full_audit_analyses") ?? "[]"); } catch { return []; }
  });
  const [regeneratedSections, setRegeneratedSections] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem("full_audit_sections") ?? "{}"); } catch { return {}; }
  });
  const [auditEverRun, setAuditEverRun] = useState<boolean>(() =>
    localStorage.getItem("seo_audit_ever_run") === "true"
  );
  const [creds, setCreds] = useState<Credentials>(loadCreds);
  const [hidden, setHidden] = useState<HiddenMap>(loadHidden);
  const [connInfo, setConnInfo] = useState<ConnectionInfo | null>(null);
  const [connected, setConnected] = useState(false);
  // True while the app is auto-connecting on mount (after a Site Storage switch remounts App, or a
  // refresh restores the session) — drives the Site Storage loading spinner. Cleared on success OR
  // failure so a connection issue makes the spinner disappear.
  const [autoConnecting, setAutoConnecting] = useState(false);
  const [competitorsCrawling, setCompetitorsCrawling] = useState(false);
  // Set of section IDs currently crawling — allows multiple tabs to crawl at once.
  const [crawlingSections,    setCrawlingSections]    = useState<Set<string>>(new Set());
  // Tabs whose durable crawl job was interrupted (window/tab closed, server disconnect) —
  // their "Stop Crawl" button morphs into a blue "Resume" until re-run.
  const [interruptedSections, setInterruptedSections] = useState<Set<string>>(new Set());
  const [crawlConfirm, setCrawlConfirm] = useState<{ sectionId: string; label: string } | null>(null);
  // Crawl queue: at most MAX_CONCURRENT_CRAWLS run at once (to avoid API rate-limiting); extra
  // crawls the user starts wait here in FIFO order and auto-start as running ones finish.
  const MAX_CONCURRENT_CRAWLS = 3;
  const [crawlQueue, setCrawlQueue] = useState<{ sectionId: string; label: string }[]>([]);
  const crawlQueueRef = useRef<{ sectionId: string; label: string }[]>([]);
  const updateCrawlQueue = (next: { sectionId: string; label: string }[]) => { crawlQueueRef.current = next; setCrawlQueue(next); };
  const enqueueCrawl = (sectionId: string, label: string) => {
    if (crawlingSections.has(sectionId) || crawlQueueRef.current.some(q => q.sectionId === sectionId)) return;
    updateCrawlQueue([...crawlQueueRef.current, { sectionId, label }]);
  };
  const cancelQueuedCrawl = (sectionId: string) => updateCrawlQueue(crawlQueueRef.current.filter(q => q.sectionId !== sectionId));
  // Whenever a crawl slot frees (a running crawl finished/stopped), start the next queued
  // crawl(s) in order, up to the concurrency cap. Runs on any change to crawlingSections.
  useEffect(() => {
    const free = MAX_CONCURRENT_CRAWLS - crawlingSections.size;
    if (free <= 0 || crawlQueueRef.current.length === 0) return;
    const toStart = crawlQueueRef.current.slice(0, free);
    updateCrawlQueue(crawlQueueRef.current.slice(toStart.length));
    toStart.forEach(item => void handleRunCrawl(item.sectionId));
    // handleRunCrawl is a stable hoisted declaration; intentionally excluded from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crawlingSections]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showExportTip, setShowExportTip] = useState(false);
  const [competitorsCrawlTime, setCompetitorsCrawlTime] = useState<string | null>(
    () => localStorage.getItem("competitors_last_crawl")
  );
  const [brandMentionResults, setBrandMentionResults] = useState<
    Array<{ domain: string; urlMentions: number; brandMentions: number; brandName?: string }>
  >(() => {
    try { return JSON.parse(localStorage.getItem("competitors_brand_data") ?? "null") ?? []; }
    catch { return []; }
  });
  const clientRef = useRef<WPClient | null>(null);
  // Live mirror of connInfo so executeFixById (reachable via a stale handler
  // closure) always reads the CURRENT plugin/siteTitle, not a pre-connect value.
  const connInfoRef = useRef<ConnectionInfo | null>(null);
  // Schema tab: whether the SEO Audit schema helper is confirmed active on the
  // connected site (refs so the [state]-deps approve handler reads live values).
  const schemaReadyRef     = useRef(false);
  const schemaHelperKeyRef = useRef("");

  // Restore the WordPress connection after a page refresh. The credentials (incl. the app password)
  // persist in sessionStorage, so rather than dropping to "disconnected" we silently re-establish
  // the live client on mount — unless the user explicitly clicked Disconnect this session.
  useEffect(() => {
    if (connected) return;
    try { if (sessionStorage.getItem(WP_DISCONNECTED_KEY) === "1") return; } catch { /* ignore */ }
    let cancelled = false;
    console.warn("[conn-diag] auto-connect effect running (connected was false)");
    setAutoConnecting(true); // show the Site Storage spinner while we load + connect this site
    void (async () => {
      let c = creds;
      // Fresh session (sessionStorage had no app password): load the ACTIVE saved site's credentials
      // so Connect isn't greyed out and we can reconnect. Boot only swaps the audit data, not the WP
      // login — previously only an explicit Site Storage pick loaded creds, leaving refresh stuck.
      if (!c.appPassword) {
        const activeKey = getActiveSiteKey();
        if (activeKey) {
          const sites = await listSites().catch(() => [] as SiteSummary[]);
          const match = sites.find(s => siteKey(s.siteUrl) === activeKey);
          if (match) {
            const full = await getSite(match.id).catch(() => null);
            if (!cancelled && full && full.appPassword) {
              c = { siteUrl: full.siteUrl, username: full.username, appPassword: full.appPassword };
              setCreds(c);
              saveCreds(c); // so the Connect button enables and a later refresh restores it
            }
          }
        }
      }
      if (cancelled || !c.siteUrl || !c.username || !c.appPassword) { setAutoConnecting(false); return; }
      const attempt = () => describeConnection(c.siteUrl, c.username, c.appPassword).catch(() => null);
      let info = await attempt();
      // Retry with backoff before giving up. This is the reconnect that runs on every page
      // refresh, and refreshes most often happen right after an "Update available" deploy — when
      // the server (pm2) is mid-restart for a few seconds and every WP proxy call transiently
      // fails. Riding that out over ~5s is what lets a refresh reconnect on its own like it used
      // to, instead of landing disconnected. (The proxy also retries a 401 on a fresh connection.)
      const delays = [800, 1600, 3000];
      for (let i = 0; i < delays.length && !cancelled && !(info && info.ok); i++) {
        await new Promise(r => setTimeout(r, delays[i]));
        if (cancelled) return;
        info = await attempt();
      }
      if (cancelled) return;
      // Connect on success. On failure stay QUIET (no error banner) — a transient deploy-window
      // blip must not flash a scary "not logged in" message on a normal refresh; the user can
      // click Connect to see the real reason if it's a genuine, persistent failure.
      if (info && info.ok) handleConnect(info, c);
      else console.warn("[conn-diag] auto-connect gave up after retries", { error: info?.error });
      setAutoConnecting(false); // done — connected, or an issue occurred (spinner goes away either way)
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The pending Schema-tab approval, held while the setup dialog is shown.
  const [schemaSetup, setSchemaSetup] = useState<{ fixId: string; message?: string; isCreate?: boolean } | null>(null);
  // A business-entity enhancement that can't be auto-applied without duplicating the page's existing
  // entity — holds the pre-merged block for the user to paste over their original (see mergedSchema).
  const [schemaMerge, setSchemaMerge] = useState<{ json: string; message: string } | null>(null);
  const [schemaMergeCopied, setSchemaMergeCopied] = useState(false);
  // "View JSON-LD schema" popup — shows a schema card's validated blocks for copy/manual replace.
  const [schemaView, setSchemaView] = useState<{ json: string; url: string } | null>(null);
  const [schemaViewCopied, setSchemaViewCopied] = useState(false);
  // Schema cards that can't be auto-applied (approving would duplicate an existing business entity, so
  // they open the "Replace your schema block" popup for a manual paste). Their button reads "Manual
  // Update" and clicking it just re-opens the popup — no queue/spinner/done.
  const [manualSchema, setManualSchema] = useState<Set<string>>(new Set());
  const openSchemaView = useCallback((json: string, url: string) => { setSchemaViewCopied(false); setSchemaView({ json, url }); }, []);
  // The schema.org issues that a card auto-fixes on approve — shown in a wide popup when the issue
  // count in the card's confirmation sentence is clicked (replaces the old hover tooltip).
  const [schemaIssues, setSchemaIssues] = useState<{ issues: string[]; url: string } | null>(null);
  const openSchemaIssues = useCallback((issues: string[], url: string) => { setSchemaIssues({ issues, url }); }, []);
  const coverRef = useRef<HTMLDivElement | null>(null);
  const auditAbortRef = useRef<AbortController | null>(null);
  // The durable full-audit job: its id + a "stop listening" fn (does NOT stop the server job).
  const auditJobIdRef = useRef<string | null>(null);
  const auditJobCloseRef = useRef<(() => void) | null>(null);
  // Per-tab durable crawl jobs, keyed by sectionId → { jobId, close listener }.
  const crawlJobRef = useRef<Map<string, { jobId: string; close: () => void }>>(new Map());
  const navRef = useRef<HTMLElement | null>(null);
  const scrollTrackRef = useRef<HTMLDivElement | null>(null);
  const scrollThumbRef = useRef<HTMLDivElement | null>(null);

  // Restore cover field values synchronously before paint, re-run when HTML_COVER changes (hot reload)
  useLayoutEffect(() => {
    const FIELDS = [
      { id: "cover-business-name", key: "cover_business_name" },
      { id: "cover-website-url",   key: "cover_website_url"   },
      { id: "cover-full-address",  key: "cover_full_address"  },
      { id: "cover-gbp-category",  key: "cover_gbp_category"  },
      { id: "cover-location",      key: "cover_location"      },
      { id: "cover-kw-1",          key: "cover_kw_1"          },
      { id: "cover-kw-2",          key: "cover_kw_2"          },
      { id: "cover-kw-3",          key: "cover_kw_3"          },
      { id: "cover-kw-4",          key: "cover_kw_4"          },
      { id: "cover-kw-5",          key: "cover_kw_5"          },
      // Custom Competitors mode: the 5 user-entered competitor-domain inputs.
      { id: "cover-comp-1",        key: "cover_comp_1"        },
      { id: "cover-comp-2",        key: "cover_comp_2"        },
      { id: "cover-comp-3",        key: "cover_comp_3"        },
      { id: "cover-comp-4",        key: "cover_comp_4"        },
      { id: "cover-comp-5",        key: "cover_comp_5"        },
    ];

    // Restore saved values immediately (before browser paint)
    FIELDS.forEach(({ id, key }) => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      const saved = localStorage.getItem(key);
      if (el && saved !== null) el.value = saved;
    });

    // Sync dynamic spans with restored field values
    const nameInput    = document.getElementById("cover-business-name") as HTMLInputElement | null;
    const titleSpan    = document.getElementById("cv-title-name")       as HTMLElement | null;
    const kw1Input     = document.getElementById("cover-kw-1")          as HTMLInputElement | null;
    const kwSubSpan    = document.getElementById("cv-sub-keyword")       as HTMLElement | null;
    const auditDateEl  = document.getElementById("cv-last-audit-date")  as HTMLElement | null;
    if (nameInput && titleSpan) titleSpan.textContent = nameInput.value;
    if (kw1Input  && kwSubSpan) kwSubSpan.textContent = `"${kw1Input.value}"`;
    const savedDate = localStorage.getItem("cover_last_audit_date");
    if (auditDateEl) auditDateEl.textContent = savedDate ?? "—";

    // Wire save-on-input, save-on-change (catches paste/autofill), and title sync
    const cleanup: Array<() => void> = [];
    FIELDS.forEach(({ id, key }) => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      if (!el) return;
      const save = () => { localStorage.setItem(key, el.value); scheduleFlush(); };
      el.addEventListener("input",  save);
      el.addEventListener("change", save);
      cleanup.push(() => { el.removeEventListener("input", save); el.removeEventListener("change", save); });
    });
    if (nameInput && titleSpan) {
      const sync = () => { titleSpan.textContent = nameInput.value; };
      nameInput.addEventListener("input", sync);
      cleanup.push(() => nameInput.removeEventListener("input", sync));
    }
    if (kw1Input && kwSubSpan) {
      const syncKw = () => { kwSubSpan.textContent = `"${kw1Input.value}"`; };
      kw1Input.addEventListener("input",  syncKw);
      kw1Input.addEventListener("change", syncKw);
      cleanup.push(() => { kw1Input.removeEventListener("input", syncKw); kw1Input.removeEventListener("change", syncKw); });
    }

    // Clear position + volume spans for any blank keyword fields (on load and on erase)
    function syncBlankKw(i: number) {
      const kwEl  = document.getElementById(`cover-kw-${i}`) as HTMLInputElement | null;
      const posEl = document.getElementById(`kw-pos-${i}`);
      const volEl = document.getElementById(`kw-vol-${i}`);
      if (!kwEl) return;
      const blank = !kwEl.value.trim();
      if (posEl && blank) posEl.textContent = "";
      if (volEl && blank) volEl.textContent = "";
    }

    for (let i = 1; i <= 5; i++) syncBlankKw(i);

    for (let i = 1; i <= 5; i++) {
      const kwEl = document.getElementById(`cover-kw-${i}`) as HTMLInputElement | null;
      if (!kwEl) continue;
      const idx = i;
      const onKwChange = () => syncBlankKw(idx);
      kwEl.addEventListener("input",  onKwChange);
      kwEl.addEventListener("change", onKwChange);
      cleanup.push(() => { kwEl.removeEventListener("input", onKwChange); kwEl.removeEventListener("change", onKwChange); });
    }

    // Restore competitor strip from seo_full_metrics (authoritative source written by Get Competitors).
    // This is preferred over seo_cover_strip (DOM-scraped text) because it always reflects the
    // last successful crawl, regardless of whether the DOM ref was valid at save time.
    const metricsRaw = localStorage.getItem("seo_full_metrics");
    if (metricsRaw) {
      try {
        const { sites } = JSON.parse(metricsRaw) as {
          sites?: Array<{ domain?: string; dr?: number | null; traffic?: number | null }>;
        };
        const cover = coverRef.current;
        if (cover && sites?.length) {
          const fmtNum = (n: number | null | undefined) =>
            n !== null && n !== undefined
              ? (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(Math.round(n)))
              : "—";

          // Target site (index 0)
          const target = sites[0];
          if (target) {
            const yn = cover.querySelector<HTMLElement>(".cs-you .cs-name");
            const yd = cover.querySelector<HTMLElement>(".cs-you .cs-dr");
            const yt = cover.querySelector<HTMLElement>(".cs-you .cs-traffic");
            if (yn && target.domain) yn.textContent = target.domain;
            if (yd) yd.textContent = target.dr != null ? String(Math.round(target.dr)) : "—";
            if (yt) yt.textContent = fmtNum(target.traffic);
          }

          // Competitor sites (indices 1–5)
          const compItems = cover.querySelectorAll<HTMLElement>(".cs-item:not(.cs-you)");
          sites.slice(1).forEach((site, i) => {
            const item = compItems[i]; if (!item) return;
            const n = item.querySelector<HTMLElement>(".cs-name");
            const d = item.querySelector<HTMLElement>(".cs-dr");
            const t = item.querySelector<HTMLElement>(".cs-traffic");
            if (n && site.domain) { n.textContent = site.domain; n.style.fontSize = "14px"; }
            if (d) d.textContent = site.dr != null ? String(Math.round(site.dr)) : "—";
            if (t) t.textContent = fmtNum(site.traffic);
          });
        }
      } catch { /* ignore */ }
    }

    return () => cleanup.forEach(fn => fn());
  }, [HTML_COVER]);

  // Audit state is hydrated into localStorage by AppRoot before this component mounts
  // (and re-hydrated on a remount when switching sites), so no per-mount fetch here.

  // Wire the "Get Competitors" button on the cover page.
  // Runs SERP + Ahrefs metrics (same as Run Full Audit step 1) but skips all section regeneration.
  // Updates the cover competitor strip and stores competitor domains for the Run Crawl button.
  useEffect(() => {
    const btn    = document.getElementById("get-competitors-btn") as HTMLButtonElement | null;
    const status = document.getElementById("get-competitors-status") as HTMLElement | null;
    if (!btn) return;

    const setStatus = (msg: string, color?: string) => {
      if (!status) return;
      // Progress messages (no explicit color) render grey with an animated spinner while crawling.
      // Terminal messages pass a color: error red (darkened in light mode) / success (blue in light).
      const isLight = document.documentElement.getAttribute("data-theme") === "light";
      const spin = color === undefined;
      status.style.display = "block";
      status.style.color = spin
        ? (isLight ? "#64748b" : "rgba(255,255,255,.65)")
        : (isLight ? (color === "#f87171" ? "#dc2626" : color === "#fbbf24" ? "#b45309" : "#2563eb") : color);
      if (spin) {
        status.innerHTML = "";
        const s = document.createElement("span");
        s.className = "lsg-spinner";
        s.style.marginRight = "7px";
        s.style.verticalAlign = "-2px";
        status.appendChild(s);
        status.appendChild(document.createTextNode(msg));
      } else {
        status.textContent = msg;
      }
    };

    const handler = async () => {
      const siteUrl  = (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim() ?? "";
      const location = (document.getElementById("cover-location")    as HTMLInputElement | null)?.value?.trim() ?? "";

      // Collect all 6 keyword fields
      const keywords: string[] = [];
      for (let i = 1; i <= 5; i++) {
        const v = (document.getElementById(`cover-kw-${i}`) as HTMLInputElement | null)?.value?.trim() ?? "";
        keywords.push(v);
      }
      const keyword = keywords[0];

      const businessName = (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim() ?? "";
      const gbpCategory  = (document.getElementById("cover-gbp-category")  as HTMLInputElement | null)?.value?.trim() ?? "";

      // Custom Competitors mode: competitors come from the 5 domain inputs, not the keyword SERP.
      const mode   = getCompetitorMode();
      const custom = mode === "custom" ? readCustomCompetitors() : [];

      // ── Required fields check ─────────────────────────────────────
      // Same required set as the Full Audit (kept consistent), so the Primary GBP Category is captured
      // here too — later per-tab crawls (Schema / Entities / Service Area) need it for business-type
      // and schema decisions. A target keyword is required in BOTH modes — several tabs' crawls depend
      // on it, even when competitors are supplied manually.
      if (!businessName || !siteUrl || !location || !gbpCategory || !keyword) {
        void showAlert("Please complete the following fields before continuing:\n\n  • Full Business Name\n  • Website URL\n  • City\n  • Primary GBP Category\n  • Target Keyword (first keyword field)", { title: "Missing Required Fields" });
        return;
      }
      if (mode === "custom" && !custom.length) {
        void showAlert("Enter at least one competitor domain (e.g., competitor.com) in the Competitors table, then click Save Competitors.", { title: "No Competitors Entered" });
        return;
      }

      // ── City format: must be "City, State" ────────────────────────
      if (!/^[^,]+,\s*[A-Za-z][A-Za-z. ]*$/.test(location)) {
        void showAlert("Please add a city in the City field in the format: City, State (Example: Denver, CO).", { title: "Invalid City Format" });
        return;
      }

      // ── Website URL format check ──────────────────────────────────
      const urlToTest = siteUrl.startsWith("http") ? siteUrl : `https://${siteUrl}`;
      let validUrl = false;
      try { const p = new URL(urlToTest); validUrl = p.hostname.includes(".") && !p.hostname.includes(" "); } catch { /* invalid */ }
      if (!validUrl) {
        void showAlert("Please make sure the Website URL is in the correct format (e.g., www.example.com or https://example.com) before proceeding.", { title: "Invalid Website URL" });
        return;
      }

      // Use the same key-loading functions as the rest of the app
      const ahrefsKey                              = loadAhrefsKey();
      const { login: dfsLogin, password: dfsPass } = loadDataForSeoCredentials();

      if (!dfsLogin || !dfsPass) {
        setStatus("DataForSEO credentials required — open ⚙ Settings.", "#f87171");
        return;
      }

      btn.disabled    = true;
      btn.textContent = mode === "custom" ? "⏳ Saving…" : "⏳ Fetching…";
      setStatus(mode === "custom"
        ? "Saving competitors — fetching DR & visits/mo…"
        : "Running Google SERP — finding competitors & keyword positions…");

      try {
        const res = await fetch("/api/run-audit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            keyword,
            keywords,
            siteUrl:            siteUrl.startsWith("http") ? siteUrl : `https://${siteUrl}`,
            location,
            ahrefsKey,
            dataForSeoLogin:    dfsLogin,
            dataForSeoPassword: dfsPass,
            customCompetitors:  custom, // [] in keyword mode → server treats as keyword discovery
          }),
        });

        const result = await res.json() as {
          ok: boolean; error?: string;
          target?:      { domain: string; dr: number | null; traffic: number | null };
          competitors?: Array<{ domain: string; dr: number | null; traffic: number | null }>;
          positions?:   (number | null)[];
          volumes?:     (number | null)[];
          mapPackEntries?: unknown[];
          mapPackPosition?: number | null;
          locationUsed?: string;
          locationInvalid?: boolean;
        };

        if (!result.ok) throw new Error(result.error ?? "Request failed");

        // ── Unrecognized city: fail cleanly, tell the user to fix the City ──
        // We only ever search the exact city entered (never the state/country). If the search
        // engine doesn't recognize it (e.g. a county like "Monmouth, NJ"), stop here without
        // storing anything — the user needs to enter a valid city and try again.
        if (mode !== "custom" && result.locationInvalid) {
          const msg = `Competitor pull unsuccessful — "${location}" isn't a recognized city. Please enter a valid city in the City field (format: City, State — e.g. "Princeton, NJ"), then click Get Competitors again.`;
          setStatus(`✗ ${msg}`, "#f87171");
          void showAlert(msg, { title: "Unrecognized City" });
          return; // finally block re-enables the button
        }

        // ── Persist competitor domains for Run Crawl ──────────────────
        if (result.target)      localStorage.setItem("seo_target_domain",     result.target.domain);
        if (result.competitors) localStorage.setItem("seo_competitor_domains", JSON.stringify(result.competitors.map(c => c.domain)));

        const cover = coverRef.current;

        // ── Update the cover competitor strip ─────────────────────────
        if (cover && result.target && result.competitors) {
          const fmt = (n: number | null) =>
            n === null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);

          const youDr      = cover.querySelector(".cs-you .cs-dr");
          const youTraffic = cover.querySelector(".cs-you .cs-traffic");
          const youName    = cover.querySelector<HTMLElement>(".cs-you .cs-name");
          if (youDr)      youDr.textContent      = result.target.dr !== null ? String(Math.round(result.target.dr)) : "—";
          if (youTraffic) youTraffic.textContent  = fmt(result.target.traffic);
          if (youName)    youName.textContent      = result.target.domain;

          const compItems = cover.querySelectorAll<HTMLElement>(".cs-item:not(.cs-you)");
          result.competitors.forEach((comp, i) => {
            const item = compItems[i];
            if (!item) return;
            const nameEl    = item.querySelector<HTMLElement>(".cs-name");
            const drEl      = item.querySelector(".cs-dr");
            const trafficEl = item.querySelector(".cs-traffic");
            if (nameEl)    { nameEl.textContent = comp.domain; nameEl.style.fontSize = "14px"; }
            if (drEl)      drEl.textContent      = comp.dr !== null ? String(Math.round(comp.dr)) : "—";
            if (trafficEl) trafficEl.textContent  = fmt(comp.traffic);
          });
        }

        // ── Update keyword position + volume labels ───────────────────
        if (result.positions && result.volumes) {
          const fmtVol = (n: number | null) =>
            n === null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k/mo` : `${n}/mo`;

          result.positions.forEach((pos, i) => {
            const posEl = document.getElementById(`kw-pos-${i + 1}`);
            const volEl = document.getElementById(`kw-vol-${i + 1}`);
            if (posEl) posEl.textContent = pos !== null ? `#${pos}` : "Not ranking";
            if (volEl) volEl.textContent = fmtVol(result.volumes![i]);
          });
        }

        // ── Store positions + volumes for section regeneration ────────
        if (result.positions) localStorage.setItem("seo_keyword_positions", JSON.stringify(result.positions));
        if (result.volumes)   localStorage.setItem("seo_keyword_volumes",   JSON.stringify(result.volumes));

        // ── Store map pack data — needed by GBP and other map-pack-aware sections ──
        if (result.mapPackEntries) localStorage.setItem("seo_map_pack", JSON.stringify({
          entries:            result.mapPackEntries,
          targetPosition:     result.mapPackPosition ?? null,
          targetBusinessName: (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim() ?? "",
        }));

        // ── Fetch full Ahrefs metrics (needed by Run Crawl on every tab) ──
        if (result.target && result.competitors) {
          const allDomains = [result.target.domain, ...result.competitors.map(c => c.domain)];
          // Distinct stage so the user sees their own site is being crawled here, not just
          // competitor discovery — this metrics pull is what feeds the individual tabs.
          setStatus("Crawling your website data for use in tabs…");
          try {
            const mRes = await fetch("/api/full-metrics", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ domains: allDomains, ahrefsKey, dfsLogin, dfsPassword: dfsPass }),
            });
            const mData = await mRes.json() as { ok?: boolean; error?: string; [k: string]: unknown };
            if (mData.ok !== false) {
              // Merge map pack into metrics so section regeneration has it
              const mapPack = (() => { try { return JSON.parse(localStorage.getItem("seo_map_pack") ?? "null"); } catch { return null; } })();
              if (mapPack) mData.mapPack = mapPack;
              localStorage.setItem("seo_full_metrics", JSON.stringify(mData));
            }
          } catch { /* non-fatal — Run Crawl will warn if missing */ }
        }

        const count  = result.competitors?.length ?? 0;
        const ranked = result.positions?.filter(p => p !== null).length ?? 0;
        const kwCount = keywords.filter(k => k).length;
        saveCompetitorStrip();
        commitActiveAndFlush(siteUrl); // adopt this domain's bucket + persist
        if (mode !== "custom" && count === 0) {
          // Valid city, but the SERP returned no usable competitors (top results were all
          // directories/aggregators or the target itself). Don't claim "Ready" — competitor-based
          // tabs can't run. Say what happened instead of a misleading success message.
          setStatus(`⚠ No competitor sites found for "${keyword}" in ${location} — the top results were all directories/aggregators or your own site. Try a more specific target keyword, or switch to Custom Competitors mode and enter competitors manually.`, "#fbbf24");
        } else {
          setCompetitorsReady(true); // crawl done → enable Run Full Audit
          setStatus("Competitor and website data crawl completed. Ready for running manual crawls in individual tabs.", "#6ee7b7");
        }
      } catch (err) {
        setStatus(`✗ ${err instanceof Error ? err.message : "Request failed"}`, "#f87171");
      } finally {
        btn.disabled    = false;
        btn.textContent = competitorBtnLabel();
      }
    };

    btn.addEventListener("click", handler);
    return () => btn.removeEventListener("click", handler);
  }, [HTML_COVER]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wire the "Custom Competitors" toggle on the cover. Off = keyword-SERP competitor discovery
  // (default); On = reveal the Custom Competitors table and relabel the button to "Save
  // Competitors". Mode persists per-site under seo_competitor_mode.
  useEffect(() => {
    const toggle = document.getElementById("custom-comp-toggle") as HTMLButtonElement | null;
    if (!toggle) return;
    const block = document.getElementById("custom-comp-block") as HTMLElement | null;
    const btn   = document.getElementById("get-competitors-btn") as HTMLButtonElement | null;
    const kwBlock = document.getElementById("kw-table-block") as HTMLElement | null;

    const apply = (m: "keyword" | "custom") => {
      toggle.setAttribute("aria-checked", m === "custom" ? "true" : "false");
      if (block) block.style.display = m === "custom" ? "" : "none";
      // In custom mode both tables share the section: the keyword table is ~60% longer than
      // the Competitors table (flex 1.6 vs 1) and together they fill the full width. When off,
      // the keyword table takes the whole width.
      if (kwBlock) kwBlock.style.flex = m === "custom" ? "1.6" : "1";
      if (btn && !btn.disabled) btn.textContent = m === "custom" ? "Save Competitors" : "Get Competitors";
    };
    apply(getCompetitorMode());

    const onClick = () => {
      const next = getCompetitorMode() === "custom" ? "keyword" : "custom";
      try { localStorage.setItem("seo_competitor_mode", next); } catch { /* ignore */ }
      scheduleFlush(); // persist the mode into this site's bucket
      apply(next);
    };
    toggle.addEventListener("click", onClick);
    return () => toggle.removeEventListener("click", onClick);
  }, [HTML_COVER]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rebuild the ENTIRE brand mention table from state whenever the competitors
  // tab is active, results exist, OR the section HTML changes. Rebuilding (vs.
  // updating cells by ID) is required because regeneration resets the injected
  // table to placeholder rows whose IDs won't match the real competitor domains.
  useEffect(() => {
    if (activeSection !== "competitors" || !brandMentionResults.length) return;
    const tbody = document.getElementById("bm-tbody");
    if (!tbody) return;
    tbody.innerHTML = brandMentionResults.map((r, i) => {
      const isTarget = i === 0;
      const name = r.brandName || r.domain;
      return `<tr${isTarget ? ' class="yr"' : ""}>
        <td>${isTarget ? `<strong>${r.domain}</strong>` : r.domain}</td>
        <td><a href="https://${r.domain}" target="_blank" class="visit-link">↗</a></td>
        <td>${r.urlMentions.toLocaleString()}</td>
        <td>${r.brandMentions.toLocaleString()} *<br><span style="font-size:10px;color:var(--ink4);font-style:italic;">${name}</span></td>
      </tr>`;
    }).join("");
  }, [activeSection, brandMentionResults, regeneratedSections]);


  function updateState(next: StateMap) {
    setState(next);
    saveState(next);   // instant local write (seo_v5_state)
    scheduleFlush();   // debounced per-site server sync (fix state rides in the blob)
  }

  // ── Approve with confirm ──────────────────────────────────
  const handleApprove = useCallback(async (fixId: string, message?: string, isCreate?: boolean) => {
    // Already marked done — clicking the button does nothing
    if (state[fixId] === "done") return;

    // Require an active WordPress connection before any live change / page creation.
    // Read clientRef.current (a ref — always live) rather than the `connected`
    // state, because this handler is wired into the DOM via a [html]-deps effect
    // and its closure can be stale if the user connected after the section rendered.
    if (!clientRef.current) {
      await showAlert(
        "Please connect to your WordPress site first before trying to make a website change.\n\n" +
        "If you have not made a user in WordPress and created an application password in that user's settings, " +
        "please do so first and enter the credentials in the WP Connection section before continuing.",
        { title: "WordPress Not Connected", tone: "danger" },
      );
      return;
    }

    // The one-time WordPress helper (functions.php script or plugin) is required for
    // programmatic schema (sc-*) AND for inserting internal links (il-ko-*) — the
    // latter relies on it for Elementor edits and reliable per-page builder detection.
    // After confirming the WP connection above, validate the helper script EXISTS on
    // the site before attempting the change: probe once (cached), and if it's missing,
    // show the setup dialog and hold this approval until the user installs + verifies it.
    if ((fixId.startsWith("sc-") || fixId.startsWith("il-ko-")) && !schemaReadyRef.current) {
      const installed = await clientRef.current.isSchemaHelperInstalled().catch(() => false);
      if (installed) {
        schemaReadyRef.current = true;
        if (schemaHelperKeyRef.current) { try { localStorage.setItem(schemaHelperKeyRef.current, "1"); } catch { /* ignore */ } }
      } else {
        setSchemaSetup({ fixId, message, isCreate });
        return;
      }
    }

    // Homepage schema: its sameAs links are auto-aggregated from a live Google
    // search — show this reminder FIRST; only after the user clicks Continue do we
    // show the standard change-confirmation dialog below.
    if (fixId === "sc-home" || fixId === "sc-homepage") {
      const proceed = await showConfirm(
        "You're about to apply the homepage schema. Afterward, open the homepage in your WordPress page editor and check the \"sameAs\" links in the Schema box.\n\n" +
        "Those social/citation profile links are aggregated automatically from websites that show in a live Google search for \"yourdomain.com\" — meaning these websites display your website and/or link to it in this format, which branded profiles and citations likely would. They may include profiles you don't want in your schema, or miss profiles that aren't currently indexed.\n\n" +
        "You can add or remove any of them directly in that Schema box.",
        { title: "Review the homepage sameAs links", confirmLabel: "Continue", tone: "info" },
      );
      if (!proceed) return;
    }

    let confirmed: boolean;
    if (isCreate) {
      // For new-page publishes, lead with the page title (green), no competitor reference.
      const pageTitle = (message ?? "").trim();
      const escHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const messageHtml =
        `Publish a new page: <span style="color:#16a34a;font-weight:700">${escHtml(pageTitle)}</span>` +
        `<br/><br/>This will publish a new live page on WordPress. Continue?`;
      confirmed = await showConfirm("", {
        title: "Confirm Publish to WordPress", messageHtml, confirmLabel: "Continue", tone: "create",
      });
    } else {
      confirmed = await showConfirm(
        `${message ? `${message}\n\n` : ""}This will make a live change on WordPress. Continue?`,
        { title: "Confirm Change or Update", confirmLabel: "Continue", tone: "default" },
      );
    }
    if (!confirmed) return;
    updateState(setApproved(state, fixId));

    // clientRef.current is guaranteed here (checked above) — execute the live fix.
    executeFixById(fixId);
  }, [state]);

  const handleIgnore = useCallback((fixId: string, checked: boolean) => {
    updateState(setIgnored(state, fixId, checked));
  }, [state]);

  const handleDone = useCallback((fixId: string, checked: boolean) => {
    updateState(setDone(state, fixId, checked));
  }, [state]);

  // Reset icon — clears a hung "queued" status so the fix can be approved again.
  const handleReset = useCallback((fixId: string) => {
    updateState(clearState(state, fixId));
  }, [state]);

  // A "Manual Update" schema card was clicked — just re-open its merge popup (executeFixById detects
  // the duplicate business entity and returns the merged block). No confirm, no queued state.
  const handleManualUpdate = useCallback((fixId: string) => { void executeFixById(fixId); }, []);

  // A schema card was PREDICTED manual up front (AuditSection checked the live page: the card adds a
  // business entity but the page already has one). Label its button "Manual Update" before any click.
  // Skip if the card is already queued/done so we never relabel a card the user already actioned.
  const handlePredictManual = useCallback((fixId: string) => {
    const st = loadState();
    if (st[fixId] === "done" || st[fixId] === "queued") return;
    setManualSchema(prev => (prev.has(fixId) ? prev : new Set(prev).add(fixId)));
  }, []);

  // Fetch a WordPress page's current tag names by its URL slug (for the Pages &
  // Meta Tags field). Returns null when not connected / page not found, [] when
  // the page exists but has no tags. Reads the live clientRef.
  const fetchPageTags = useCallback(async (pathSlug: string): Promise<string[] | null> => {
    const client = clientRef.current;
    if (!client || !pathSlug) return null;
    try {
      const page = await client.getPageBySlug(pathSlug);
      if (!page) return null;
      return page.tags.length ? await client.getTagNames(page.tags) : [];
    } catch { return null; }
  }, []);

  // Content Drawer History — scan WordPress for every page/post the tool has created
  // (via the _lsg_tool_created meta key, falling back to the "LSG Tool" tag).
  const listToolCreatedPages = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return [];
    try { return await client.listLsgPages(); } catch { return []; }
  }, []);

  // Hide toggle — marks a fix as not applicable; excluded from all exports.
  const handleToggleHide = useCallback((fixId: string) => {
    setHidden(prev => {
      const next = toggleHidden(prev, fixId);
      saveHidden(next);
      return next;
    });
  }, []);

  // Clear ALL status (queued/done/ignored + hidden) for a set of fix-ids in one batch. Bulk tools
  // reuse card fix-ids across recrawls (e.g. after clearing cards), so a freshly generated card can
  // otherwise inherit a prior batch's status — this wipes it so new cards start blank.
  const handleClearFixIds = useCallback((ids: string[]) => {
    if (!ids.length) return;
    const cur = loadState();
    let stateChanged = false;
    const nextState: StateMap = { ...cur };
    for (const id of ids) if (id in nextState) { delete nextState[id]; stateChanged = true; }
    if (stateChanged) updateState(nextState);
    setHidden(prev => {
      let touched = false;
      const next = { ...prev };
      for (const id of ids) if (next[id]) { delete next[id]; touched = true; }
      if (touched) saveHidden(next);
      return touched ? next : prev;
    });
  }, []);

  // ── Save as PDF ───────────────────────────────────────────
  // Builds the cleaned cover HTML for PDF exports — used by BOTH the per-tab export
  // and the full-audit export so the cover page looks identical: strips action/get-
  // competitors UI, removes tooltips + the "Last Full Audit Run" eyebrow and the GBP
  // hint, fills in live values, and renders the site-detail fields as one-line
  // content-width text so nothing is clipped.
  function buildPdfCoverHtml(): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<body>${HTML_COVER}</body>`, "text/html");
    doc.getElementById("get-competitors-btn")?.closest("div")?.remove();
    doc.getElementById("get-competitors-status")?.remove();
    doc.querySelectorAll(".ac-btn,.create-expand-btn,.run-crawl-btn").forEach(el => {
      const next = el.nextElementSibling;
      if (next?.classList.contains("ac-sep")) next.remove();
      el.remove();
    });
    doc.querySelectorAll(".audit-tip").forEach(el => el.remove());
    doc.querySelectorAll("span").forEach(el => {
      const t = el.textContent?.trim() ?? "";
      if (t.startsWith("(Enter main target keyword")) el.remove();
      else if (t.startsWith("(used as the exact search origin")) el.remove();
    });
    const eyebrow = doc.querySelector(".cv-eyebrow");
    const firstEyebrowSpan = eyebrow?.querySelector("span");
    if (eyebrow && firstEyebrowSpan) eyebrow.innerHTML = firstEyebrowSpan.outerHTML;
    (["cover-business-name", "cover-website-url", "cover-location",
      "cover-kw-1", "cover-kw-2", "cover-kw-3", "cover-kw-4", "cover-kw-5"] as const).forEach(id => {
      const live = document.getElementById(id) as HTMLInputElement | null;
      const target = doc.getElementById(id) as HTMLInputElement | null;
      // setAttribute (NOT .value) so the live value serializes into the exported HTML —
      // setting the .value property alone leaves the old default in the markup.
      if (live && target) target.setAttribute("value", live.value);
    });
    // Use the CURRENT business name (from the live field) in the cover title, not the
    // static default baked into HTML_COVER.
    const titleName = doc.getElementById("cv-title-name");
    const liveBiz = (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim();
    if (titleName && liveBiz) titleName.textContent = liveBiz;
    for (let i = 1; i <= 5; i++) {
      const pos = document.getElementById(`kw-pos-${i}`);
      const vol = document.getElementById(`kw-vol-${i}`);
      const tPos = doc.getElementById(`kw-pos-${i}`);
      const tVol = doc.getElementById(`kw-vol-${i}`);
      if (pos && tPos) tPos.textContent = pos.textContent;
      if (vol && tVol) tVol.textContent = vol.textContent;
    }
    // Copy the live competitor strip (name / DR / visits-per-month) into the export. These
    // elements have classes but NO ids, so the old id-based copy never matched and the export
    // showed the "—" placeholders. Match position-by-position instead (same HTML_COVER order).
    const liveItems = coverRef.current?.querySelectorAll<HTMLElement>(".cs-item");
    if (liveItems) {
      doc.querySelectorAll<HTMLElement>(".cs-item").forEach((item, i) => {
        const src = liveItems[i];
        if (!src) return;
        (["cs-name", "cs-dr", "cs-traffic"] as const).forEach(cls => {
          const from = src.querySelector(`.${cls}`);
          const to = item.querySelector(`.${cls}`);
          if (from && to) to.textContent = from.textContent;
        });
      });
    }
    (["cover-business-name", "cover-website-url", "cover-location", "cover-full-address", "cover-gbp-category"] as const).forEach(id => {
      const target = doc.getElementById(id) as HTMLInputElement | null;
      if (!target) return;
      const live = document.getElementById(id) as HTMLInputElement | null;
      const fieldStyle = (target.getAttribute("style") ?? "").replace(/width\s*:\s*100%/g, "");
      const div = doc.createElement("div");
      div.textContent = (live?.value ?? target.getAttribute("value") ?? "").trim();
      // These fields become <div>s, so the live [data-theme="light"] input-color override no
      // longer reaches them — they'd keep the dark-theme light-blue (invisible on the white
      // PDF). Force dark text in light mode; leave the dark-theme color as-is in dark mode.
      const colorOverride = theme === "light" ? ";color:#0f172a" : "";
      div.setAttribute("style", fieldStyle + ";display:inline-block;white-space:nowrap;width:auto;height:auto;" + colorOverride);
      target.replaceWith(div);
      if (div.parentElement) div.parentElement.style.flex = "0 0 auto";
    });
    const leftCol = doc.querySelector<HTMLElement>(".kw-hero > div");
    if (leftCol) leftCol.style.flex = "0 0 auto";
    return doc.body.innerHTML;
  }

  const handleSaveReport = useCallback((format: "pdf" | "html") => {
    const domain =
      (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim() ||
      localStorage.getItem("cover_website_url") ||
      "SEO Audit";

    // Inline all app CSS so the export is self-contained (styled even opened from disk).
    const styleNodes = collectExportStyles();

    // Process section HTML for print: remove approve buttons, rename Done → Completed
    const parser = new DOMParser();
    const processHtml = (html: string): string => {
      const doc = parser.parseFromString(`<body>${html}</body>`, "text/html");

      // Drop any fix cards the user hid — they must not appear in exports
      stripHiddenCards(doc.body, hidden);

      // Remove approve/create buttons
      doc.querySelectorAll(".ac-btn").forEach(btn => btn.remove());

      // Remove the first .ac-sep in each .ac (was the separator after the button)
      doc.querySelectorAll(".ac").forEach(ac => {
        const sep = ac.querySelector(".ac-sep");
        if (sep) sep.remove();
      });

      // Rename "Done ✓" / "Done" checkbox labels → "Completed"
      doc.querySelectorAll(".ac-chk span").forEach(span => {
        const t = span.textContent ?? "";
        if (/done/i.test(t) || t.trim() === "✓") span.textContent = "Completed";
      });

      // Remove create-form rows (inline page-creation panels) — not relevant in PDF
      doc.querySelectorAll(".create-form-row").forEach(el => el.remove());

      // Remove "Click ⚡ Create to build a competing page on your site" prompts
      doc.body.innerHTML = doc.body.innerHTML.replace(
        /Click\s+⚡\s+Create\s+to\s+build\s+a\s+competing\s+page\s+on\s+your\s+site/gi, ""
      );

      // Remove about:blank — replace <a href="about:blank"> with its text content
      doc.querySelectorAll<HTMLAnchorElement>('a[href="about:blank"]').forEach(a => {
        const span = doc.createElement("span");
        span.innerHTML = a.innerHTML;
        a.replaceWith(span);
      });
      // Remove any remaining literal "about:blank" text
      doc.body.innerHTML = doc.body.innerHTML.replace(/\babout:blank\b/gi, "");

      return doc.body.innerHTML;
    };

    // Only include tabs that have actually been generated (full audit or per-tab Run Crawl),
    // in sidebar order. Un-generated tabs are skipped — no static placeholder content. The
    // Content Drawer tab is never exported. Express Schema (Bulk Page Schema) cards are a
    // tool artifact and are intentionally NOT exported.
    const sectionsHtml = SECTIONS.map(s => {
      if (s.id === "contentdrawer" || s.id.startsWith("express") || s.id === "sitemap" || s.id === "llmtracking" || s.id === "organicrank" || s.id === "llmvisibility" || s.id === "gbpreviews" || s.id === "servicearea" || s.id === "conversational" || s.id === "redirectionmanager" || s.id === "history" || s.id === "nicheadsscraper" || s.id === "topicalcoverage") return ""; // tool-only tabs, never exported
      let html = regeneratedSections[s.id] || "";
      // The Cannibalization tab's tool sections are live-only — append them from stored results.
      if (s.id === "cannibal") html = (html.length >= 100 ? html : "") + buildCannibalizationExportHtml(siteKey(creds.siteUrl));
      if (!html || html.length < 100) return "";
      return `<div class="pdf-sec" id="pdf-${s.id}">${processHtml(html)}</div><div class="pdf-brk"></div>`;
    }).join("");

    const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    const fullHtml = `<!DOCTYPE html>
<html lang="en" data-theme="${theme}">
<head>
<meta charset="utf-8">
<title>SEO Audit — ${domain}</title>
${styleNodes}
<style>
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { background: #fff !important; margin: 0; padding: 0; }
  .pdf-cover { font-family: Mulish, sans-serif; padding: 20px 28px 16px; border-bottom: 2px solid #1e293b; margin-bottom: 4px; }
  .pdf-cover h1 { margin: 0; font-size: 20px; color: #0f172a; }
  .pdf-cover p  { margin: 4px 0 0; font-size: 12px; color: #64748b; }
  .pdf-sec { padding: 0 20px 8px; }

  /* ── Landscape + margins ─────────────────── */
  @page { size: landscape; margin: 10mm 8mm; }

  /* ── Cover page — same treatment as the per-tab export ── */
  .cover { padding: 20px 30px 22px !important; }
  .cover .kw-hero { gap: 20px !important; }
  .cover .kw-hero [style*="flex:0 0 526px"] { flex: 0 0 auto !important; }

  /* ── Table overflow fix ──────────────────── */
  .tw {
    overflow: visible !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  /* ── Compact tables ──────────────────────── */
  table {
    width: 100% !important;
    table-layout: auto !important;
    font-size: 8px !important;
    border-collapse: collapse !important;
    word-break: break-word !important;
  }
  th {
    font-size: 7.5px !important;
    padding: 3px 4px !important;
    white-space: normal !important;
    line-height: 1.3 !important;
  }
  td {
    font-size: 8px !important;
    padding: 3px 5px !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    line-height: 1.35 !important;
    max-width: 180px;
  }

  /* ── Compact badges + score bars ─────────── */
  .b  { font-size: 7px !important; padding: 1px 4px !important; }
  .sb { min-width: 28px !important; max-width: 55px !important; }
  .sn { font-size: 7px !important; }

  /* ── Fix card text ────────────────────────── */
  .oc-text { font-size: 9px !important; }
  .oc { padding: 6px 10px !important; }

  /* ── Ignore / Complete checkboxes — 15% smaller ── */
  .ac-chk { font-size: 85% !important; gap: 4px !important; }
  .ac-chk input[type="checkbox"] { transform: scale(0.85); transform-origin: left center; }

  @media print {
    .pdf-brk { page-break-after: always; break-after: page; height: 0; }
    .generate-content-btn, .cfp-generate-area, .cfp-content-ta, #cfp-content-wrap { display: none !important; }
    /* Prevent tables being clipped by page right edge */
    body { overflow: visible !important; }
  }
  @media screen {
    .pdf-brk { border-top: 2px dashed #cbd5e1; margin: 40px 0; }
    body { padding: 0 0 60px; }
  }
</style>
</head>
<body>
<div class="cover">${buildPdfCoverHtml()}</div>
<div class="pdf-brk"></div>
${sectionsHtml}
</body>
</html>`;

    // HTML export → download the document as-is.
    if (format === "html") {
      const blob = new Blob([fullHtml], { type: "text/html" });
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `${domain.replace(/[^a-z0-9]/gi, "-")}-seo-audit.html`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup — revoking synchronously after click can cancel the download.
      setTimeout(() => { a.remove(); URL.revokeObjectURL(dlUrl); }, 1500);
      return;
    }

    // PDF export → open a print window with a table-scaling + auto-print script.
    const win = window.open("", "_blank");
    if (!win) { void showAlert("Please allow pop-ups to save as PDF.", { title: "Pop-up Blocked" }); return; }
    const printScript = `<script>
  window.addEventListener("load", function() {
    var pageW = document.documentElement.clientWidth;
    var cover = document.querySelector(".cover");
    if (cover && cover.scrollWidth > pageW) { var cs = pageW / cover.scrollWidth; cover.style.transformOrigin = "left top"; cover.style.transform = "scale(" + cs + ")"; }
    document.querySelectorAll(".tw").forEach(function(tw) {
      var tbl = tw.querySelector("table");
      if (!tbl) return;
      var tblW = tbl.scrollWidth;
      if (tblW > pageW) { var scale = pageW / tblW; tbl.style.transformOrigin = "left top"; tbl.style.transform = "scale(" + scale + ")"; tw.style.height = Math.ceil(tbl.offsetHeight * scale) + "px"; }
    });
    setTimeout(function() { window.print(); }, 800);
  });
<\/script>`;
    win.document.write(fullHtml.replace("</body>", printScript + "</body>"));
    win.document.close();
  }, [regeneratedSections, hidden, theme, creds.siteUrl]);

  // ── WP execution ──────────────────────────────────────────
  async function executeFixById(fixId: string) {
    const client = clientRef.current;
    if (!client) return;
    // Read the live connection mirror — connInfo from the enclosing closure can be
    // stale (pre-connect) when this runs via an imperatively-wired handler.
    const conn = connInfoRef.current;
    // Brand suffix for {{BRAND}} tokens in recommended meta titles — prefer the
    // connected site's title, fall back to the cover Business Name.
    const brand = conn?.siteTitle || (localStorage.getItem("cover_business_name") ?? "");
    // SEO plugin detected on connect — drives which meta keys the update/create uses.
    const result = await runFix(fixId, client, brand, conn?.plugin ?? "unknown");

    // The schema helper went missing (e.g. removed by a theme switch) — the write
    // was dropped. Clear the cached "ready" flag and re-open the setup dialog so the
    // user can reinstall and retry, rather than just seeing an error toast.
    if (result.needsSchemaHelper) {
      schemaReadyRef.current = false;
      if (schemaHelperKeyRef.current) { try { localStorage.removeItem(schemaHelperKeyRef.current); } catch { /* ignore */ } }
      setSchemaSetup({ fixId });
      return;
    }

    // A business-entity enhancement was blocked to avoid a duplicate, but we computed the merged
    // block — show the copy-and-replace dialog instead of a dead toast. This is a MANUAL card: mark
    // it (its button becomes "Manual Update") and clear any queued state so it shows no spinner/Queued.
    if (result.mergedSchema) {
      setManualSchema(prev => prev.has(fixId) ? prev : new Set(prev).add(fixId));
      updateState(clearState(loadState(), fixId));
      setSchemaMergeCopied(false);
      setSchemaMerge({ json: result.mergedSchema, message: result.message });
      return;
    }

    // Auto-mark done if fully automated and successful
    if (result.ok && !result.manual) {
      updateState(setDone(loadState(), fixId, true));
      // Record the applied action to the shared History log (best-effort — never blocks the apply).
      // Creates carry createdUrl; schema/internal-links/meta carry targetUrl (set ONLY on a real write,
      // so no-ops don't log). The action is derived from the fixId prefix. Express Internal Links only
      // (il-bulk-), so the audit Internal Links tab (il-ko-/il-{slug}) is excluded here.
      const histUrl = result.createdUrl || result.targetUrl;
      if (histUrl) {
        const act: HistoryAction | null =
          result.createdUrl ? (fixId.startsWith("sa-") ? "new_service_area_page" : "new_page")
          : (fixId.startsWith("sc-") || fixId.startsWith("af-sch-") || fixId.startsWith("en-")) ? "schema_added"
          : fixId.startsWith("il-bulk-") ? "internal_links_added"
          : fixId.startsWith("um-") ? "meta_updated"
          : null;
        if (act) void recordHistory({ action: act, url: histUrl, siteKey: siteKey(creds.siteUrl), pageId: result.pageId });
      }
    } else if (fixId.startsWith("il-")) {
      // Internal-link approval didn't actually apply (it failed, or needs a manual action — a popup
      // explains). This tab has no reset button, so revert the card from Queued back to Approve on
      // its own, making it clearly actionable again instead of stuck on "Queued".
      updateState(clearState(loadState(), fixId));
    } else if (typeof document !== "undefined" && document.getElementById(`cft-content-${fixId}`)) {
      // A create-page publish that didn't complete (failed, or returned a manual notice) → clear the
      // Queued state so the button returns to "Publish to WordPress" (actionable) instead of sticking
      // on "Publishing..". Success already took the setDone branch above.
      updateState(clearState(loadState(), fixId));
    } else if (fixId.startsWith("sc-")) {
      // A Schema Approve that didn't apply (couldn't resolve the page, or returned a manual notice)
      // — the mergedSchema / needsSchemaHelper cases already returned early above. Schema cards have
      // no reset button, so revert Queued → Approve so the card is actionable again (retry after the
      // cause is fixed) instead of being stuck on "Queued". Success already took the setDone branch.
      updateState(clearState(loadState(), fixId));
    }
    // Stays until the user dismisses it with the OK button (no auto-dismiss).
    setToast({ ...result, fixId });
  }

  // ── Export current tab (cover + active section) ──────────────
  function handleExportTab(format: "pdf" | "html" | "csv") {
    setShowExportMenu(false);

    // CSV is only offered on the Sitemap tab — build a spreadsheet of the crawled rows and
    // download it directly (no cover / print wrapper).
    if (format === "csv") {
      if (activeSec.id !== "sitemap") return;
      const csv = buildSitemapCsv(sitemapRows);
      const domain = (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim() || "sitemap";
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `${domain.replace(/[^a-z0-9]/gi, "-")}-sitemap.csv`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(dlUrl); }, 1500);
      return;
    }

    const parser = new DOMParser();

    // Clean cover HTML (shared with the full-audit export so they look identical)
    const cleanCover = buildPdfCoverHtml();

    // Clean section HTML
    const rawSection = (auditEverRun ? regeneratedSections[activeSec.id] : null) || activeSec.html;
    const cleanSection = (() => {
      const doc = parser.parseFromString(`<body>${rawSection}</body>`, "text/html");
      // Drop any fix cards the user hid — they must not appear in exports
      stripHiddenCards(doc.body, hidden);
      // Remove Create/Approve buttons and their adjacent separator — keep Ignore & Done checkboxes
      doc.querySelectorAll(".ac-btn,.create-expand-btn,.create-submit-btn,.generate-content-btn,.run-crawl-btn").forEach(el => {
        // Also remove the immediately following .ac-sep so no orphan separator remains
        const next = el.nextElementSibling;
        if (next?.classList.contains("ac-sep")) next.remove();
        el.remove();
      });
      // Remove create/expand forms
      doc.querySelectorAll(".create-form-row,.create-cancel-btn").forEach(el => el.remove());
      doc.querySelectorAll<HTMLAnchorElement>('a[href="about:blank"]').forEach(a => { const s = doc.createElement("span"); s.innerHTML = a.innerHTML; a.replaceWith(s); });
      return doc.body.innerHTML;
    })();

    // Express Schema (Bulk Page Schema) cards are a tool artifact and are intentionally NOT
    // exported — the Schema tab exports only its crawled audit content.
    let sectionBody = cleanSection;
    // The Cannibalization tab's tool sections are live-only — append them from stored results.
    if (activeSec.id === "cannibal") sectionBody += buildCannibalizationExportHtml(siteKey(creds.siteUrl));
    // The Sitemap tab has no section HTML — build the table from the stored rows.
    if (activeSec.id === "sitemap") {
      sectionBody = buildSitemapExportHtml(sitemapRows);
    }

    const styleNodes = collectExportStyles();
    const domain = (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim() || "SEO Audit";
    const title  = `${domain} — ${activeSec.label}`;

    const bodyHtml = `
      <div class="cover">${cleanCover}</div>
      <div class="pdf-brk"></div>
      <div class="pdf-sec" id="pdf-${activeSec.id}">${sectionBody}</div>`;

    // Same print styling as the full-audit PDF export (handleSavePDF) so tab-level
    // PDFs are landscape + full width and tables don't get cut off.
    const printCss = `<style>
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { margin: 0; background: #fff !important; }
  @page { size: landscape; margin: 10mm 8mm; }
  /* Tighter cover padding so it uses the full landscape width */
  .cover { padding: 20px 30px 22px !important; }
  /* Left field column sizes to its content (fields shown as one-line text) + tighter
     gap, so the Target Keywords table shifts left and isn't cut off the right edge */
  .cover .kw-hero { gap: 20px !important; }
  .cover .kw-hero [style*="flex:0 0 526px"] { flex: 0 0 auto !important; }
  /* Full-width section wrapper (no .sec side background/padding) — matches the full-audit export */
  .pdf-sec { padding: 0 20px 8px; }
  .tw { overflow: visible !important; width: 100% !important; max-width: 100% !important; }
  table { width: 100% !important; table-layout: auto !important; font-size: 8px !important; border-collapse: collapse !important; word-break: break-word !important; }
  th { font-size: 7.5px !important; padding: 3px 4px !important; white-space: normal !important; line-height: 1.3 !important; }
  td { font-size: 8px !important; padding: 3px 5px !important; white-space: normal !important; word-break: break-word !important; overflow-wrap: anywhere !important; line-height: 1.35 !important; max-width: 180px; }
  .b  { font-size: 7px !important; padding: 1px 4px !important; }
  .sb { min-width: 28px !important; max-width: 55px !important; }
  .sn { font-size: 7px !important; }
  .oc-text { font-size: 9px !important; }
  .oc { padding: 6px 10px !important; }
  .ac-chk { font-size: 85% !important; gap: 4px !important; }
  .ac-chk input[type="checkbox"] { transform: scale(0.85); transform-origin: left center; }
  @media print { .pdf-brk { page-break-after: always; break-after: page; height: 0; } body { overflow: visible !important; } }
  @media screen { .pdf-brk { border-top: 2px dashed #cbd5e1; margin: 40px 0; } }
</style>`;

    const fullHtml = `<!DOCTYPE html><html lang="en" data-theme="${theme}"><head><meta charset="UTF-8"/>
      <title>${title}</title>${styleNodes}
      ${printCss}
      </head><body>${bodyHtml}</body></html>`;

    if (format === "html") {
      const blob = new Blob([fullHtml], { type: "text/html" });
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `${domain.replace(/[^a-z0-9]/gi, "-")}-${activeSec.id}.html`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(dlUrl); }, 1500);
    } else {
      // Scale down any table still wider than the usable page area, then print —
      // mirrors the full-audit PDF export.
      const printScript = `<script>window.addEventListener("load",function(){var pageW=document.documentElement.clientWidth;var cover=document.querySelector(".cover");if(cover&&cover.scrollWidth>pageW){var cs=pageW/cover.scrollWidth;cover.style.transformOrigin="left top";cover.style.transform="scale("+cs+")";}document.querySelectorAll(".tw").forEach(function(tw){var tbl=tw.querySelector("table");if(!tbl)return;var tblW=tbl.scrollWidth;if(tblW>pageW){var scale=pageW/tblW;tbl.style.transformOrigin="left top";tbl.style.transform="scale("+scale+")";tw.style.height=Math.ceil(tbl.offsetHeight*scale)+"px";}});setTimeout(function(){window.print();},800);});<\/script>`;
      const win = window.open("", "_blank");
      if (!win) { void showAlert("Please allow pop-ups to save as PDF.", { title: "Pop-up Blocked" }); return; }
      win.document.write(fullHtml.replace("</body>", printScript + "</body>"));
      win.document.close();
    }
  }

  function handleStopAudit() {
    auditAbortRef.current?.abort();
    auditAbortRef.current = null;
    // Discard the server job too (Stop ≠ pause): cancel it so it doesn't keep running.
    const id = auditJobIdRef.current || auditResumeIdRef.current
      || (() => { try { return localStorage.getItem(ACTIVE_AUDIT_JOB_KEY); } catch { return null; } })();
    if (id) void cancelCrawlJob(id).catch(() => {});
    auditJobCloseRef.current?.();
    auditJobCloseRef.current = null;
    auditJobIdRef.current = null;
    auditResumeIdRef.current = null;
    try { localStorage.removeItem(ACTIVE_AUDIT_JOB_KEY); } catch { /* ignore */ }
    setAuditRunning(false);
    setAuditPaused(false);
    setAuditToast({ type: "error", message: "✕ Audit stopped." });
  }

  // ── Sitemap crawl ──────────────────────────────────────────────
  // Pull the full page/post list from WordPress (gives the Page ID + tags), then crawl each
  // URL in batches for status code, headings, links, word count, meta. Stored per-site.
  function handleStopSitemap() { sitemapAbortRef.current = true; }
  async function handleSitemapCrawl() {
    if (sitemapCrawling) return;
    const client = clientRef.current;
    if (!client) { void showAlert("Connect your WordPress site first — the Sitemap reads your pages, posts and their IDs from WordPress.", { title: "WordPress not connected", tone: "danger" }); return; }
    sitemapAbortRef.current = false;
    setSitemapCrawling(true);
    setSitemapProgress({ done: 0, total: 0 });
    setAuditToast({ type: "info", message: "⏳ Listing pages & posts…" });
    try {
      const pages = await client.getAllPagesAndPosts();
      if (!pages.length) {
        void showAlert(
          "No pages or posts were returned from WordPress.\n\nMake sure the WordPress plugin is installed and activated on your site and that the connection is active, then click Run Crawl again.",
          { title: "No Pages Found — Check the WordPress Plugin" }, // default tone → green OK button
        );
        setAuditToast({ type: "error", message: "✗ No pages or posts found — make sure the WordPress plugin is activated and connected, then try again." });
        return;
      }
      setSitemapProgress({ done: 0, total: pages.length });
      // Editor type per page (Elementor/Block/Classic/Classic-Markdown) — one cheap bulk call up
      // front (empty map on older plugins → the column shows "—").
      const editorTypes = await client.getEditorTypes(pages.map(p => p.id)).catch(() => ({} as Record<number, import("./lib/wpClient").EditorType>));
      const BATCH = 40;
      const rows: SitemapRow[] = [];
      for (let i = 0; i < pages.length && !sitemapAbortRef.current; i += BATCH) {
        const batch = pages.slice(i, i + BATCH);
        const res = await fetch("/api/sitemap-crawl", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ urls: batch.map(p => p.url) }),
        });
        const data = await res.json() as { ok: boolean; stats?: Array<{ url: string; statusCode: number | null; title: string; description: string; internalLinks: number; wordCount: number; h1: number; h2: number; h3: number; h4: number; h5: number; h6: number; hasSchema: boolean }>; error?: string };
        if (!data.ok) throw new Error(data.error ?? "Crawl request failed");
        const byUrl = new Map((data.stats ?? []).map(s => [s.url, s]));
        for (const p of batch) {
          const s = byUrl.get(p.url);
          rows.push({
            id: p.id, title: p.title, url: p.url, type: p.type, tags: p.tags, parent: p.parent, slug: p.slug,
            metaTitle: s?.title ?? "", metaDescription: s?.description ?? "",
            internalLinks: s?.internalLinks ?? 0,
            h1: s?.h1 ?? 0, h2: s?.h2 ?? 0, h3: s?.h3 ?? 0, h4: s?.h4 ?? 0, h5: s?.h5 ?? 0, h6: s?.h6 ?? 0,
            wordCount: s?.wordCount ?? 0,
            statusCode: s?.statusCode ?? null,
            editorType: editorTypes[p.id],
            hasSchema: s?.hasSchema ?? false, schemaJson: schemaJsonFromBlocks(p.auditSchema),
          });
        }
        setSitemapRows([...rows]);
        setSitemapProgress({ done: Math.min(i + BATCH, pages.length), total: pages.length });
        setAuditToast({ type: "info", message: `⏳ Crawling pages… ${Math.min(i + BATCH, pages.length)} / ${pages.length}` });
        try { localStorage.setItem("seo_sitemap_rows", JSON.stringify(rows)); } catch { /* ignore */ }
      }
      const when = new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
      setSitemapCrawledAt(when);
      try { localStorage.setItem("seo_sitemap_crawled_at", when); localStorage.setItem("seo_sitemap_rows", JSON.stringify(rows)); } catch { /* ignore */ }
      scheduleFlush();
      setAuditToast(sitemapAbortRef.current
        ? { type: "error", message: `⏹ Sitemap crawl stopped — ${rows.length} pages saved.` }
        : { type: "success", message: `✓ Sitemap crawl complete — ${rows.length} pages.` });
    } catch (e) {
      setAuditToast({ type: "error", message: `✗ Sitemap crawl failed: ${e instanceof Error ? e.message : "Unknown error"}` });
    } finally {
      setSitemapCrawling(false);
      setSitemapProgress(null);
    }
  }

  // ── Sitemap: per-row / selected refresh + sync (update without a full re-crawl) ──
  type SitemapStat = { url: string; statusCode: number | null; title: string; description: string; internalLinks: number; wordCount: number; h1: number; h2: number; h3: number; h4: number; h5: number; h6: number; hasSchema: boolean };
  const persistSitemapRows = (rows: SitemapRow[]) => { try { localStorage.setItem("seo_sitemap_rows", JSON.stringify(rows)); } catch { /* ignore */ } scheduleFlush(); };
  // Load the site's redirects so the sitemap "Redirected" column can show where each URL points
  // (and flag redirected URLs in red). Silently degrades to no column on older plugins.
  const reloadSitemapRedirects = () => {
    const client = clientRef.current;
    if (!client) return;
    client.listRedirects().then(setSitemapRedirects).catch(() => { /* older plugin → no redirects */ });
  };
  useEffect(() => {
    if (connected) reloadSitemapRedirects();
    else setSitemapRedirects([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);
  // The LSG _audit_schema blocks (array of JSON strings) as one editable JSON string for the
  // Sitemap Schema cell — a single object when there's one block, else an array. "" when none.
  const schemaJsonFromBlocks = (blocks?: string[]): string => {
    const parsed: unknown[] = [];
    for (const b of blocks ?? []) { try { parsed.push(JSON.parse(b)); } catch { /* skip invalid */ } }
    if (!parsed.length) return "";
    return JSON.stringify(parsed.length === 1 ? parsed[0] : parsed, null, 2);
  };
  const rowFrom = (wp: { id: number; title: string; url: string; type: "page" | "post"; tags: string[]; parent: number; slug: string; auditSchema: string[] }, s?: SitemapStat): SitemapRow => ({
    id: wp.id, title: wp.title, url: wp.url, type: wp.type, tags: wp.tags, parent: wp.parent, slug: wp.slug,
    metaTitle: s?.title ?? "", metaDescription: s?.description ?? "", internalLinks: s?.internalLinks ?? 0,
    h1: s?.h1 ?? 0, h2: s?.h2 ?? 0, h3: s?.h3 ?? 0, h4: s?.h4 ?? 0, h5: s?.h5 ?? 0, h6: s?.h6 ?? 0,
    wordCount: s?.wordCount ?? 0, statusCode: s?.statusCode ?? null,
    hasSchema: s?.hasSchema ?? false, schemaJson: schemaJsonFromBlocks(wp.auditSchema),
  });
  async function crawlStatsByUrl(urls: string[]): Promise<Map<string, SitemapStat>> {
    const out = new Map<string, SitemapStat>();
    const BATCH = 40;
    for (let i = 0; i < urls.length; i += BATCH) {
      const res = await fetch("/api/sitemap-crawl", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ urls: urls.slice(i, i + BATCH) }) });
      const data = await res.json() as { ok: boolean; stats?: SitemapStat[]; error?: string };
      if (!data.ok) throw new Error(data.error ?? "Crawl request failed");
      (data.stats ?? []).forEach(s => out.set(s.url, s));
    }
    return out;
  }

  // Re-crawl just these rows (re-reads their WP title/tags/url too) and update them in place.
  async function refreshSitemapRows(rowKeys: string[]) {
    const client = clientRef.current;
    if (!client || !rowKeys.length || sitemapCrawling) return;
    const keySet = new Set(rowKeys);
    setSitemapRefreshing(prev => new Set([...prev, ...rowKeys]));
    try {
      const targets = sitemapRows.filter(r => keySet.has(`${r.type}-${r.id}`));
      const wpByKey = new Map<string, { id: number; title: string; url: string; tags: string[]; parent: number; slug: string; auditSchema: string[] } | null>();
      await Promise.all(targets.map(async r => { wpByKey.set(`${r.type}-${r.id}`, await client.getOnePageOrPost(r.type, r.id).catch(() => null)); }));
      const urlFor = (r: SitemapRow) => wpByKey.get(`${r.type}-${r.id}`)?.url ?? r.url;
      const stats = await crawlStatsByUrl(targets.map(urlFor));
      const editorTypes = await client.getEditorTypes(targets.map(r => r.id)).catch(() => ({} as Record<number, import("./lib/wpClient").EditorType>));
      setSitemapRows(prev => {
        const next = prev.map(r => {
          const k = `${r.type}-${r.id}`;
          if (!keySet.has(k)) return r;
          const wp = wpByKey.get(k);
          const url = wp?.url ?? r.url;
          const s = stats.get(url);
          return {
            ...r, title: wp?.title ?? r.title, url, tags: wp?.tags ?? r.tags,
            parent: wp?.parent ?? r.parent, slug: wp?.slug ?? r.slug,
            metaTitle: s?.title ?? r.metaTitle, metaDescription: s?.description ?? r.metaDescription,
            internalLinks: s?.internalLinks ?? r.internalLinks, wordCount: s?.wordCount ?? r.wordCount,
            h1: s?.h1 ?? r.h1, h2: s?.h2 ?? r.h2, h3: s?.h3 ?? r.h3, h4: s?.h4 ?? r.h4, h5: s?.h5 ?? r.h5, h6: s?.h6 ?? r.h6,
            statusCode: s?.statusCode ?? r.statusCode,
            editorType: editorTypes[r.id] ?? r.editorType,
            hasSchema: s?.hasSchema ?? r.hasSchema, schemaJson: wp ? schemaJsonFromBlocks(wp.auditSchema) : r.schemaJson,
          };
        });
        persistSitemapRows(next);
        return next;
      });
    } catch (e) {
      setAuditToast({ type: "error", message: `✗ Refresh failed: ${e instanceof Error ? e.message : "error"}` });
    } finally {
      setSitemapRefreshing(prev => { const n = new Set(prev); rowKeys.forEach(k => n.delete(k)); return n; });
    }
  }

  async function handleRefreshSelectedSitemap() {
    const keys = [...sitemapSelected];
    if (!keys.length) return;
    await refreshSitemapRows(keys);
    // Clear the selection once the refresh finishes → the button's count returns to zero.
    setSitemapSelected(new Set());
  }

  // Inline edit of a single Sitemap cell (hold-to-edit). Optimistically updates the table, then
  // writes the change to WordPress for that page/post; the write differs by which column changed.
  // Reverts the row + shows an error if the WordPress write fails.
  async function handleSitemapEdit(key: string, field: SitemapEditField, value: string) {
    const client = clientRef.current;
    if (!client) { setAuditToast({ type: "error", message: "Connect WordPress first." }); return; }
    const row = sitemapRows.find(r => `${r.type}-${r.id}` === key);
    if (!row) return;
    const prev = row; // for revert on failure
    const trimmed = value.trim();
    const tagList = trimmed ? trimmed.split(",").map(t => t.trim()).filter(Boolean) : [];

    // Optimistic table update.
    const applied: SitemapRow = field === "tags"
      ? { ...row, tags: tagList }
      : field === "title" ? { ...row, title: trimmed }
      : field === "metaTitle" ? { ...row, metaTitle: trimmed }
      : field === "metaDescription" ? { ...row, metaDescription: trimmed }
      : { ...row, schemaJson: trimmed }; // schema
    setSitemapRows(p => { const next = p.map(r => (`${r.type}-${r.id}` === key ? applied : r)); persistSitemapRows(next); return next; });

    try {
      if (field === "title") {
        const r = await client.updatePageOrPost(row.type, row.id, { title: trimmed });
        if (!r.success) throw new Error(r.error ?? "Update failed");
      } else if (field === "tags") {
        const ids = tagList.length ? await client.getOrCreateTags(tagList) : [];
        const r = await client.updatePageOrPost(row.type, row.id, { tags: ids });
        if (!r.success) throw new Error(r.error ?? "Update failed");
      } else if (field === "schema") {
        // Write the LSG _audit_schema (array of JSON strings). Empty clears it.
        const stype = row.type === "post" ? "posts" : "pages";
        let blocks: string[] = [];
        if (trimmed) {
          let obj: unknown;
          try { obj = JSON.parse(trimmed); } catch { throw new Error("Schema isn't valid JSON — enter a JSON-LD object or an array of objects."); }
          blocks = (Array.isArray(obj) ? obj : [obj]).map(o => JSON.stringify(o));
        }
        const r = await client.setAuditSchema(row.id, blocks, stype);
        if (!r.success) throw new Error(r.error ?? "Update failed");
        // WordPress silently drops unregistered meta, so confirm the LSG plugin accepted it.
        const after = await client.getAuditSchema(row.id, stype).catch(() => null);
        if (after && after.length !== blocks.length) throw new Error("Schema wasn't saved — activate/verify the Local SEO Garage plugin on this site.");
        void refreshSitemapRows([key]); // reconcile the column's schema state (hasSchema) after add/remove
      } else {
        // Meta title / description — route by the detected SEO plugin.
        const plugin = connInfoRef.current?.plugin ?? "unknown";
        if (plugin === "aioseo") {
          const r = await client.saveAioseoMeta(row.id, field === "metaTitle" ? { title: trimmed } : { description: trimmed });
          if (!r.success) throw new Error(r.error ?? "Update failed");
        } else {
          const keys = metaKeysFor(plugin);
          if (!keys) throw new Error(`SEO meta isn't writable for the "${plugin}" plugin`);
          const metaKey = field === "metaTitle" ? keys.title : keys.description;
          const r = await client.updatePageOrPost(row.type, row.id, { meta: { [metaKey]: trimmed } });
          if (!r.success) throw new Error(r.error ?? "Update failed");
        }
      }
      setAuditToast({ type: "success", message: "✓ Changes Saved Successfully." });
    } catch (e) {
      // Revert the optimistic change.
      setSitemapRows(p => { const next = p.map(r => (`${r.type}-${r.id}` === key ? prev : r)); persistSitemapRows(next); return next; });
      setAuditToast({ type: "error", message: `✗ Edit not saved: ${e instanceof Error ? e.message : "error"}` });
    }
  }

  // Stable callbacks (+ a latest-logic ref) for the MEMOIZED SitemapTab, so the large table doesn't
  // re-render on unrelated App state changes (toasts, audit polling, redirect loads, …). Without this
  // every App re-render re-rendered the whole table — a burst of them hung the tab. The ref keeps
  // refresh/edit pointing at the latest closures even though the wrappers are stable.
  const sitemapCbRef = useRef({ refresh: refreshSitemapRows, edit: handleSitemapEdit });
  sitemapCbRef.current.refresh = refreshSitemapRows;
  sitemapCbRef.current.edit = handleSitemapEdit;
  const sm_onRefreshRow = useCallback((k: string) => { void sitemapCbRef.current.refresh([k]); }, []);
  const sm_onEditCell = useCallback((k: string, f: SitemapEditField, v: string) => sitemapCbRef.current.edit(k, f, v), []);
  const sm_onToggleSelect = useCallback((k: string) => setSitemapSelected(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; }), []);
  const sm_onToggleAll = useCallback((keys: string[], checked: boolean) => setSitemapSelected(checked ? new Set(keys) : new Set()), []);
  const sm_onEditParent = useCallback((k: string) => setSitemapParentEdit(k), []);
  const sm_onEditPage = useCallback((k: string) => setSitemapEditPage(k), []);

  // Bulk-edit the selected Sitemap pages: add tags (merged with each page's existing tags so
  // nothing is duplicated) and/or set the post status. Confirms first, then writes each page to
  // WordPress. Tags shown in the table update optimistically; status isn't a table column.
  // Core apply: tag / status / reparent a set of rows, create old→new redirects for moved pages
  // and their descendants, update the table, and toast. Returns true if confirmed + applied.
  // Shared by the multi-row Bulk Edit and the single-row Parent Page Edit (hold a URL cell).
  async function applyBulkEdit(rowsSel: SitemapRow[], values: BulkEditValues, confirmTone: "danger" | "default" = "danger"): Promise<boolean> {
    const client = clientRef.current;
    if (!client) { setAuditToast({ type: "error", message: "Connect WordPress first." }); return false; }
    if (!rowsSel.length) return false;

    const wantParent = values.parent !== "";
    const newParentId = wantParent ? Number(values.parent) : null;
    const pathOf = (u: string) => { try { return new URL(u).pathname.replace(/\/$/, "") || "/"; } catch { return u; } };

    // Pages that will actually MOVE (only pages have parents; parent must differ; can't self-parent).
    const reparent = wantParent
      ? rowsSel.filter(r => r.type === "page" && r.parent !== newParentId && r.id !== newParentId)
      : [];

    // Slug rename — applies to the SINGLE page being edited (only the Parent Page Edit dialog passes a
    // slug). A new slug changes this page's URL (and its descendants'), exactly like a move, so it
    // feeds the same old→new 301 redirect logic below.
    const newSlug = (values.slug ?? "").trim();
    const slugRow = (newSlug && rowsSel.length === 1) ? rowsSel[0] : null;
    const wantSlug = !!slugRow && slugRow.slug !== newSlug;

    const STATUS_LABEL: Record<string, string> = { publish: "Published", private: "Private", pending: "Pending Review", draft: "Draft" };
    const parts: string[] = [];
    if (values.tags.length) parts.push(`${values.overwriteTags ? "replace tags with" : `add tag${values.tags.length === 1 ? "" : "s"}`} “${values.tags.join(", ")}”`);
    if (values.status) parts.push(`set status to ${STATUS_LABEL[values.status] ?? values.status}`);
    if (reparent.length) {
      const pLabel = newParentId === 0
        ? "top level (no parent)"
        : (() => { const pr = sitemapRows.find(r => r.id === newParentId && r.type === "page"); return pr ? pathOf(pr.url) : `page #${newParentId}`; })();
      parts.push(`move ${reparent.length} page${reparent.length === 1 ? "" : "s"} under ${pLabel} — old URLs (and any child pages') will 301-redirect to the new ones`);
    }
    if (wantSlug) parts.push(`change the URL slug of “${slugRow!.title || pathOf(slugRow!.url)}” to “${newSlug}” — the old URL (and any child pages') will 301-redirect to the new one`);
    // Manual redirect (Edit Page URL dialog "Redirect Page" field) — single-row edits only. Add a new
    // redirect, update the existing one when its destination changed, or remove it when cleared.
    const redirKey = (u: string) => { try { return (new URL(u).pathname.replace(/\/+$/, "") || "/").toLowerCase(); } catch { return (u.replace(/[#?].*$/, "").replace(/\/+$/, "") || "/").toLowerCase(); } };
    const redirRow = (values.manageRedirect && rowsSel.length === 1) ? rowsSel[0] : null;
    const redirDest = redirRow ? (values.redirect ?? "").trim() : "";
    const existingRedirect = redirRow ? (sitemapRedirects.find(rr => rr.enabled !== false && redirKey(rr.source) === redirKey(redirRow.url)) ?? null) : null;
    let redirectAction: "none" | "add" | "update" | "remove" = "none";
    if (redirRow) {
      if (redirDest) redirectAction = !existingRedirect ? "add" : (redirDest !== existingRedirect.destination ? "update" : "none");
      else if (existingRedirect) redirectAction = "remove";
    }
    if (redirectAction === "add") parts.push(`add a 301 redirect from “${pathOf(redirRow!.url)}” to “${redirDest}” (added to the Redirection Manager)`);
    else if (redirectAction === "update") parts.push(`update this page's redirect to “${redirDest}”`);
    else if (redirectAction === "remove") parts.push(`remove this page's redirect`);
    if (!parts.length) return false;

    // Child-page freshness check: the descendant redirects below are built from the CURRENT crawled
    // sitemap, so if a child page's URL was changed in WordPress AFTER the last crawl, we'd redirect
    // from a stale old URL (a mismatch). Warn when any page being reparented/reslugged has child
    // pages, so the user can verify the URL column is current (or re-crawl) before proceeding.
    const movedIds = new Set<number>([
      ...reparent.map(r => r.id),
      ...(wantSlug && slugRow!.type === "page" ? [slugRow!.id] : []),
    ]);
    const hasChildPages = [...movedIds].some(id => sitemapRows.some(r => r.type === "page" && r.parent === id));
    const childWarning = hasChildPages
      ? "\n\n⚠ This page has child page(s). Their URLs will change too, and this tool creates their 301 redirects from the URLs shown in the sitemap's URL column. Make sure those child URLs are up to date first — if a child page's URL was changed in WordPress since the last crawl (Run Crawl), its redirect may not be created correctly due to a URL mismatch. If you're unsure, either re-run a Full Crawl, or (faster) select the child page(s) with their checkboxes and click “Refresh Selected” to update just their URLs before continuing."
      : "";

    const ok = await showConfirm(
      `Apply to ${rowsSel.length} selected item${rowsSel.length === 1 ? "" : "s"}: ${parts.join("; ")}.\n\nThis makes live changes to your WordPress site.${childWarning}`,
      { title: hasChildPages ? "Child Pages — Please Confirm" : "Confirm Changes", tone: confirmTone, confirmLabel: hasChildPages ? "Continue" : "Save Changes", cancelLabel: "Cancel" },
    );
    if (!ok) return false;

    setSitemapBulkSaving(true);

    // Build the page parent→children tree from the CURRENT rows so we can also redirect the
    // descendants of any moved page (their URLs change too when an ancestor moves).
    const pageRows = sitemapRows.filter(r => r.type === "page");
    const childrenOf = new Map<number, SitemapRow[]>();
    for (const r of pageRows) { const a = childrenOf.get(r.parent) ?? []; a.push(r); childrenOf.set(r.parent, a); }
    const descendantsOf = (id: number): SitemapRow[] => {
      const out: SitemapRow[] = []; const stack = [...(childrenOf.get(id) ?? [])];
      while (stack.length) { const n = stack.pop()!; out.push(n); for (const k of childrenOf.get(n.id) ?? []) stack.push(k); }
      return out;
    };
    const oldUrlById = new Map<number, string>(pageRows.map(r => [r.id, r.url])); // captured BEFORE the move

    const tagPatch = new Map<string, string[]>();
    const movedOk: SitemapRow[] = [];
    let okCount = 0; const fails: string[] = [];
    for (const row of rowsSel) {
      const key = `${row.type}-${row.id}`;
      try {
        const body: Record<string, unknown> = {};
        let merged: string[] | null = null;
        if (values.tags.length) { merged = values.overwriteTags ? [...values.tags] : mergeTags(row.tags, values.tags); body.tags = await client.getOrCreateTags(merged); }
        if (values.status) body.status = values.status;
        const isReparent = reparent.some(r => r.id === row.id);
        if (isReparent) body.parent = newParentId;
        const isReslug = wantSlug && slugRow!.id === row.id;
        if (isReslug) body.slug = newSlug;
        if (!Object.keys(body).length) continue; // nothing applicable to this row (e.g. parent-only on a post)
        const r = await client.updatePageOrPost(row.type, row.id, body);
        if (!r.success) throw new Error(r.error ?? "Update failed");
        okCount++;
        if (merged) tagPatch.set(key, merged);
        if (isReparent || isReslug) movedOk.push(row);
      } catch (e) {
        fails.push(`${row.title || key}: ${e instanceof Error ? e.message : "error"}`);
      }
    }

    // For every moved page + its descendants, re-read the NEW url and create an old→new 301.
    const urlPatch = new Map<number, string>();
    const parentPatch = new Map<number, number>();
    const slugPatch = new Map<number, string>();
    let redirectCount = 0;
    if (movedOk.length) {
      const affected = new Map<number, SitemapRow>();
      for (const m of movedOk) { affected.set(m.id, m); for (const d of descendantsOf(m.id)) affected.set(d.id, d); }
      await Promise.all([...affected.values()].map(async pg => {
        try {
          const fresh = await client.getOnePageOrPost(pg.type, pg.id);
          if (!fresh) return;
          urlPatch.set(pg.id, fresh.url);
          slugPatch.set(pg.id, fresh.slug); // keep the row's slug in sync so re-opening Edit Page URL shows the new value
          if (movedOk.some(m => m.id === pg.id)) parentPatch.set(pg.id, fresh.parent);
          const oldUrl = oldUrlById.get(pg.id) ?? pg.url;
          if (fresh.url && oldUrl && fresh.url !== oldUrl) {
            const res = await client.createRedirect({ source: pathOf(oldUrl), destination: fresh.url, status: 301 });
            if (res.success) { redirectCount++; void recordHistory({ action: "redirect_added", url: `${pathOf(oldUrl)} → ${fresh.url}`, siteKey: siteKey(creds.siteUrl), pageId: pg.id }); }
          }
        } catch { /* skip this page's redirect on error */ }
      }));
    }

    // Manual redirect from the Edit Page URL dialog: add / update / remove this page's redirect.
    let redirectUpdated = false, redirectRemoved = false;
    if (redirRow && redirectAction !== "none") {
      try {
        if (redirectAction === "add") {
          const res = await client.createRedirect({ source: pathOf(redirRow.url), destination: redirDest, status: 301 });
          if (!res.success) throw new Error(res.error ?? "failed");
          redirectCount++; void recordHistory({ action: "redirect_added", url: `${pathOf(redirRow.url)} → ${redirDest}`, siteKey: siteKey(creds.siteUrl), pageId: redirRow.id });
        } else if (redirectAction === "update" && existingRedirect) {
          const res = await client.updateRedirect(existingRedirect.id, { destination: redirDest, status: 301, enabled: true });
          if (!res.success) throw new Error(res.error ?? "failed");
          redirectUpdated = true; void recordHistory({ action: "redirect_added", url: `${pathOf(redirRow.url)} → ${redirDest}`, siteKey: siteKey(creds.siteUrl), pageId: redirRow.id });
        } else if (redirectAction === "remove" && existingRedirect) {
          const res = await client.deleteRedirect(existingRedirect.id);
          if (!res.success) throw new Error(res.error ?? "failed");
          redirectRemoved = true;
        }
      } catch (e) { fails.push(`redirect: ${e instanceof Error ? e.message : "error"}`); }
    }

    // Optimistic table updates: merged tags, moved pages' url+slug+parent, descendants' url.
    if (tagPatch.size || urlPatch.size || parentPatch.size || slugPatch.size) {
      setSitemapRows(p => {
        const next = p.map(r => {
          let nr = r;
          const m = tagPatch.get(`${r.type}-${r.id}`);
          if (m) nr = { ...nr, tags: m };
          // URL + slug patches apply to whichever row changed (page reparent/reslug, or a post
          // reslug) — WP IDs are unique across types, so matching by id is safe. Parent is page-only.
          const u = urlPatch.get(r.id); if (u) nr = { ...nr, url: u };
          const sg = slugPatch.get(r.id); if (sg !== undefined) nr = { ...nr, slug: sg };
          if (r.type === "page") {
            const pp = parentPatch.get(r.id); if (pp !== undefined) nr = { ...nr, parent: pp };
          }
          return nr;
        });
        persistSitemapRows(next);
        return next;
      });
    }

    setSitemapBulkSaving(false);
    // Refresh the Redirected column with any redirect changes (moves, reslugs, manual add/update/remove).
    if (redirectCount || redirectUpdated || redirectRemoved) reloadSitemapRedirects();
    const bits: string[] = [];
    // Omit "Updated 0 of 1" when the only action was a redirect change.
    if (okCount > 0 || (redirectCount === 0 && !redirectUpdated && !redirectRemoved)) bits.push(`Updated ${okCount} of ${rowsSel.length} item${rowsSel.length === 1 ? "" : "s"}`);
    if (redirectCount) bits.push(`${redirectCount} redirect${redirectCount === 1 ? "" : "s"} created`);
    if (redirectUpdated) bits.push("redirect updated");
    if (redirectRemoved) bits.push("redirect removed");
    if (fails.length) bits.push(`${fails.length} failed`);
    setAuditToast({ type: fails.length ? "error" : "success", message: `${fails.length ? "" : "✓ "}${bits.join(" · ")}.` });
    return true;
  }

  // Multi-row Bulk Edit (from the toolbar button + selection).
  async function handleSitemapBulkEdit(values: BulkEditValues) {
    const rowsSel = [...sitemapSelected]
      .map(k => sitemapRows.find(r => `${r.type}-${r.id}` === k))
      .filter((r): r is SitemapRow => !!r);
    if (!rowsSel.length) { setSitemapBulkOpen(false); return; }
    const applied = await applyBulkEdit(rowsSel, values);
    if (applied) { setSitemapBulkOpen(false); setSitemapSelected(new Set()); }
  }

  // Single-row Parent Page Edit (hold a URL cell → dialog with only the Parent dropdown).
  async function handleSitemapParentEdit(values: BulkEditValues) {
    const key = sitemapParentEdit;
    const row = key ? sitemapRows.find(r => `${r.type}-${r.id}` === key) : null;
    if (!row) { setSitemapParentEdit(null); return; }
    const applied = await applyBulkEdit([row], values);
    if (applied) setSitemapParentEdit(null);
  }

  // Edit Custom URLs — match a pasted list of URLs to sitemap rows, then bulk-edit those rows.
  async function handleSitemapCustomUrlsEdit(values: BulkEditValues) {
    const norm = (u: string) => u.trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/[#?].*$/, "").replace(/\/+$/, "").toLowerCase();
    const pathOf = (u: string) => { try { return (new URL(/^https?:\/\//i.test(u) ? u : `https://${u.replace(/^\/+/, "")}`).pathname).replace(/\/+$/, "").toLowerCase() || "/"; } catch { return u.replace(/[#?].*$/, "").replace(/\/+$/, "").toLowerCase(); } };
    const byNorm = new Map<string, SitemapRow>();
    const byPath = new Map<string, SitemapRow>();
    for (const r of sitemapRows) { byNorm.set(norm(r.url), r); byPath.set(pathOf(r.url), r); }
    const lines = values.urls.split("\n").map(l => l.trim()).filter(Boolean);
    if (!lines.length) { void showAlert("Paste at least one URL to edit.", { title: "No URLs" }); return; }
    const matched: SitemapRow[] = [];
    const seen = new Set<string>();
    const unmatchedList: string[] = [];
    for (const line of lines) {
      const row = byNorm.get(norm(line)) ?? byPath.get(pathOf(line));
      if (!row) { unmatchedList.push(line); continue; }
      const k = `${row.type}-${row.id}`;
      if (!seen.has(k)) { seen.add(k); matched.push(row); }
    }
    // Surface exactly WHICH pasted URLs weren't found before any editing happens — they're either
    // pages added to the site since the last crawl (Sync fixes that) or simply mistyped.
    if (unmatchedList.length) {
      const shown = unmatchedList.slice(0, 25).map(u => `  • ${u}`).join("\n");
      const more = unmatchedList.length > 25 ? `\n  …and ${unmatchedList.length - 25} more` : "";
      const guidance = "If these pages were added to the website after the last page crawl, click the Sync button to refresh the sitemap. Otherwise, make sure the URLs were typed correctly.";
      if (!matched.length) {
        await showAlert(`None of the ${lines.length} pasted URL${lines.length === 1 ? "" : "s"} matched a page in the sitemap:\n\n${shown}${more}\n\n${guidance}`, { title: "No Matching URLs" });
        return;
      }
      const proceed = await showConfirm(
        `${unmatchedList.length} of the ${lines.length} pasted URL${lines.length === 1 ? "" : "s"} ${unmatchedList.length === 1 ? "wasn't" : "weren't"} found in the sitemap:\n\n${shown}${more}\n\n${guidance}\n\nContinue editing the ${matched.length} URL${matched.length === 1 ? "" : "s"} that matched?`,
        { title: "Some URLs Not Found", confirmLabel: `Continue with ${matched.length}`, cancelLabel: "Cancel", tone: "info" },
      );
      if (!proceed) return; // keep the Edit Custom URLs dialog open so the user can fix the list
    }
    const applied = await applyBulkEdit(matched, values, "default"); // green confirm button for this flow
    if (applied) setSitemapCustomUrlsOpen(false);
  }

  // Sync the table with WordPress: add newly-published pages (crawl only those), drop deleted
  // ones, and refresh titles/tags of the rest — without re-crawling existing pages' stats.
  async function handleSitemapSync() {
    if (sitemapCrawling) return;
    const client = clientRef.current;
    if (!client) { void showAlert("Connect your WordPress site first.", { title: "WordPress not connected", tone: "danger" }); return; }
    setSitemapCrawling(true);
    setSitemapProgress({ done: 0, total: 0 });
    setAuditToast({ type: "info", message: "⏳ Syncing pages & posts…" });
    try {
      const fresh = await client.getAllPagesAndPosts();
      const freshByKey = new Map(fresh.map(p => [`${p.type}-${p.id}`, p]));
      const existingKeys = new Set(sitemapRows.map(r => `${r.type}-${r.id}`));
      const kept = sitemapRows
        .filter(r => freshByKey.has(`${r.type}-${r.id}`))
        .map(r => { const f = freshByKey.get(`${r.type}-${r.id}`)!; return { ...r, title: f.title, tags: f.tags, url: f.url, parent: f.parent, slug: f.slug, schemaJson: schemaJsonFromBlocks(f.auditSchema) }; });
      const removed = sitemapRows.length - kept.length;
      const newPages = fresh.filter(p => !existingKeys.has(`${p.type}-${p.id}`));
      setSitemapProgress({ done: 0, total: newPages.length });
      const newRows: SitemapRow[] = [];
      const BATCH = 40;
      for (let i = 0; i < newPages.length; i += BATCH) {
        const batch = newPages.slice(i, i + BATCH);
        const stats = await crawlStatsByUrl(batch.map(p => p.url));
        batch.forEach(p => newRows.push(rowFrom(p, stats.get(p.url))));
        setSitemapProgress({ done: Math.min(i + BATCH, newPages.length), total: newPages.length });
        setAuditToast({ type: "info", message: `⏳ Syncing pages… ${Math.min(i + BATCH, newPages.length)} / ${newPages.length}` });
      }
      const merged = [...kept, ...newRows];
      setSitemapRows(merged);
      setSitemapSelected(new Set());
      const when = new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
      setSitemapCrawledAt(when);
      try { localStorage.setItem("seo_sitemap_crawled_at", when); } catch { /* ignore */ }
      persistSitemapRows(merged);
      setAuditToast({ type: "success", message: `✓ Sync complete — ${newRows.length} added, ${removed} removed.` });
    } catch (e) {
      setAuditToast({ type: "error", message: `✗ Sync failed: ${e instanceof Error ? e.message : "error"}` });
    } finally {
      setSitemapCrawling(false);
      setSitemapProgress(null);
    }
  }

  // Resume a paused full audit: re-enqueue the job (server skips already-saved tabs) and
  // reattach to its stream so progress continues filling in from where it left off.
  async function handleResumeAudit() {
    const id = auditResumeIdRef.current;
    if (!id) return;
    const key = getActiveSiteKey();
    setAuditPaused(false);
    setAuditRunning(true);
    setAuditDoneTabs(new Set(savedAuditTabIds())); // seed progress from actually-saved tabs, not stale job bookkeeping
    setSiteJobActive(key, true);
    auditJobIdRef.current = id;
    try { localStorage.setItem(ACTIVE_AUDIT_JOB_KEY, id); } catch { /* ignore */ }
    setAuditToast({ type: "info", message: "⏳ Resuming audit…" });
    try { await resumeCrawlJob(id); } catch { /* may already be running — just reattach */ }

    const finish = () => { setAuditRunning(false); setAuditPaused(false); setSiteJobActive(key, false); auditJobIdRef.current = null; try { localStorage.removeItem(ACTIVE_AUDIT_JOB_KEY); } catch { /* ignore */ } };
    const toPaused = () => { finish(); if (savedAuditTabIds().length >= AUDIT_TAB_COUNT) return; auditResumeIdRef.current = id; setAuditPaused(true); setAuditToast({ type: "info", message: "⏸ Audit paused — progress saved. Click Resume Audit to finish." }); };
    const close = subscribeCrawlJob(id, {
      onProgress: (msg) => setAuditToast({ type: "info", message: `⏳ ${msg}` }),
      onSection: (sid, html) => {
        setRegeneratedSections(prev => { const next = { ...prev, [sid]: html }; localStorage.setItem("full_audit_sections", JSON.stringify(next)); return next; });
        setAuditDoneTabs(prev => { const n = new Set(prev); n.add(sid); return n; });
        setInterruptedSections(prev => { if (!prev.has(sid)) return prev; const n = new Set(prev); n.delete(sid); return n; }); // this tab was just refreshed
        if (sid === "competitors") void handleCompetitorsCrawl();
        else if (sid === "citations") void enrichCitationsSection();
        else if (sid === "llm") void enrichLlmSection();
      },
      onDone: () => { finish(); setAuditToast({ type: "success", message: "✓ Full audit complete." }); },
      onError: (msg) => { toPaused(); setAuditToast({ type: "error", message: `✗ ${msg} — click Resume Audit to retry.` }); },
      onInterrupted: toPaused,
      onCanceled: finish,
    });
    auditJobCloseRef.current = close;
  }

  // ── Run Audit — step 1: validate all API keys + test connections ────────
  async function handleRunAudit() {
    // A full audit and a tab-specific crawl can't run at the same time — it's one or the other.
    if (crawlingSections.size > 0 || competitorsCrawling) {
      void showAlert("A Full Audit can't run at the same time as a tab-specific crawl.\n\nStop the running tab crawl (or wait for it to finish), then run the Full Audit.", { title: "Tab crawl in progress" });
      return;
    }
    const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value?.trim() || "";
    const businessName = val("cover-business-name");
    const siteUrl      = val("cover-website-url");
    const city         = val("cover-location");
    const gbpCategory  = val("cover-gbp-category");
    const keyword      = val("cover-kw-1");

    // Required cover fields must all be filled before a full audit. The Primary GBP Category is
    // required — it drives the business type and industry-appropriate schema recommendations (and is
    // the fallback source when no GBP is connected/matched). The Full Local Business Address is
    // intentionally NOT required: website-only businesses (no physical address) can still run the
    // audit; the address is read from its field elsewhere as the GBP snack-pack crawl origin when set.
    const missingFields: string[] = [];
    if (!businessName) missingFields.push("Full Business Name");
    if (!siteUrl)      missingFields.push("Website URL");
    if (!city)         missingFields.push("City");
    if (!gbpCategory)  missingFields.push("Primary GBP Category");
    if (!keyword)      missingFields.push("Target Keyword (first keyword field)");
    if (missingFields.length) {
      void showAlert("Please complete the following fields before running the audit:\n\n" + missingFields.map(f => `  • ${f}`).join("\n"), { title: "Missing Required Fields" });
      return;
    }

    const ahrefsKey = loadAhrefsKey();
    const anthropicKey = loadAnthropicKey();
    const { login: dfsLogin, password: dfsPassword } = loadDataForSeoCredentials();

    // Ahrefs key is OPTIONAL — when absent, metrics fall back to DataForSEO. Only Claude +
    // DataForSEO are hard requirements.
    const missing: string[] = [];
    if (!anthropicKey)           missing.push("Claude (Anthropic) API Key");
    if (!dfsLogin || !dfsPassword) missing.push("DataForSEO Credentials");
    if (missing.length > 0)      { setAuditConfirm({ type: "missing_keys", missing }); return; }

    // Required keys present — test connections
    setAuditConfirm({ type: "testing" });

    const [ahrefs, claude, dataforseo] = await Promise.all([
      // Skip the Ahrefs probe entirely when no key is set (metrics will use DataForSEO).
      ahrefsKey
        ? fetch("/api/test-ahrefs", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ apiKey: ahrefsKey }) })
            .then(r => r.json() as Promise<ApiTestResult>)
            .catch((): ApiTestResult => ({ ok: false, message: "Network error — server unreachable." }))
        : Promise.resolve<ApiTestResult>({ ok: true, message: "Not set (optional) — metrics via DataForSEO" }),
      fetch("/api/test-claude", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ apiKey: anthropicKey }) })
        .then(r => r.json() as Promise<ApiTestResult>)
        .catch((): ApiTestResult => ({ ok: false, message: "Network error — server unreachable." })),
      fetch(`https://api.dataforseo.com/v3/appendix/user_data`, { headers: { Authorization: `Basic ${btoa(`${dfsLogin}:${dfsPassword}`)}` } })
        .then(async r => {
          if (r.status === 401) return { ok: false, message: "Invalid credentials." };
          if (!r.ok) return { ok: false, message: `HTTP ${r.status}` };
          const j = await r.json() as { tasks?: Array<{ result?: Array<{ money?: { balance?: number } }> }> };
          const bal = j.tasks?.[0]?.result?.[0]?.money?.balance;
          return { ok: true, message: bal !== undefined ? `Connected — balance $${Number(bal).toFixed(2)}` : "Connected" };
        })
        .catch((): ApiTestResult => ({ ok: false, message: "Network error." })),
    ]);

    setAuditConfirm({ type: "preflight", allPassed: ahrefs.ok && claude.ok && dataforseo.ok, ahrefs, claude, dataforseo });
  }

  // ── Run Audit — step 2: execute after confirmation ────────────
  async function executeAudit() {
    setAuditConfirm(null);

    // Always read live values from the cover input fields — never fall back to hardcoded defaults
    const siteUrl      = (document.getElementById("cover-website-url")   as HTMLInputElement | null)?.value?.trim() ?? "";
    const location     = (document.getElementById("cover-location")     as HTMLInputElement | null)?.value?.trim() ?? "";
    const businessName = (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim() ?? "";

    // Collect all 6 keyword inputs
    const keywords: string[] = [];
    for (let i = 1; i <= 5; i++) {
      const val = (document.getElementById(`cover-kw-${i}`) as HTMLInputElement | null)?.value?.trim() || "";
      keywords.push(val);
    }
    const keyword = keywords[0];

    const ahrefsKey = loadAhrefsKey();
    const { login: dataForSeoLogin, password: dataForSeoPassword } = loadDataForSeoCredentials();

    // Lock out static HTML from this point forward — even if sections fail to generate
    if (!auditEverRun) {
      setAuditEverRun(true);
      localStorage.setItem("seo_audit_ever_run", "true");
    }

    const abort = new AbortController();
    auditAbortRef.current = abort;
    setAuditRunning(true);
    setAuditDoneTabs(new Set()); // fresh run — all tabs start in "Generating…" until each streams in
    setInterruptedSections(new Set()); // a full audit regenerates every tab → clear any stale per-tab "Resume"
    setAuditPaused(false); auditResumeIdRef.current = null; // a fresh run supersedes any paused audit
    setAuditToast({ type: "info", message: "⏳ Running live Google SERP…" });

    try {
      const res = await fetch("/api/run-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword, keywords, siteUrl: siteUrl.startsWith("http") ? siteUrl : `https://${siteUrl}`, location, ahrefsKey, dataForSeoLogin, dataForSeoPassword, businessName, customCompetitors: getCompetitorMode() === "custom" ? readCustomCompetitors() : [] }),
        signal: abort.signal,
      });
      const result = await res.json() as {
        ok: boolean; error?: string; serpSource?: string;
        target?: { domain: string; dr: number | null; traffic: number | null };
        competitors?: Array<{ domain: string; dr: number | null; traffic: number | null }>;
        keywords?: string[];
        positions?: (number | null)[];
        volumes?: (number | null)[];
        mapPackEntries?: Array<{ name: string; domain: string; url: string; position: number; rating: number | null; reviewCount: number | null; address: string; isSponsored: boolean; photosCount: number | null; servicesCount: number | null; productsCount: number | null; lastReviewDate: string | null; reviewVelocity: number | null; lastPostDate: string | null }>;
        mapPackPosition?: number | null;
      };

      if (!result.ok) throw new Error(result.error ?? "Audit failed");

      // ── Persist competitor domains so Run Crawl can use them dynamically ──
      if (result.target) localStorage.setItem("seo_target_domain", result.target.domain);
      if (result.competitors) localStorage.setItem("seo_competitor_domains", JSON.stringify(result.competitors.map((c: { domain: string }) => c.domain)));

      // ── Update cover competitor strip ────────────────────────────
      const cover = coverRef.current;
      if (cover && result.target && result.competitors) {
        const formatTraffic = (n: number | null) =>
          n === null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);

        const youDr      = cover.querySelector(".cs-you .cs-dr");
        const youTraffic = cover.querySelector(".cs-you .cs-traffic");
        const youName    = cover.querySelector<HTMLElement>(".cs-you .cs-name");
        if (youDr)      youDr.textContent     = result.target.dr !== null ? String(Math.round(result.target.dr)) : "—";
        if (youTraffic) youTraffic.textContent = formatTraffic(result.target.traffic);
        if (youName)    youName.textContent    = result.target.domain;

        const compItems = cover.querySelectorAll<HTMLElement>(".cs-item:not(.cs-you)");
        result.competitors.forEach((comp, i) => {
          const item = compItems[i];
          if (!item) return;
          const name      = item.querySelector<HTMLElement>(".cs-name");
          const drEl      = item.querySelector(".cs-dr");
          const trafficEl = item.querySelector(".cs-traffic");
          if (name)      { name.textContent = comp.domain; name.style.fontSize = "14px"; }
          if (drEl)      drEl.textContent      = comp.dr !== null ? String(Math.round(comp.dr)) : "—";
          if (trafficEl) trafficEl.textContent = formatTraffic(comp.traffic);
        });
      }

      // ── Update keyword position + volume labels ───────────────────
      if (cover && result.positions && result.volumes) {
        const formatVol = (n: number | null) =>
          n === null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k/mo` : `${n}/mo`;

        result.positions.forEach((pos, i) => {
          const posEl = document.getElementById(`kw-pos-${i + 1}`);
          const volEl = document.getElementById(`kw-vol-${i + 1}`);
          if (posEl) posEl.textContent = pos !== null ? `#${pos}` : "Not ranking";
          if (volEl) volEl.textContent = formatVol(result.volumes![i]);
        });
      }

      const ranked = result.positions?.filter(p => p !== null).length ?? 0;
      const competitorCount = result.competitors?.length ?? 0;
      const kwCount = keywords.filter(k => k).length;

      // Persist the live cover strip so it survives page reloads
      saveCompetitorStrip();
      commitActiveAndFlush(siteUrl); // adopt this domain's bucket (markTabsCrawled below also flushes)

      // Stamp the full audit date on the cover page + the toolbar (full audit also updates all tabs)
      const newAuditDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      localStorage.setItem("cover_last_audit_date", newAuditDate); // cover page "Last Full Audit Run"
      localStorage.setItem("seo_last_tab_crawl",    newAuditDate); // toolbar "Last Audit Date"
      setAuditDate(newAuditDate);
      markTabsCrawled(SECTIONS.map(s => s.id), newAuditDate); // full audit refreshes every tab
      const auditDateEl = document.getElementById("cv-last-audit-date");
      if (auditDateEl) auditDateEl.textContent = newAuditDate;

      // Chain full analysis
      await runFullAnalysis({
        target: result.target ?? null,
        competitors: result.competitors ?? [],
        keywords,
        positions: result.positions ?? [],
        volumes: result.volumes ?? [],
        mapPackEntries: result.mapPackEntries ?? [],
        mapPackPosition: result.mapPackPosition ?? null,
        businessName,
        competitorCount,
        ranked,
        kwCount,
        signal: abort.signal,
      });

    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      setAuditToast({ type: "error", message: `✗ ${err instanceof Error ? err.message : "Audit failed"}` });
    } finally {
      auditAbortRef.current = null;
      setAuditRunning(false);
    }
  }

  async function runFullAnalysis(opts: {
    target: { domain: string; dr: number | null; traffic: number | null } | null;
    competitors: Array<{ domain: string; dr: number | null; traffic: number | null }>;
    keywords: string[];
    positions: (number | null)[];
    volumes: (number | null)[];
    mapPackEntries: Array<{ name: string; domain: string; url: string; position: number; rating: number | null; reviewCount: number | null; address: string; isSponsored: boolean; photosCount: number | null; servicesCount: number | null; productsCount: number | null; lastReviewDate: string | null; reviewVelocity: number | null; lastPostDate: string | null }>;
    mapPackPosition: number | null;
    businessName: string;
    competitorCount: number;
    ranked: number;
    kwCount: number;
    signal?: AbortSignal;
  }) {
    const { target, competitors, keywords, positions, volumes, mapPackEntries, mapPackPosition, businessName, competitorCount, ranked, kwCount, signal } = opts;
    const ahrefsKey = loadAhrefsKey();
    const anthropicKey = loadAnthropicKey();

    if (!target) {
      setAuditToast({ type: "success", message: `✓ Audit complete — ${competitorCount} competitors via live Google · ${ranked}/${kwCount} keywords ranking` });
      return;
    }

    const domains = [target.domain, ...competitors.map(c => c.domain)];

    try {
      setAuditToast({ type: "info", message: "⏳ Fetching comprehensive site metrics…" });

      const { login: fmDfsLogin, password: fmDfsPassword } = loadDataForSeoCredentials();
      const metricsRes = await fetch("/api/full-metrics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains, ahrefsKey, dfsLogin: fmDfsLogin, dfsPassword: fmDfsPassword }),
        signal,
      });
      const metricsData = await metricsRes.json() as {
        ok?: boolean; error?: string;
        sites?: unknown[]; topPages?: unknown[]; anchors?: unknown[];
        mapPack?: unknown;
      };
      if (metricsData.ok === false) throw new Error(metricsData.error ?? "Full metrics failed");

      // Merge map pack data from the audit step into the metrics object
      if (mapPackEntries.length > 0 || mapPackPosition !== null) {
        metricsData.mapPack = {
          entries: mapPackEntries,
          targetPosition: mapPackPosition,
          targetBusinessName: businessName,
        };
      }

      const metrics = metricsData as { sites: unknown[]; topPages: unknown[]; anchors: unknown[]; mapPack?: unknown };

      if (!anthropicKey) {
        setAuditToast({ type: "success", message: `✓ Audit complete — ${competitorCount} competitors via live Google · ${ranked}/${kwCount} keywords ranking · site metrics fetched (no Anthropic key for AI analysis)` });
        return;
      }

      setAuditToast({ type: "info", message: "⏳ Running AI analysis across all 20 tabs…" });

      const analyzeRes = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          metrics,
          keywords,
          positions,
          volumes,
          targetDomain: target.domain,
          anthropicKey,
        }),
        signal,
      });
      const analysisData = await analyzeRes.json() as {
        ok?: boolean; error?: string;
        generatedAt?: string; sections?: SectionAnalysis[];
      };
      if (analysisData.ok === false) throw new Error(analysisData.error ?? "Analysis failed");

      if (analysisData.sections) {
        setSectionAnalyses(analysisData.sections);
        localStorage.setItem("full_audit_analyses", JSON.stringify(analysisData.sections));
        scheduleFlush();
      }

      // ── Regenerate all 20 section tabs as a DURABLE BACKGROUND JOB ────
      // The crawl runs server-side and checkpoints each finished section into the user's
      // account, so it survives a browser close/refresh and is resumable after a server
      // restart. The server pulls API keys from the user's encrypted record — none are sent
      // here. We subscribe to the job's SSE stream for live progress; if the connection
      // drops, EventSource reconnects and replays, and on return listActiveJobs() reattaches.
      setAuditToast({ type: "info", message: "⏳ Regenerating all 20 tabs — sections will appear as they complete…" });

      const jobParams = {
        metrics,
        keywords,
        positions,
        volumes,
        targetDomain: target.domain,
        targetUrl: `https://${target.domain}`,
        location: (document.getElementById("cover-location") as HTMLInputElement | null)?.value?.trim() ?? "",
        cityLocation: (document.getElementById("cover-location") as HTMLInputElement | null)?.value?.trim() ?? "",
        auditDate: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
        businessName: (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim() ?? "",
        gbpCategory: (document.getElementById("cover-gbp-category") as HTMLInputElement | null)?.value?.trim() ?? "",
      };

      const jobSiteKey = getActiveSiteKey() || siteKey(target.domain);
      const { jobId } = await startCrawlJob({ siteKey: jobSiteKey, kind: "full-audit", params: jobParams });
      setSiteJobActive(jobSiteKey, true); // the job owns this site's blob until it finishes
      auditJobIdRef.current = jobId;
      try { localStorage.setItem(ACTIVE_AUDIT_JOB_KEY, jobId); } catch { /* ignore */ }

      let genCount = 0;
      let streamComplete = false;
      let wasPaused = false;
      let wasCanceled = false;

      try {
        await new Promise<void>((resolve, reject) => {
          const close = subscribeCrawlJob(jobId, {
            onProgress: (msg) => setAuditToast({ type: "info", message: `⏳ ${msg}` }),
            onSection: (id, html) => {
              genCount++;
              // Progressive update — tab content appears as each section finishes.
              setRegeneratedSections(prev => {
                const next = { ...prev, [id]: html };
                localStorage.setItem("full_audit_sections", JSON.stringify(next));
                return next;
              });
              setAuditDoneTabs(prev => { const n = new Set(prev); n.add(id); return n; });
              setInterruptedSections(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n; }); // this tab was just refreshed
              // Same client-side enrichment the per-tab Run Crawl does (fire-and-forget).
              if (id === "competitors") void handleCompetitorsCrawl();
              else if (id === "citations") void enrichCitationsSection();
              else if (id === "llm") void enrichLlmSection();
            },
            onDone: () => { streamComplete = true; resolve(); },
            onError: (msg) => reject(new Error(msg)),
            onInterrupted: () => { wasPaused = true; resolve(); },   // paused → offer Resume
            onCanceled: () => { wasCanceled = true; resolve(); },    // discarded via Stop
          });
          auditJobCloseRef.current = close;
        });
      } finally {
        setSiteJobActive(jobSiteKey, false);
        auditJobCloseRef.current?.();
        auditJobCloseRef.current = null;
        auditJobIdRef.current = null;
        try { localStorage.removeItem(ACTIVE_AUDIT_JOB_KEY); } catch { /* ignore */ }
      }

      if (wasCanceled) return; // Stop/Discard already showed its own toast

      // Only surface Resume if tabs are genuinely missing — if the pause landed right as the
      // last tab finished, everything's saved, so treat it as a normal completion.
      if (wasPaused && savedAuditTabIds().length < AUDIT_TAB_COUNT) {
        const savedN = savedAuditTabIds().length;
        auditResumeIdRef.current = jobId;
        setAuditPaused(true);
        setAuditToast({ type: "info", message: `⏸ Audit paused — ${savedN} tab${savedN === 1 ? "" : "s"} saved. Click Resume Audit to finish the remaining tabs.` });
        return;
      }
      setAuditPaused(false); // success / fully-complete pause → ensure the button isn't stuck on Resume

      if (genCount > 0) {
        const suffix = streamComplete ? "" : ` (connection closed early — ${genCount} tabs saved)`;
        setAuditToast({ type: "success", message: `✓ Full audit complete — ${genCount}/20 tabs updated with live data for ${target.domain}${suffix}` });
      } else {
        setAuditToast({ type: "success", message: `✓ Audit complete — ${competitorCount} competitors · ${ranked}/${kwCount} keywords ranking` });
      }

    } catch (err) {
      setAuditToast({ type: "error", message: `✗ Full analysis error: ${err instanceof Error ? err.message : "Unknown error"}` });
    }
  }

  // ── Persist competitor strip to localStorage ──────────────────
  function saveCompetitorStrip() {
    const cover = coverRef.current; if (!cover) return;
    const youName = cover.querySelector<HTMLElement>(".cs-you .cs-name")?.textContent ?? "";
    const youDr   = cover.querySelector<HTMLElement>(".cs-you .cs-dr")?.textContent ?? "";
    const youTraf = cover.querySelector<HTMLElement>(".cs-you .cs-traffic")?.textContent ?? "";
    const comps   = Array.from(cover.querySelectorAll<HTMLElement>(".cs-item:not(.cs-you)")).map(item => ({
      name:    item.querySelector<HTMLElement>(".cs-name")?.textContent ?? "",
      dr:      item.querySelector<HTMLElement>(".cs-dr")?.textContent ?? "",
      traffic: item.querySelector<HTMLElement>(".cs-traffic")?.textContent ?? "",
    }));
    localStorage.setItem("seo_cover_strip", JSON.stringify({
      you: { name: youName, dr: youDr, traffic: youTraf },
      competitors: comps,
    }));
  }

  // ── Competitors tab: single-section crawl ────────────────────
  async function handleCompetitorsCrawl() {
    // Real Page Inventory is independent of DataForSEO (it crawls each site's own URLs),
    // so kick it off first — even if brand-mention creds are missing it still populates.
    void enrichPageInventorySection();
    const { login, password } = loadDataForSeoCredentials();
    if (!login || !password) {
      setAuditToast({ type: "error", message: "DataForSEO credentials required — open ⚙ Settings → DataForSEO and save your login/password." });
      return;
    }

    // Resolve target domain from cover field
    const rawSiteUrl = (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim()
      || localStorage.getItem("cover_website_url") || "";
    let targetDomain = "";
    try { targetDomain = new URL(rawSiteUrl.startsWith("http") ? rawSiteUrl : `https://${rawSiteUrl}`).hostname.replace(/^www\./, ""); }
    catch { targetDomain = rawSiteUrl.replace(/^www\./, ""); }
    if (!targetDomain) {
      setAuditToast({ type: "error", message: "Enter the website URL in the cover fields first." });
      return;
    }

    // Competitor domains from last audit run; fall back to the static competitor list
    const FALLBACK_COMPETITORS = [
      "maryvillenj.org", "newchapteryouthprogram.com",
      "embarkbh.com", "newbridgehealth.org", "hackensackmeridianhealth.org",
    ];
    let competitorDomains: string[] = [];
    try { competitorDomains = JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "[]"); }
    catch { competitorDomains = []; }
    if (!competitorDomains.length) competitorDomains = FALLBACK_COMPETITORS;

    const allDomains = [targetDomain, ...competitorDomains.filter(d => d !== targetDomain)];

    // Brand variant builder (used for target site's business name)
    function buildBrandVariants(name: string): string[] {
      if (!name) return [];
      const v = new Set<string>([name]);
      const sd = name.indexOf(" - ");
      if (sd !== -1) {
        v.add(name.replace(/ - /g, " "));
        const after = name.slice(sd + 3).trim();
        if (after) v.add(after);
      } else {
        const d = name.indexOf("-");
        if (d !== -1) {
          v.add(name.replace(/-/g, " "));
          const after = name.slice(d + 1).trim();
          if (after) v.add(after);
        }
      }
      return [...v];
    }

    setCompetitorsCrawling(true);

    try {
      // ── Step 1: Fetch brand names from each site's homepage ──────────
      setAuditToast({ type: "info", message: `⏳ Fetching brand names from ${allDomains.length} homepages…` });
      const bnRes = await fetch("/api/fetch-brand-names", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains: allDomains }),
      });
      const bnData = await bnRes.json() as { ok: boolean; results?: Array<{ domain: string; brandName: string }>; error?: string };
      if (!bnData.ok || !bnData.results) throw new Error(bnData.error ?? "Brand name fetch failed");
      const brandNameMap = new Map(bnData.results.map(r => [r.domain, r.brandName]));

      // ── Step 2: Build site configs — target uses cover business name + variants ──
      const rawBusinessName =
        (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim()
        || localStorage.getItem("cover_business_name")
        || brandNameMap.get(targetDomain) || "";

      const sites = allDomains.map((domain, i) => {
        const isTarget = i === 0;
        const brandName = isTarget
          ? (rawBusinessName || brandNameMap.get(domain) || domain)
          : (brandNameMap.get(domain) || domain);
        return { domain, brandName, brandVariants: buildBrandVariants(brandName) };
      });

      // ── Step 3: Run Google SERP brand mention searches ───────────────
      setAuditToast({ type: "info", message: `⏳ Searching Google for URL & brand mentions across ${sites.length} sites…` });
      const bmRes = await fetch("/api/brand-mentions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sites: sites.map(({ domain, brandName, brandVariants }) => ({ domain, brandName, brandVariants })), dataForSeoLogin: login, dataForSeoPassword: password }),
      });
      const bmData = await bmRes.json() as { ok: boolean; results?: Array<{ domain: string; urlMentions: number; brandMentions: number }>; error?: string };
      if (!bmData.ok || !bmData.results) throw new Error(bmData.error ?? "Brand mention search failed");

      // ── Step 4: Rebuild table with live competitor data ──────────────
      const tbody = document.getElementById("bm-tbody");
      if (tbody) {
        tbody.innerHTML = bmData.results.map((r, i) => {
          const cid  = r.domain.replace(/[^a-z0-9]/gi, "-");
          const name = brandNameMap.get(r.domain) || r.domain;
          const isTarget = i === 0;
          return `<tr${isTarget ? ' class="yr"' : ""}>
            <td>${isTarget ? `<strong>${r.domain}</strong>` : r.domain}</td>
            <td><a href="https://${r.domain}" target="_blank" class="visit-link">↗</a></td>
            <td id="bm-url-${cid}">${r.urlMentions.toLocaleString()}</td>
            <td id="bm-brand-${cid}">${r.brandMentions.toLocaleString()} *<br><span style="font-size:10px;color:var(--ink4);font-style:italic;">${name}</span></td>
          </tr>`;
        }).join("");
      }

      // Persist WITH brand names + ordering so the useEffect can rebuild the
      // whole table on re-render (cell-by-ID updates fail after regeneration
      // resets the injected table to the static rows).
      const brandResults = bmData.results.map(r => ({
        ...r,
        brandName: brandNameMap.get(r.domain) || r.domain,
      }));
      setBrandMentionResults(brandResults);
      localStorage.setItem("competitors_brand_data", JSON.stringify(brandResults));

      const now = new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
      setCompetitorsCrawlTime(now);
      localStorage.setItem("competitors_last_crawl", now);
      scheduleFlush();
      setAuditToast({ type: "success", message: "✓ Competitors tab crawl complete — brand mentions updated" });
    } catch (err) {
      setAuditToast({ type: "error", message: `✗ Crawl error: ${err instanceof Error ? err.message : "Network error"}` });
    } finally {
      setCompetitorsCrawling(false);
    }
  }

  // Client-side enrichment for the Citations tab — fetches the live citation
  // comparison and prepends it to the section HTML. Used by BOTH the per-tab Run
  // Crawl and the full audit so the tab is identical either way.
  async function enrichCitationsSection() {
    try {
      const { login: dfsLogin, password: dfsPassword } = loadDataForSeoCredentials();
      if (!dfsLogin || !dfsPassword) return;
      const targetDomain = localStorage.getItem("seo_target_domain") ?? "";
      let competitorDomains: string[] = [];
      try { competitorDomains = JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "[]") ?? []; } catch { competitorDomains = []; }
      const location = localStorage.getItem("cover_location") ?? "";
      const storedMetrics = (() => { try { return JSON.parse(localStorage.getItem("seo_full_metrics") ?? "{}"); } catch { return {}; } })() as { sites?: Array<{ domain?: string; siteTitle?: string }> };
      const allDomains = [targetDomain, ...competitorDomains.slice(0, 5)];
      const labels = allDomains.map((d, i) => (storedMetrics?.sites ?? [])[i]?.siteTitle || d);
      const storedBrandNames: Record<string, string> = (() => { try { return JSON.parse(localStorage.getItem("seo_brand_names") ?? "{}"); } catch { return {}; } })();
      const brandNames = allDomains.map((d, i) => i === 0 ? (localStorage.getItem("cover_business_name") || labels[0]) : (storedBrandNames[d] ?? labels[i]));
      const res = await fetch("/api/fetch-citations", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ domains: allDomains, labels, brandNames, location, dfsLogin, dfsPassword }) });
      const data = await res.json() as { ok: boolean; html?: string; directoryHtml?: string };
      if (data.ok && data.html) {
        setRegeneratedSections(prev => {
          // The live per-directory check replaces the AI-estimated NAP Citation Audit table in place.
          const withDirs = data.directoryHtml ? replaceLiveBlock(prev["citations"] ?? "", data.directoryHtml, /nap citation audit/i) : (prev["citations"] ?? "");
          const combined = injectLiveBlock(withDirs, data.html!, [/citation comparison/i, /top directory listings/i]);
          const next = { ...prev, citations: combined };
          localStorage.setItem("full_audit_sections", JSON.stringify(next));
          return next;
        });
        scheduleFlush();
      }
    } catch (e) { console.warn("[CITATIONS] fetch failed:", e instanceof Error ? e.message : e); }
  }

  // Client-side enrichment for the LLM/AI Search tab — fetches AI visibility data
  // and prepends it. Shared by per-tab Run Crawl and the full audit.
  async function enrichLlmSection() {
    try {
      const { login: dfsLogin, password: dfsPassword } = loadDataForSeoCredentials();
      if (!dfsLogin || !dfsPassword) return;
      const targetDomain = localStorage.getItem("seo_target_domain") ?? "";
      let competitorDomains: string[] = [];
      try { competitorDomains = JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "[]") ?? []; } catch { competitorDomains = []; }
      const location = localStorage.getItem("cover_location") ?? "";
      const keywords = [1, 2, 3, 4, 5].map(i => localStorage.getItem(`cover_kw_${i}`) ?? "");
      const storedMetrics = (() => { try { return JSON.parse(localStorage.getItem("seo_full_metrics") ?? "{}"); } catch { return {}; } })() as { sites?: Array<{ domain?: string; siteTitle?: string }> };
      const allDomains = [targetDomain, ...competitorDomains.slice(0, 5)];
      const labels = allDomains.map((d, i) => (storedMetrics?.sites ?? [])[i]?.siteTitle || d);
      const res = await fetch("/api/fetch-ai-visibility", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ domains: allDomains, labels, keywords, location, dfsLogin, dfsPassword }) });
      const data = await res.json() as { ok: boolean; html?: string };
      if (data.ok && data.html) {
        setRegeneratedSections(prev => {
          const combined = injectLiveBlock(prev["llm"] ?? "", data.html!, [/top domains cited in llms/i]);
          const next = { ...prev, llm: combined };
          localStorage.setItem("full_audit_sections", JSON.stringify(next));
          return next;
        });
        scheduleFlush();
      }
    } catch (e) { console.warn("[LLM] AI visibility fetch failed:", e instanceof Error ? e.message : e); }
  }

  // Client-side enrichment for the Competitors tab — fetches the REAL Page Inventory
  // (each site's own crawled URLs, categorized) and injects it into the section, replacing
  // any prior (estimated or live) Page Inventory sub-section. Shared by the per-tab Run
  // Crawl and the full audit so the tab is identical either way.
  async function enrichPageInventorySection() {
    try {
      const targetDomain = localStorage.getItem("seo_target_domain")
        ?? (() => { try { return new URL((localStorage.getItem("cover_website_url") ?? "").replace(/^(?!https?:)/, "https://")).hostname.replace(/^www\./, ""); } catch { return ""; } })();
      if (!targetDomain) return;
      let competitorDomains: string[] = [];
      try { competitorDomains = JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "[]") ?? []; } catch { competitorDomains = []; }
      const allDomains = [targetDomain, ...competitorDomains.filter(d => d !== targetDomain).slice(0, 5)];
      const res = await fetch("/api/page-inventory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains: allDomains, anthropicKey: loadAnthropicKey(), businessName: localStorage.getItem("cover_business_name") ?? "" }),
      });
      const data = await res.json() as { ok: boolean; html?: string };
      if (!data.ok || !data.html) return;

      setRegeneratedSections(prev => {
        const current = prev["competitors"] ?? "";
        if (!current) return prev;
        const tmp = document.createElement("div");
        tmp.innerHTML = current;
        // Remove any existing Page Inventory (the old AI estimate, or a prior live one).
        tmp.querySelectorAll("#real-page-inventory").forEach(n => n.remove());
        tmp.querySelectorAll<HTMLElement>(".sub").forEach(sub => {
          const lbl = sub.querySelector(".sub-lbl");
          if (lbl && /page inventory/i.test(lbl.textContent ?? "")) sub.remove();
        });
        // Insert the real table just before the first fix card, else append.
        const wrap = document.createElement("div");
        wrap.innerHTML = data.html!.trim();
        const node = wrap.firstElementChild;
        if (!node) return prev;
        const anchor = tmp.querySelector('.oc[id^="oc-cp"]') ?? tmp.querySelector(".oc");
        if (anchor) anchor.parentElement?.insertBefore(node, anchor);
        else tmp.appendChild(node);
        const next = { ...prev, competitors: tmp.innerHTML };
        localStorage.setItem("full_audit_sections", JSON.stringify(next));
        return next;
      });
      scheduleFlush();
    } catch (e) { console.warn("[PAGE-INV] fetch failed:", e instanceof Error ? e.message : e); }
  }

  // ── Per-tab Run Crawl: regenerates one section via Claude + live metrics ──
  async function handleRunCrawl(sectionId: string) {
    // Clear any leftover status banner up front, so if a precondition guard below bails (e.g. a
    // modal alert about missing competitor data), the user isn't left staring at a STALE error
    // from an earlier crawl (which made a blocked crawl look like a live "no content" failure).
    setAuditToast(null);
    // A tab crawl can't run while a Full Audit is running — it's one or the other.
    if (auditRunning) {
      void showAlert("A tab crawl can't run while a Full Audit is in progress.\n\nThe Full Audit already refreshes every tab — wait for it to finish (or stop it) before crawling a single tab.", { title: "Full Audit in progress" });
      return;
    }
    // Guard: required cover fields + competitors must be present before any crawl.
    const missingFields: string[] = [];
    if (!(localStorage.getItem("cover_business_name") ?? "").trim()) missingFields.push("Business Name");
    if (!(localStorage.getItem("cover_website_url")   ?? "").trim()) missingFields.push("Website URL");
    if (!(localStorage.getItem("cover_location")      ?? "").trim()) missingFields.push("Location");
    if (!(localStorage.getItem("cover_kw_1")          ?? "").trim()) missingFields.push("Target Keyword (field 1)");

    // Check competitors via stored domains or metrics strip
    const hasCompetitors = (() => {
      try {
        const stored = JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "null");
        if (Array.isArray(stored) && stored.length) return true;
        const m = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null");
        if (Array.isArray(m?.sites) && m.sites.length > 1) return true;
      } catch { /* ignore */ }
      return false;
    })();
    if (!hasCompetitors) missingFields.push("Competitor domains (click Get Competitors)");

    if (missingFields.length) {
      void showAlert(
        "Please complete the following before running a crawl:\n\n" +
        missingFields.map(f => `  • ${f}`).join("\n") +
        "\n\nFill in your business information and target keywords on the cover page, then click Get Competitors.",
        { title: "Missing Required Fields" }
      );
      return;
    }

    // Gov/Local Links and PAA FAQs run geolocated SERP searches — require the
    // City field in "City, State" format so DataForSEO can resolve the location.
    if (sectionId === "govlinks" || sectionId === "faqs") {
      const cityVal = ((document.getElementById("cover-location") as HTMLInputElement | null)?.value
        ?? localStorage.getItem("cover_location") ?? "").trim();
      if (!/^[^,]+,\s*[A-Za-z][A-Za-z. ]*$/.test(cityVal)) {
        void showAlert("Please add a city in the City field in the format: City, State (Example: Denver, CO).", { title: "Invalid City Format" });
        return;
      }
    }

    // Tabs that generate schema cards (Schema, NLP Entities) need the Primary GBP Category — it
    // determines the business type and drives the industry-appropriate schema recommendations (and
    // is the fallback when no GBP is connected/matched).
    if (sectionId === "schema" || sectionId === "entities") {
      const catVal = ((document.getElementById("cover-gbp-category") as HTMLInputElement | null)?.value
        ?? localStorage.getItem("cover_gbp_category") ?? "").trim();
      if (!catVal) {
        void showAlert("Please add the Primary GBP Category on the cover page before crawling this tab — it determines the business type and drives the industry-appropriate schema recommendations.", { title: "Missing Required Field" });
        return;
      }
    }

    // GBP Optimization geocodes the Full Local Business Address to an exact
    // search origin — require a complete address (street, city, state, ZIP).
    if (sectionId === "gbp") {
      const addr = ((document.getElementById("cover-full-address") as HTMLInputElement | null)?.value
        ?? localStorage.getItem("cover_full_address") ?? "").trim();
      const hasStreet = /^\s*\d+\s+\S+/.test(addr);                                       // street number + name
      const hasCity   = (addr.match(/,/g)?.length ?? 0) >= 1;                             // comma separates city from state
      const hasState  = /,\s*[A-Za-z]{2}\b/.test(addr) || /\b[A-Za-z]{2}\s+\d{5}(-\d{4})?\b/.test(addr); // 2-letter state
      const hasZip    = /\b\d{5}(-\d{4})?\b/.test(addr);                                  // 5-digit ZIP
      if (!addr || !hasStreet || !hasCity || !hasState || !hasZip) {
        void showAlert("Please add a complete address to the Full Local Business Address field before continuing.\n\nIt must include a street address, city, state, and ZIP code (Example: 123 Main St, City, ST 12345).", { title: "Incomplete Address" });
        return;
      }
    }

    // Guard: competitors must have been retrieved first.
    // Primary: seo_competitor_domains. Fallback: sites[1..] from seo_full_metrics.
    let competitorDomains: string[] = [];
    try { competitorDomains = JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "null") ?? []; }
    catch { competitorDomains = []; }

    // Fallback 1: extract from stored full metrics sites array
    if (!competitorDomains.length) {
      try {
        const m = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null");
        if (Array.isArray(m?.sites) && m.sites.length > 1) {
          competitorDomains = (m.sites as Array<{ domain?: string }>)
            .slice(1).map((s: { domain?: string }) => s.domain ?? "").filter(Boolean);
        }
      } catch { /* ignore */ }
    }

    // Fallback 2: read directly from the cover page competitor strip
    if (!competitorDomains.length) {
      document.querySelectorAll<HTMLElement>(".cs-item:not(.cs-you) .cs-name").forEach(el => {
        const d = (el.textContent ?? "").trim().replace(/^www\./, "").replace(/\s.*$/, "");
        if (d && d.includes(".")) competitorDomains.push(d);
      });
    }

    if (competitorDomains.length) {
      localStorage.setItem("seo_competitor_domains", JSON.stringify(competitorDomains));
    } else {
      void showAlert("No competitor domains are stored for this site.\n\nIf you already clicked Get Competitors and it found none, the top results for your target keyword were all directories/aggregators or your own site. Try a more specific target keyword, or switch to Custom Competitors mode on the cover page to enter competitors manually — then run this crawl again.", { title: "No Competitor Data" });
      return;
    }

    // Regenerate the section via Claude for all tabs
    const metricsJson = localStorage.getItem("seo_full_metrics");
    if (!metricsJson) {
      void showAlert("Full metrics data not found.\n\nPlease click Get Competitors on the cover page first, then try again.", { title: "No Metrics Data" });
      return;
    }
    const anthropicKey = loadAnthropicKey();
    if (!anthropicKey) {
      setAuditToast({ type: "error", message: "Claude (Anthropic) API key required — add it in ⚙ Settings." });
      return;
    }

    let metrics: unknown;
    try { metrics = JSON.parse(metricsJson); }
    catch { setAuditToast({ type: "error", message: "Metrics data corrupted — re-run Get Competitors." }); return; }

    // seo_map_pack is stored separately from seo_full_metrics — merge it in so the
    // GBP crawl can fall back to the initial audit's map pack position if the Maps
    // endpoint returns a different geo-center and misses the target business.
    const mapPackJson = localStorage.getItem("seo_map_pack");
    if (mapPackJson && metrics && typeof metrics === "object") {
      try { (metrics as Record<string, unknown>).mapPack = JSON.parse(mapPackJson); } catch { /* ignore */ }
    }

    const targetDomain = localStorage.getItem("seo_target_domain") ?? "";

    // Guard: the stored audit data (pageMeta, competitors, sitemap, etc.) is tied
    // to the domain that was crawled when "Get Competitors" last ran. If the
    // Website URL field has since changed, that data is for a DIFFERENT site —
    // block the crawl so we never render stale, wrong-site data.
    const liveUrl = (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim()
      || localStorage.getItem("cover_website_url") || "";
    const domainOf = (u: string) => {
      try { return new URL(u.startsWith("http") ? u : `https://${u}`).hostname.replace(/^www\./, "").toLowerCase(); }
      catch { return u.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].toLowerCase(); }
    };
    const liveDomain    = domainOf(liveUrl);
    const crawledDomain = targetDomain.replace(/^www\./, "").toLowerCase();
    if (crawledDomain && liveDomain && liveDomain !== crawledDomain) {
      void showAlert(
        `The current audit data is for "${crawledDomain}", but the Website URL field now shows "${liveDomain}".\n\n` +
        `Click "Get Competitors" on the cover page to refresh the data for the new site, then run this crawl again.`,
        { title: "Website URL Changed" }
      );
      // Point the user straight at the fix: scroll to + pulse the Get Competitors button.
      const gcBtn = document.getElementById("get-competitors-btn");
      if (gcBtn) {
        gcBtn.scrollIntoView({ behavior: "smooth", block: "center" });
        const ring = "0 0 0 4px rgba(234,88,12,.55), 0 0 20px 4px rgba(234,88,12,.85)";
        gcBtn.style.transition = "box-shadow .25s";
        let on = false;
        const pulse = setInterval(() => { on = !on; gcBtn.style.boxShadow = on ? ring : "0 2px 8px rgba(234,88,12,.4)"; }, 400);
        setTimeout(() => { clearInterval(pulse); gcBtn.style.boxShadow = ""; }, 3600);
      }
      setAuditToast({ type: "error", message: `⚠ Website URL changed to "${liveDomain}" — click Get Competitors to refresh before crawling tabs.` });
      return;
    }

    const rawUrl       = localStorage.getItem("cover_website_url") ?? "";
    const targetUrl    = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
    const cityLocation = localStorage.getItem("cover_location") ?? "";
    // Read the Full Business Name + Full Local Business Address from the LIVE cover fields (which
    // are always mounted, even on this tab) so the GBP crawl always uses what's currently entered.
    // localStorage is only a fallback: switching sites overwrites cover_business_name with the saved
    // site's label, which can be an old/wrong name — the live field is the source of truth.
    const liveBusinessName = (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim();
    const liveFullAddress  = (document.getElementById("cover-full-address")  as HTMLInputElement | null)?.value?.trim();
    const businessNameNow  = (liveBusinessName ?? "") || (localStorage.getItem("cover_business_name") ?? "");
    const fullAddress      = (liveFullAddress  ?? "").trim() || (localStorage.getItem("cover_full_address") ?? "").trim();
    // The GBP snack pack crawl geocodes the location to a precise search origin,
    // so prefer the full street address when provided. Other tabs use the
    // city-level location (better for SERP locale / PAA).
    const location     = (sectionId === "gbp" && fullAddress) ? fullAddress : cityLocation;
    // Read the keyword fields live too (cover is always mounted), so the crawl — especially the GBP
    // map-pack search that uses keyword #1 — always uses the current target keywords, not stale storage.
    const keywords     = [1,2,3,4,5].map(i => {
      const live = (document.getElementById(`cover-kw-${i}`) as HTMLInputElement | null)?.value?.trim();
      return (live ?? "") || (localStorage.getItem(`cover_kw_${i}`) ?? "");
    });
    const positions: (number | null)[] = (() => { try { return JSON.parse(localStorage.getItem("seo_keyword_positions") ?? "null") ?? []; } catch { return []; } })();
    const volumes:   (number | null)[] = (() => { try { return JSON.parse(localStorage.getItem("seo_keyword_volumes")   ?? "null") ?? []; } catch { return []; } })();
    const auditDateStr = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const label = SECTIONS.find(s => s.id === sectionId)?.label ?? sectionId;

    setCrawlingSections(prev => new Set(prev).add(sectionId));
    setInterruptedSections(prev => { const n = new Set(prev); n.delete(sectionId); return n; }); // fresh run clears prior interruption
    setAuditToast(null); // clear any previous toast — placeholder shows loading state

    // Run this tab's crawl as a DURABLE JOB: it continues server-side if the browser
    // closes, checkpoints into the account, and is resumable. Keys come from the server.
    const jobSiteKey = getActiveSiteKey() || siteKey(targetDomain);
    setSiteJobActive(jobSiteKey, true);

    try {
      const { jobId } = await startCrawlJob({
        siteKey: jobSiteKey, kind: "tab", tabId: sectionId,
        params: { metrics, keywords, positions, volumes, targetDomain, targetUrl, location, cityLocation, auditDate: auditDateStr, businessName: businessNameNow, gbpCategory: (document.getElementById("cover-gbp-category") as HTMLInputElement | null)?.value?.trim() ?? "" },
      });

      let gotSection = false;
      const outcome = await new Promise<"done" | "interrupted" | "canceled">((resolve, reject) => {
        const close = subscribeCrawlJob(jobId, {
          onProgress: (msg) => setAuditToast({ type: "info", message: `⏳ ${msg}` }),
          onSection: (id, html) => {
            gotSection = true;
            setRegeneratedSections(prev => {
              const next = { ...prev, [id]: html };
              localStorage.setItem("full_audit_sections", JSON.stringify(next));
              return next;
            });
          },
          onDone: () => resolve("done"),
          onError: (msg) => reject(new Error(msg)),
          onInterrupted: () => resolve("interrupted"),
          onCanceled: () => resolve("canceled"),
        });
        crawlJobRef.current.set(sectionId, { jobId, close });
      });

      if (outcome === "canceled") {
        setAuditToast({ type: "info", message: `⏹ ${label} crawl stopped` });
        return;
      }
      if (outcome === "interrupted") {
        setInterruptedSections(prev => new Set(prev).add(sectionId));
        setAuditToast({ type: "error", message: `⚠ ${label} crawl interrupted — your progress is saved. Click Resume to finish.` });
        return;
      }

      // outcome === "done"
      if (!gotSection) throw new Error("Section generation returned no content — please try again.");
      if (!auditEverRun) {
        setAuditEverRun(true);
        localStorage.setItem("seo_audit_ever_run", "true");
      }
      // Stamp this tab's own crawl date (each tab tracks when IT was last run).
      const crawlDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      setAuditDate(crawlDate);
      localStorage.setItem("seo_last_tab_crawl", crawlDate);
      markTabsCrawled([sectionId], crawlDate);

      // For Competitors tab: fetch brand mentions after section regeneration completes
      if (sectionId === "competitors") handleCompetitorsCrawl();

      // Tab-specific live enrichment (same helpers the full audit uses).
      if (sectionId === "citations") {
        setAuditToast({ type: "success", message: `⏳ Fetching citation comparison data…` });
        await enrichCitationsSection();
      }
      if (sectionId === "llm") {
        await enrichLlmSection();
      }
      setAuditToast({ type: "success", message: `✓ ${label} tab updated with live data` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Regeneration failed";
      if (msg === "RATE_LIMITED") {
        void showAlert("The keyword-data service is currently rate-limiting requests.\n\nPlease try again in 10 minutes.", { title: "Rate Limited", tone: "danger" });
      } else {
        setAuditToast({ type: "error", message: `✗ ${msg}` });
      }
    } finally {
      setSiteJobActive(jobSiteKey, false);
      crawlJobRef.current.get(sectionId)?.close();
      crawlJobRef.current.delete(sectionId);
      setCrawlingSections(prev => { const n = new Set(prev); n.delete(sectionId); return n; });
    }
  }

  // Stop a running per-tab crawl: abort its request and reset the tab's state.
  const handleStopCrawl = useCallback((sectionId: string) => {
    // Intentional stop → cancel the durable job server-side (this is NOT "interrupted").
    const entry = crawlJobRef.current.get(sectionId);
    if (entry) { entry.close(); void cancelCrawlJob(entry.jobId).catch(() => {}); crawlJobRef.current.delete(sectionId); }
    setCrawlingSections(prev => { const n = new Set(prev); n.delete(sectionId); return n; });
    setInterruptedSections(prev => { const n = new Set(prev); n.delete(sectionId); return n; });
    if (sectionId === "competitors") setCompetitorsCrawling(false);
  }, []);

  // Resume an interrupted tab crawl — same as Run Crawl (the server skips finished work);
  // morphs the blue Resume button back to the red Stop Crawl button.
  const handleResumeCrawl = useCallback((sectionId: string) => {
    setInterruptedSections(prev => { const n = new Set(prev); n.delete(sectionId); return n; });
    void handleRunCrawl(sectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Style active tab as bookmark (bypasses audit.css specificity) ──
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    nav.querySelectorAll<HTMLButtonElement>("button.nb").forEach(btn => {
      const isOn = btn.classList.contains("on");
      if (isOn) {
        btn.style.background      = "#1e3a8a";
        btn.style.color           = "#ffffff";
        btn.style.borderRadius    = "12px 12px 0 0";
        btn.style.border          = "1px solid rgba(0,0,0,.2)";
        btn.style.borderBottom    = "5px solid #1e3a8a";
        btn.style.height          = "72px";
        btn.style.fontWeight      = "700";
        btn.style.boxShadow       = "-2px -4px 8px rgba(0,0,0,.12), 2px -4px 8px rgba(0,0,0,.08)";
        btn.style.position        = "relative";
        btn.style.zIndex          = "2";
        btn.style.marginBottom    = "-4px";
        btn.style.padding         = "0 16px";
      } else {
        btn.style.background   = "";
        btn.style.color        = "";
        btn.style.borderRadius = "";
        btn.style.border       = "";
        btn.style.borderBottom = "";
        btn.style.height       = "";
        btn.style.fontWeight   = "";
        btn.style.boxShadow    = "";
        btn.style.position     = "";
        btn.style.zIndex       = "";
        btn.style.marginBottom = "";
        btn.style.padding      = "";
      }
    });
  }, [activeSection]);

  // ── Custom tab nav scrollbar ──────────────────────────────────
  useEffect(() => {
    const nav = navRef.current;
    const track = scrollTrackRef.current;
    const thumb = scrollThumbRef.current;
    if (!nav || !track || !thumb) return;

    function updateThumb() {
      if (!nav || !track || !thumb) return;
      const ratio = nav.scrollWidth > nav.clientWidth
        ? nav.clientWidth / nav.scrollWidth : 1;
      const thumbW = Math.max(40, track.clientWidth * ratio);
      const maxScroll = nav.scrollWidth - nav.clientWidth;
      const scrolled = maxScroll > 0 ? nav.scrollLeft / maxScroll : 0;
      const maxLeft = track.clientWidth - thumbW;
      thumb.style.width = `${thumbW}px`;
      thumb.style.left = `${scrolled * maxLeft}px`;
      track.style.display = ratio >= 1 ? "none" : "block";
    }

    nav.addEventListener("scroll", updateThumb);
    window.addEventListener("resize", updateThumb);
    updateThumb();
    return () => {
      nav.removeEventListener("scroll", updateThumb);
      window.removeEventListener("resize", updateThumb);
    };
  }, []);

  // ── Reattach to durable crawl jobs on load ────────────────────
  // If a crawl was running when the browser was closed/refreshed, it kept running
  // server-side — resubscribe and show live progress (button stays red "Stop Crawl").
  // If a job was interrupted (e.g. server restart), surface saved progress: the full
  // audit prompts re-run; a per-tab crawl's button morphs to blue "Resume".
  useEffect(() => {
    let alive = true;
    const closers: Array<() => void> = [];
    (async () => {
      const key = getActiveSiteKey();
      if (!key) return;
      let jobs;
      try { jobs = await listActiveJobs(key); } catch { return; }
      if (!alive) return;

      // ── Full audit ──
      // Seed progress from the tabs that ACTUALLY have saved content (ground truth), not the
      // job's progress.doneIds, which can be stale/inflated and was causing 100% too early.
      const savedNow = savedAuditTabIds();
      const fa = jobs.find(j => j.kind === "full-audit");
      if (fa && (fa.status === "running" || fa.status === "queued")) {
        setAuditRunning(true);
        setSiteJobActive(key, true);
        auditJobIdRef.current = fa.id;
        try { localStorage.setItem(ACTIVE_AUDIT_JOB_KEY, fa.id); } catch { /* ignore */ }
        setAuditDoneTabs(new Set(savedNow));
        setAuditToast({ type: "info", message: "⏳ A full audit is still running — reattaching to live progress…" });
        const finish = () => { if (!alive) return; setAuditRunning(false); setAuditPaused(false); setSiteJobActive(key, false); auditJobIdRef.current = null; try { localStorage.removeItem(ACTIVE_AUDIT_JOB_KEY); } catch { /* ignore */ } };
        // If the job pauses while we're reattached (e.g. another tab closed) AND tabs are
        // still missing, morph to Resume; if everything's already saved, treat it as done.
        const toPaused = () => { finish(); if (!alive) return; if (savedAuditTabIds().length >= AUDIT_TAB_COUNT) return; auditResumeIdRef.current = fa.id; setAuditPaused(true); setAuditToast({ type: "info", message: "⏸ Audit paused — progress saved. Click Resume Audit to finish." }); };
        const close = subscribeCrawlJob(fa.id, {
          onProgress: (msg) => { if (alive) setAuditToast({ type: "info", message: `⏳ ${msg}` }); },
          onSection: (id, html) => {
            if (!alive) return;
            setRegeneratedSections(prev => { const next = { ...prev, [id]: html }; localStorage.setItem("full_audit_sections", JSON.stringify(next)); return next; });
            setAuditDoneTabs(prev => { const n = new Set(prev); n.add(id); return n; });
            setInterruptedSections(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n; }); // this tab was just refreshed
          },
          onDone: () => { finish(); if (alive) setAuditToast({ type: "success", message: "✓ Full audit complete." }); },
          onError: (msg) => { toPaused(); if (alive) setAuditToast({ type: "error", message: `✗ ${msg} — click Resume Audit to retry.` }); },
          onInterrupted: toPaused,
          onCanceled: finish,
        });
        auditJobCloseRef.current = close;
        closers.push(close);
      } else if (fa && (fa.status === "interrupted" || fa.status === "error")) {
        // Only offer Resume if tabs are actually missing — a leftover interrupted job after a
        // since-completed audit should NOT show "Resume Audit".
        setAuditDoneTabs(new Set(savedNow));
        if (savedNow.length < AUDIT_TAB_COUNT) {
          auditResumeIdRef.current = fa.id;
          setAuditPaused(true);
          setAuditToast({ type: "info", message: `⏸ Audit paused — ${savedNow.length} tab${savedNow.length === 1 ? "" : "s"} saved. Click Resume Audit to finish the remaining tabs.` });
        }
      }

      // ── Per-tab crawls ──
      // A full audit (or a later crawl) refreshes a tab, which supersedes any older
      // interrupted per-tab job — so we ignore interrupted tab jobs that predate the most
      // recent full audit, and only consider the latest job per tab. Without this, a stale
      // interrupted crawl would show "Resume" on a tab forever (even during a full audit).
      const fullAuditFreshTs = fa && (fa.status === "running" || fa.status === "queued" || fa.status === "done")
        ? Date.parse(fa.updatedAt) : 0;
      const seenTabs = new Set<string>();
      for (const tj of jobs) {
        const tabId = tj.tabId;
        if (tj.kind !== "tab" || !tabId) continue;
        if (seenTabs.has(tabId)) continue; // jobs are newest-first → keep only the latest per tab
        seenTabs.add(tabId);
        if (tj.status === "running" || tj.status === "queued") {
          setCrawlingSections(prev => new Set(prev).add(tabId));
          setSiteJobActive(key, true);
          const clear = () => {
            if (!alive) return;
            setSiteJobActive(key, false);
            setCrawlingSections(prev => { const n = new Set(prev); n.delete(tabId); return n; });
            crawlJobRef.current.delete(tabId);
          };
          const markInterrupted = () => { clear(); if (alive) setInterruptedSections(prev => new Set(prev).add(tabId)); };
          const close = subscribeCrawlJob(tj.id, {
            onSection: (id, html) => {
              if (!alive) return;
              setRegeneratedSections(prev => { const next = { ...prev, [id]: html }; localStorage.setItem("full_audit_sections", JSON.stringify(next)); return next; });
            },
            onDone: clear,
            onError: markInterrupted,
            onInterrupted: markInterrupted,
            onCanceled: clear,
          });
          crawlJobRef.current.set(tabId, { jobId: tj.id, close });
          closers.push(close);
        } else if (tj.status === "interrupted" || tj.status === "error") {
          // Only offer Resume if a newer full audit hasn't since refreshed this tab.
          if (Date.parse(tj.updatedAt) > fullAuditFreshTs) {
            setInterruptedSections(prev => new Set(prev).add(tabId));
          }
        }
      }
    })();
    return () => { alive = false; closers.forEach(c => c()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleTrackClick(e: React.MouseEvent<HTMLDivElement>) {
    const nav = navRef.current;
    const track = scrollTrackRef.current;
    const thumb = scrollThumbRef.current;
    if (!nav || !track || !thumb) return;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const thumbW = thumb.offsetWidth;
    const maxLeft = track.clientWidth - thumbW;
    const ratio = Math.max(0, Math.min(1, (clickX - thumbW / 2) / maxLeft));
    nav.scrollLeft = ratio * (nav.scrollWidth - nav.clientWidth);
  }

  function handleThumbMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    const nav = navRef.current;
    const track = scrollTrackRef.current;
    const thumb = scrollThumbRef.current;
    if (!nav || !track || !thumb) return;
    const startX = e.clientX;
    const startLeft = nav.scrollLeft;
    const maxScroll = nav.scrollWidth - nav.clientWidth;
    const thumbW = thumb.offsetWidth;
    const maxLeft = track.clientWidth - thumbW;

    function onMove(ev: MouseEvent) {
      const dx = ev.clientX - startX;
      const ratio = dx / maxLeft;
      nav!.scrollLeft = Math.max(0, Math.min(maxScroll, startLeft + ratio * maxScroll));
    }
    function onUp() {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      if (thumb) thumb.style.cursor = "grab";
    }
    thumb.style.cursor = "grabbing";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  function handleConnect(info: ConnectionInfo, credsOverride?: Credentials) {
    // credsOverride lets the auto-connect flow pass the creds it just loaded, since the `creds` state
    // closure here may still be the pre-load value (React state updates are async).
    const useCreds = credsOverride ?? creds;
    setConnInfo(info);
    connInfoRef.current = info; // keep the live mirror in sync for executeFixById
    if (info.ok) {
      // connect() resolved the entered URL to its canonical origin (www/non-www, http/https).
      // Use that for the live client, and persist it so the field, reconnects, and Site Storage
      // all use the working URL.
      const resolvedUrl = info.siteUrl || useCreds.siteUrl;
      if (info.siteUrl && info.siteUrl !== useCreds.siteUrl) {
        const next = { ...useCreds, siteUrl: info.siteUrl };
        setCreds(next);
        saveCreds(next);
      }
      setConnected(true);
      // A successful connection clears any prior explicit-disconnect flag so refresh restores it.
      try { sessionStorage.removeItem(WP_DISCONNECTED_KEY); } catch { /* ignore */ }
      clientRef.current = buildClient(resolvedUrl, useCreds.username, useCreds.appPassword);
      // Seed schema-helper readiness for THIS site from a prior verification so the
      // setup dialog only ever appears once per site.
      let host = resolvedUrl;
      try { host = new URL(/^https?:\/\//.test(resolvedUrl) ? resolvedUrl : `https://${resolvedUrl}`).host; } catch { /* keep raw */ }
      schemaHelperKeyRef.current = `seo_schema_helper_${host}`;
      schemaReadyRef.current = localStorage.getItem(schemaHelperKeyRef.current) === "1";
    } else {
      console.warn("[conn-diag] handleConnect → NOT connected", { error: info.error });
      setConnected(false);
      clientRef.current = null;
      schemaReadyRef.current = false;
      schemaHelperKeyRef.current = "";
    }
  }

  function handleCredsChange(c: Credentials) {
    // No-op guard: some flows re-emit the CURRENT credentials (e.g. the Site Storage dropdown
    // re-loading the already-active saved site when a tab with its own connect band mounts —
    // Service Area, Conversational, Redirection). Tearing the live connection down for an
    // identical-cred "change" is what made those tabs appear to spontaneously disconnect.
    // Changing credentials to the same values must never drop the connection.
    if (c.siteUrl === creds.siteUrl && c.username === creds.username && c.appPassword === creds.appPassword) {
      console.warn("[conn-diag] handleCredsChange IGNORED (identical creds — kept connection alive)", new Error().stack);
      return;
    }
    console.warn("[conn-diag] handleCredsChange → disconnect (creds changed)", { from: creds.siteUrl, to: c.siteUrl });
    setCreds(c);
    saveCreds(c); // persist so a refresh doesn't wipe the app password
    setConnected(false);
    setConnInfo(null);
    connInfoRef.current = null;
    clientRef.current = null;
  }

  // Explicit user disconnect (Disconnect button). Tears down the live connection and remembers the
  // choice for this session so a refresh stays disconnected (the credential fields are kept so
  // reconnecting is one click). Cleared automatically on the next successful connect.
  function handleDisconnect() {
    console.warn("[conn-diag] handleDisconnect (explicit) called", new Error().stack);
    try { sessionStorage.setItem(WP_DISCONNECTED_KEY, "1"); } catch { /* ignore */ }
    setConnected(false);
    setConnInfo(null);
    connInfoRef.current = null;
    clientRef.current = null;
    schemaReadyRef.current = false;
    schemaHelperKeyRef.current = "";
  }

  const counts = getCounts(state);
  const activeSec = SECTIONS.find(s => s.id === activeSection)!;
  // The active tab has exportable crawled data (per-tab Run Crawl or full audit). The Schema
  // tab also counts if Bulk Page Schema cards exist.
  const activeTabHasStoredData = (regeneratedSections[activeSec.id]?.length ?? 0) >= 100
    || (activeSec.id === "schema" && (() => { try { return !!localStorage.getItem("lsg_bps_cards"); } catch { return false; } })());
  // True when the active tab is showing the "Live Crawl Running" loading placeholder — used
  // to render the crawl status message inline beneath that placeholder (instead of as a
  // viewport-fixed toast that would overlap the placeholder and sit off-center).
  const activeTabCrawling = crawlingSections.has(activeSec.id)
    || (competitorsCrawling && activeSec.id === "competitors")
    || (auditRunning && !auditDoneTabs.has(activeSec.id));
  // Gates the per-tab export (💾) button: there must be finished data AND no crawl in
  // progress on this tab (nothing to export mid-crawl — the tab shows the placeholder).
  const activeTabHasData = activeTabHasStoredData && !activeTabCrawling;

  // Single, uniform renderer for every crawl/generation status message so they all share one
  // design & font (previously completion messages used a big Oswald style while errors/progress
  // used Mulish, which looked inconsistent). In-progress messages (prefixed "⏳") show a spinning
  // circle instead of the hourglass. `fixed`=true pins it bottom-center of the viewport (used when
  // not actively crawling); `fixed`=false renders it inline beneath the loading placeholder.
  const renderAuditStatus = (fixed: boolean) => {
    if (!auditToast) return null;
    const lt = theme === "light";
    const isError = auditToast.type === "error";
    const bg = isError ? (lt ? "#fee2e2" : "#7f1d1d") : (lt ? "#16a34a" : "#166534");
    const border = isError ? "#dc2626" : (lt ? "#15803d" : "#22c55e");
    const text = isError ? (lt ? "#991b1b" : "#f1f5f9") : (lt ? "#ffffff" : "#dcfce7");
    const spinning = /^⏳\s*/.test(auditToast.message);
    const msg = spinning ? auditToast.message.replace(/^⏳\s*/, "") : auditToast.message;
    const boxStyle: CSSProperties = {
      background: bg, border: `1px solid ${border}`, color: text,
      borderRadius: 0, padding: "10px 18px", fontSize: 13,
      fontFamily: "Mulish, sans-serif", fontWeight: 600,
      maxWidth: 520, textAlign: "center", display: "flex", alignItems: "center", gap: 10,
      boxShadow: "0 8px 24px rgba(15,23,42,.15)",
      // Center within the content area (right of the 252px sidebar), not the whole viewport:
      // content-center x = 252 + (100vw − 252)/2 = 50vw + 126px → calc(50% + 126px).
      ...(fixed ? { position: "fixed", bottom: 80, left: "calc(50% + 126px)", transform: "translateX(-50%)", zIndex: 9999 } : {}),
    };
    return (
      <div style={boxStyle}>
        {spinning && (
          <svg style={{ animation: "seo-spin 1.1s linear infinite", width: 16, height: 16, flexShrink: 0 }} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="28" cy="28" r="22" stroke={isError ? "rgba(220,38,38,.25)" : "rgba(255,255,255,.35)"} strokeWidth="6" />
            <path d="M28 6 A22 22 0 0 1 50 28" stroke={text} strokeWidth="6" strokeLinecap="round" />
          </svg>
        )}
        <span style={{ flex: 1 }}>{msg}</span>
        <button onClick={() => setAuditToast(null)} style={{ background: "none", border: "none", color: text, opacity: 0.6, cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
      </div>
    );
  };
  // Sitemap tab export gating: rows present and not mid-crawl.
  const sitemapHasData = sitemapRows.length > 0 && !sitemapCrawling;
  // Rows shown in the table after applying the keyword filter + exclude list (case-insensitive)
  // against the selected column — Page Title, URL, or Status Code. Exclude is comma-delimited;
  // a row is dropped if the selected column contains ANY exclude term.
  // Memoized so the (memoized) SitemapTab only re-renders when the filter/rows actually change —
  // not on every unrelated App state update (which would re-render the whole table and hang the tab).
  const sitemapVisibleRows = useMemo(() => {
    const fieldVal = (r: SitemapRow) =>
      (sitemapSearchField === "url" ? r.url
        : sitemapSearchField === "metaTitle" ? r.metaTitle
        : sitemapSearchField === "tag" ? r.tags.join(", ")
        : sitemapSearchField === "status" ? (r.statusCode == null ? "ERR" : String(r.statusCode))
        : r.title).toLowerCase();
    const q = sitemapSearch.trim().toLowerCase();
    const excludeTerms = sitemapExclude.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    let out = sitemapRows;
    if (q) out = out.filter(r => fieldVal(r).includes(q));
    if (excludeTerms.length) out = out.filter(r => { const v = fieldVal(r); return !excludeTerms.some(t => v.includes(t)); });
    return out;
  }, [sitemapRows, sitemapSearch, sitemapSearchField, sitemapExclude]);
  // Map each enabled redirect's SOURCE path → its destination URL, for the sitemap "Redirected"
  // column (and to flag redirected URLs in red). Path-normalized (lowercase, no trailing slash).
  const sitemapRedirectByPath = useMemo(() => {
    const key = (u: string) => { try { return (new URL(/^https?:\/\//i.test(u) ? u : `https://x${u.startsWith("/") ? "" : "/"}${u}`).pathname.replace(/\/+$/, "") || "/").toLowerCase(); } catch { return (u.replace(/[#?].*$/, "").replace(/\/+$/, "") || "/").toLowerCase(); } };
    const m = new Map<string, string>();
    for (const r of sitemapRedirects) { if (r.enabled === false) continue; m.set(key(r.source), r.destination); }
    return m;
  }, [sitemapRedirects]);

  return (
    <>
      <DialogHost />

      {/* Left sidebar navigation — replaces the old horizontal scrolling tab bar */}
      <aside className="sidenav">
        <div className="sidenav-brand">
          <img src="/lsg-logo.png" alt="Local SEO Garage" style={{ width: "90%", height: "auto", display: "block" }} />
        </div>
        <nav className="sidenav-list">
          <div className="snav-heading">
            Toolbox
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="14" x2="21" y2="14" />
              <line x1="10" y1="6.5" x2="14" y2="6.5" />
              <line x1="10" y1="11.5" x2="14" y2="11.5" />
              <line x1="10" y1="16.5" x2="14" y2="16.5" />
            </svg>
          </div>
          {SECTIONS.filter(s => s.id !== "history").map(s => (
            <Fragment key={s.id}>
              {/* "POWER TOOLS" section label above the tool-tab group — mirrors the Toolbox
                  heading (same .snav-heading style → same 8px spacing below). The top margin
                  separates the group from the audit sections above. */}
              {s.id === "contentdrawer" && (
                <div className="snav-heading" style={{ marginTop: 14 }}>
                  Power Tools
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                </div>
              )}
              <button
                className={`snav-item${isPowerTool(s.id) ? " snav-tool" : ""}${activeSection === s.id ? " on" : ""}`}
                onClick={() => setActiveSection(s.id)}
              >
                {isPowerTool(s.id)
                  ? <><ToolIcon id={s.id} /><span className="snav-tool-lbl">{s.label}</span></>
                  : s.label}
              </button>
            </Fragment>
          ))}
        </nav>
        <div className="sidenav-foot">
          {updateAvailable && (
            <button
              className="sidenav-update-banner"
              onClick={() => window.location.reload()}
              title="A new version is available — click to refresh"
            >
              Update available — Please Refresh
            </button>
          )}
          <button className={`sidenav-settings-btn${activeSection === "history" ? " on" : ""}`} onClick={() => setActiveSection("history")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
              <path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><path d="M12 7v5l3 3" />
            </svg> History
          </button>
          <button className="sidenav-settings-btn" onClick={() => setShowSidebarSettings(true)}>
            <span style={{ fontSize: 14 }}>⚙</span> Settings
          </button>
        </div>
      </aside>

      <div className="app-content">
      {/* Cover with credentials panel embedded top-right. Hidden (but kept mounted, so its
          imperatively-wired buttons/effects stay intact) on the LLM Mention Tracking tab, which
          is DataForSEO-only and unrelated to the audit cover / WordPress connection. */}
      <div style={{ position: "relative", display: (activeSec.id === "llmtracking" || activeSec.id === "organicrank" || activeSec.id === "llmvisibility" || activeSec.id === "gbpreviews" || activeSec.id === "servicearea" || activeSec.id === "citationcheck" || activeSec.id === "conversational" || activeSec.id === "redirectionmanager" || activeSec.id === "history" || activeSec.id === "nicheadsscraper" || activeSec.id === "topicalcoverage") ? "none" : undefined }}>
        <div ref={coverRef} dangerouslySetInnerHTML={{ __html: HTML_COVER }} />
        <div style={{ position: "absolute", top: 18, right: 52, zIndex: 10, transform: "translateX(-10%)" }}>
          <CredentialsBar
            creds={creds}
            onChange={handleCredsChange}
            info={connInfo}
            connected={connected}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onRunAudit={handleRunAudit}
            onStopAudit={handleStopAudit}
            onResumeAudit={handleResumeAudit}
            auditRunning={auditRunning}
            auditPaused={auditPaused}
            auditProgress={auditRunning ? Math.min(100, Math.round((AUDIT_TAB_IDS.filter(id => auditDoneTabs.has(id)).length / Math.max(1, AUDIT_TAB_COUNT)) * 100)) : 0}
            onSwitchSite={onSwitchSite} connecting={autoConnecting}
            sitesRefreshToken={sitesRefreshToken}
            onSaveReport={handleSaveReport}
            canSaveReport={Object.values(regeneratedSections).some(h => (h?.length ?? 0) >= 100)}
            canRunAudit={competitorsReady}
            coverMode
          />
        </div>
      </div>

      {/* Audit pre-flight dialog */}
      {auditConfirm && (() => {
        // Theme-aware palette: dark branch reproduces the original colors; light branch
        // gives the dialog a proper light-mode look. Titles use the cover business-name
        // font (Oswald, 500, uppercase, tracked) in BOTH modes.
        const lt = theme === "light";
        const dlg = {
          panelBg:      lt ? "#ffffff" : "#0f172a",
          panelBorder:  lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)",
          panelShadow:  lt ? "0 16px 48px rgba(15,23,42,.18)" : "none",
          body:         lt ? "#475569" : "rgba(255,255,255,.8)",
          bodyMid:      lt ? "#64748b" : "rgba(255,255,255,.6)",
          bodyDim:      lt ? "#94a3b8" : "rgba(255,255,255,.3)",
          sep:          lt ? "#e2e8f0" : "rgba(255,255,255,.06)",
          cancelBorder: lt ? "1px solid #cbd5e1" : "1px solid rgba(255,255,255,.15)",
          cancelText:   lt ? "#64748b" : "rgba(255,255,255,.5)",
          okBg:         lt ? "rgba(16,185,129,.10)" : "rgba(16,185,129,.08)",
          okBorder:     lt ? "rgba(5,150,105,.35)" : "rgba(16,185,129,.25)",
          okLabel:      lt ? "#047857" : "#6ee7b7",
          okIcon:       lt ? "#059669" : "#34d399",
          errBg:        lt ? "rgba(239,68,68,.08)" : "rgba(239,68,68,.1)",
          errBorder:    lt ? "rgba(220,38,38,.3)" : "rgba(239,68,68,.3)",
          errLabel:     lt ? "#b91c1c" : "#fca5a5",
          errIcon:      lt ? "#dc2626" : "#f87171",
          sub:          lt ? "#64748b" : "rgba(255,255,255,.4)",
          titleReady:   lt ? "#0f172a" : "#f1f5f9",
          titleWarn:    lt ? "#b45309" : "#fbbf24",
          titleErr:     lt ? "#dc2626" : "#f87171",
        };
        const titleFont: React.CSSProperties = { fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" };
        return (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.65)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000,
        }} onClick={() => auditConfirm.type !== "testing" && setAuditConfirm(null)}>
          <div style={{
            background: dlg.panelBg, border: dlg.panelBorder, borderRadius: 10, boxShadow: dlg.panelShadow,
            padding: "28px 28px 22px", maxWidth: 460, width: "90%", fontFamily: "Mulish, sans-serif",
          }} onClick={e => e.stopPropagation()}>

            {/* Missing keys */}
            {auditConfirm.type === "missing_keys" && (<>
              <div style={{ ...titleFont, fontSize: 15, color: dlg.titleErr, marginBottom: 10 }}>API Keys Required</div>
              <p style={{ fontSize: 12, color: dlg.bodyMid, lineHeight: 1.65, margin: "0 0 14px" }}>
                All three API credentials are required to run a full audit. The following are missing:
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                {auditConfirm.missing.map(name => (
                  <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
                    background: dlg.errBg, border: `1px solid ${dlg.errBorder}`, borderRadius: 5 }}>
                    <span style={{ color: dlg.errIcon, fontSize: 13 }}>✕</span>
                    <span style={{ fontSize: 12, color: dlg.errLabel, fontWeight: 600 }}>{name}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => setAuditConfirm(null)} style={{
                  padding: "8px 18px", borderRadius: 0, border: dlg.cancelBorder,
                  background: "transparent", color: dlg.cancelText, cursor: "pointer",
                  fontFamily: "Mulish, sans-serif", fontSize: 12, fontWeight: 600,
                }}>Cancel</button>
                <button onClick={() => { setAuditConfirm(null); setShowSidebarSettings(true); }} style={{
                  padding: "8px 20px", borderRadius: 0, border: "none", background: "#1d4ed8",
                  color: "#fff", cursor: "pointer", fontFamily: "Mulish, sans-serif", fontSize: 12, fontWeight: 700,
                }}>⚙ Open Settings</button>
              </div>
            </>)}

            {/* Testing */}
            {auditConfirm.type === "testing" && (<>
              <div style={{ ...titleFont, fontSize: 15, color: dlg.titleReady, marginBottom: 10 }}>Verifying API Connections…</div>
              {["Ahrefs API", "Claude (Anthropic) API", "DataForSEO API"].map(name => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
                  borderBottom: `1px solid ${dlg.sep}` }}>
                  <span style={{ fontSize: 13, animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</span>
                  <span style={{ fontSize: 12, color: dlg.bodyMid }}>{name}</span>
                </div>
              ))}
              <p style={{ fontSize: 11, color: dlg.bodyDim, margin: "14px 0 0", lineHeight: 1.5 }}>
                Testing all three connections before starting the crawl…
              </p>
            </>)}

            {/* Preflight results */}
            {auditConfirm.type === "preflight" && (() => {
              const rows = [
                { label: "Ahrefs API", result: auditConfirm.ahrefs },
                { label: "Claude (Anthropic) API", result: auditConfirm.claude },
                { label: "DataForSEO API", result: auditConfirm.dataforseo },
              ];
              return (<>
                <div style={{ ...titleFont, fontSize: 15, color: auditConfirm.allPassed ? dlg.titleReady : dlg.titleWarn, marginBottom: 14 }}>
                  {auditConfirm.allPassed ? "All Systems Ready" : "Connection Issues Detected"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                  {rows.map(({ label, result }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                      background: result.ok ? dlg.okBg : dlg.errBg,
                      border: `1px solid ${result.ok ? dlg.okBorder : dlg.errBorder}`,
                      borderRadius: 5 }}>
                      <span style={{ fontSize: 14, color: result.ok ? dlg.okIcon : dlg.errIcon, flexShrink: 0 }}>
                        {result.ok ? "✓" : "✕"}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: result.ok ? dlg.okLabel : dlg.errLabel }}>{label}</div>
                        <div style={{ fontSize: 10, color: dlg.sub, marginTop: 1 }}>{result.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setAuditConfirm(null)} style={{
                    padding: "8px 18px", borderRadius: 0, border: dlg.cancelBorder,
                    background: "transparent", color: dlg.cancelText, cursor: "pointer",
                    fontFamily: "Mulish, sans-serif", fontSize: 12, fontWeight: 600,
                  }}>Cancel</button>
                  {!auditConfirm.allPassed && (
                    <button onClick={() => { setAuditConfirm(null); setShowSidebarSettings(true); }} style={{
                      padding: "8px 20px", borderRadius: 0, border: "none", background: "#1d4ed8",
                      color: "#fff", cursor: "pointer", fontFamily: "Mulish, sans-serif", fontSize: 12, fontWeight: 700,
                    }}>⚙ Fix in Settings</button>
                  )}
                  {auditConfirm.allPassed && (
                    <button onClick={() => setAuditConfirm({ type: "duration" })} style={{
                      padding: "8px 22px", borderRadius: 0, border: "none", background: "#16a34a",
                      color: "#fff", cursor: "pointer", fontFamily: "Mulish, sans-serif", fontSize: 12, fontWeight: 700,
                    }}>▶ Start Audit</button>
                  )}
                </div>
              </>);
            })()}

            {auditConfirm.type === "duration" && (<>
              <div style={{ ...titleFont, fontSize: 15, color: dlg.titleReady, marginBottom: 12 }}>
                ⏱ Full Audit — Heads Up
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.55, color: dlg.body, fontFamily: "Mulish, sans-serif", margin: "0 0 18px" }}>
                A full audit can take between <strong>15–25 minutes</strong> to complete all tabs. If you are interested
                in only specific data, consider running a crawl at the tab level instead. If you would still like to run
                the full audit, press Continue.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => setAuditConfirm(null)} style={{
                  padding: "8px 18px", borderRadius: 0, border: dlg.cancelBorder,
                  background: "transparent", color: dlg.cancelText, cursor: "pointer",
                  fontFamily: "Mulish, sans-serif", fontSize: 12, fontWeight: 600,
                }}>Cancel</button>
                <button onClick={executeAudit} style={{
                  padding: "8px 22px", borderRadius: 0, border: "none", background: "#16a34a",
                  color: "#fff", cursor: "pointer", fontFamily: "Mulish, sans-serif", fontSize: 12, fontWeight: 700,
                }}>Continue →</button>
              </div>
            </>)}

          </div>
        </div>
        );
      })()}

      {/* Audit run / crawl status box — single uniform design for all message types
          (see renderAuditStatus). Bottom-center of the viewport when not actively crawling. */}
      {auditToast && !activeTabCrawling && renderAuditStatus(true)}

      {/* Tab navigation moved to the left sidebar (see <aside className="sidenav">) */}

      <button
        className="theme-toggle"
        role="switch"
        aria-checked={theme === "light"}
        onClick={() => {
          // Apply the attribute SYNCHRONOUSLY before the re-render, so components that derive their
          // colors by reading document.documentElement[data-theme] at render time pick up the new
          // theme immediately (the useEffect that also sets it runs after render — too late for them).
          const next = theme === "dark" ? "light" : "dark";
          document.documentElement.setAttribute("data-theme", next);
          setTheme(next);
        }}
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      >
        <span className="theme-toggle-knob">
          {theme === "dark" ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4.6" fill="currentColor" stroke="none" /><path d="M12 1.8v2.4M12 19.8v2.4M3.5 3.5l1.7 1.7M18.8 18.8l1.7 1.7M1.8 12h2.4M19.8 12h2.4M3.5 20.5l1.7-1.7M18.8 5.2l1.7-1.7" /></svg>
          )}
        </span>
      </button>
      <button className="settings-gear-btn" onClick={() => setShowSidebarSettings(true)} title="Settings">⚙</button>
      {showSidebarSettings && <SidebarSettings onClose={() => { setShowSidebarSettings(false); setSettingsInitialCat(undefined); }} user={authUser} onLogout={onLogout} onSitesChanged={onSitesChanged} onUserPatch={onUserPatch} initialCategory={settingsInitialCat} getClient={() => clientRef.current} />}

      {/* Schema tab: one-time helper setup, gating the first programmatic schema write */}
      <SchemaSetupDialog
        open={!!schemaSetup}
        zipUrl="/local-seo-garage.zip"
        onVerify={() => clientRef.current?.isSchemaHelperInstalled() ?? Promise.resolve(false)}
        onVerified={() => {
          schemaReadyRef.current = true;
          if (schemaHelperKeyRef.current) { try { localStorage.setItem(schemaHelperKeyRef.current, "1"); } catch { /* ignore */ } }
          const pending = schemaSetup;
          setSchemaSetup(null);
          // fixId "" = opened purely for info (e.g. Internal Links tooltip CTA) — just
          // mark the helper ready, don't trigger a stray approve.
          if (pending && pending.fixId) void handleApprove(pending.fixId, pending.message, pending.isCreate);
        }}
        onClose={() => setSchemaSetup(null)}
      />

      {/* Run Crawl confirmation dialog */}
      {crawlConfirm && (() => {
        const lt = theme === "light";
        const p = {
          panelBg:      lt ? "#ffffff" : "#0f172a",
          panelBorder:  lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)",
          panelShadow:  lt ? "0 16px 48px rgba(15,23,42,.18)" : "none",
          title:        lt ? "#0f172a" : "#f1f5f9",
          body:         lt ? "#475569" : "rgba(255,255,255,.55)",
          strong:       lt ? "#0f172a" : "#f1f5f9",
          cancelBorder: lt ? "1px solid #cbd5e1" : "1px solid rgba(255,255,255,.15)",
          cancelColor:  lt ? "#64748b" : "rgba(255,255,255,.5)",
        };
        return (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10000 }}
          onClick={() => setCrawlConfirm(null)}>
          <div style={{ background:p.panelBg, border:p.panelBorder, borderRadius:12, boxShadow:p.panelShadow, padding:"28px 28px 22px", maxWidth:420, width:"90%", fontFamily:"'Inter','Mulish',sans-serif" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:16, color:p.title, marginBottom:10, fontFamily:"'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight:500, textTransform:"uppercase", letterSpacing:"0.7px" }}>Run Crawl — {crawlConfirm.label}</div>
            <p style={{ fontSize:12, color:p.body, lineHeight:1.65, margin:"0 0 20px" }}>
              This will regenerate the <strong style={{ color:p.strong }}>{crawlConfirm.label}</strong> tab with live data using your current competitors. Any previously generated content for this tab will be replaced.
            </p>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setCrawlConfirm(null)} style={{ padding:"8px 18px", borderRadius:0, border:p.cancelBorder, background:"transparent", color:p.cancelColor, cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600 }}>Cancel</button>
              <button onClick={() => { const s = crawlConfirm; setCrawlConfirm(null); handleRunCrawl(s.sectionId); }}
                className="run-crawl-btn"
                style={{ padding:"8px 22px", background:"linear-gradient(135deg,#ea580c,#f97316)", color:"#fff", border:"none", borderRadius:0, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 2px 8px rgba(234,88,12,.4)" }}>
                Continue →
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Fix execution toast */}
      {toast && (
        <div className={`fix-toast${!toast.ok ? " error" : toast.manual ? " manual" : ""}${toast.ok && toast.createdUrl ? " created" : ""}`}>
          <button className="fix-toast-close" onClick={() => setToast(null)}>✕</button>
          <div className="fix-toast-title">
            {/* The state icon (✓/✕/⚠, colored by state) is a CSS ::before on .fix-toast-title. */}
            {toast.ok ? (toast.manual ? "Manual Action Required" : (toast.createdUrl ? "Successfully Published" : "Update Successful")) : "Fix Failed"}
          </div>
          {toast.ok && toast.createdUrl ? (
            <div className="fix-toast-msg">
              <div><span className="fix-toast-created-title" style={{ fontWeight: 700 }}>{toast.createdTitle}</span> is now live at:</div>
              <a className="fix-toast-url" href={toast.createdUrl} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: 4, color: "#2563eb", textDecoration: "underline", wordBreak: "break-all" }}>
                {toast.createdUrl}
              </a>
              {toast.createdSchemaTypes?.length ? (
                <div style={{ marginTop: 6, opacity: .85 }}>
                  <div>Schema added:</div>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18, listStyle: "disc" }}>
                    {toast.createdSchemaTypes.map(t => <li key={t} style={{ marginTop: 1 }}>{t}</li>)}
                  </ul>
                </div>
              ) : toast.createdSchema ? (
                <div style={{ marginTop: 6, opacity: .85 }}>{toast.createdSchema}</div>
              ) : null}
            </div>
          ) : (
            <div className="fix-toast-msg">{toast.message}</div>
          )}
          <div className="fix-toast-actions">
            <button className="fix-toast-ok" onClick={() => setToast(null)}>OK</button>
          </div>
        </div>
      )}

      {/* Schema merge dialog — a business-entity enhancement couldn't be auto-applied without a
          duplicate, so we show the pre-merged block (existing + enhancements, de-duplicated) to
          paste over the original. */}
      {schemaMerge && (() => {
        const lt = theme === "light";
        const p = {
          overlay: "rgba(2,6,23,.62)",
          panel: lt ? "#ffffff" : "#0f172a",
          border: lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)",
          title: lt ? "#0f172a" : "#f1f5f9",
          body: lt ? "#475569" : "rgba(255,255,255,.72)",
          codeBg: lt ? "#f8fafc" : "#0b1220",
          codeText: lt ? "#0f172a" : "#e2e8f0",
          codeBorder: lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.1)",
          link: lt ? "#1d4ed8" : "#60a5fa",
        };
        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 12000, background: p.overlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
            onClick={() => setSchemaMerge(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: p.panel, border: p.border, borderRadius: 14, boxShadow: "0 24px 60px rgba(2,6,23,.5)", width: "min(720px, 100%)", maxHeight: "86vh", display: "flex", flexDirection: "column", padding: "20px 22px", fontFamily: "'Inter','Mulish',sans-serif" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: p.title }}>Replace your schema block</div>
                <button onClick={() => setSchemaMerge(null)} style={{ background: "none", border: "none", color: p.body, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.55, color: p.body, marginTop: 8 }}>
                {schemaMerge.message}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: p.body, marginTop: 10 }}>
                Instead of approving duplicate schema/entities, it&rsquo;s better to <b style={{ color: p.title }}>replace your original business-schema block with the updated block below</b>. Copy the <b style={{ color: p.title }}>entire</b> block — don&rsquo;t leave out or duplicate any brackets <span style={{ fontFamily: "'Roboto Mono',monospace" }}>{"{ }"}</span> — then <a href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer" style={{ color: p.link, textDecoration: "underline" }}>validate the schema</a> after pasting.
              </div>
              <pre style={{ marginTop: 12, background: p.codeBg, color: p.codeText, border: p.codeBorder, borderRadius: 10, padding: "12px 14px", fontSize: 12, fontFamily: "'Roboto Mono',monospace", lineHeight: 1.5, overflow: "auto", flex: "1 1 auto", whiteSpace: "pre", tabSize: 2 }}>{schemaMerge.json}</pre>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
                <button
                  onClick={() => { void navigator.clipboard.writeText(schemaMerge.json).then(() => { setSchemaMergeCopied(true); }); }}
                  style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: schemaMergeCopied ? "#16a34a" : "#1d4ed8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {schemaMergeCopied ? "✓ Copied" : "Copy block"}
                </button>
                <button onClick={() => setSchemaMerge(null)} style={{ padding: "9px 18px", borderRadius: 8, border: p.border, background: "transparent", color: p.body, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* "View JSON-LD schema" popup — the validated schema a card would add, for copy / manual replace. */}
      {schemaView && (() => {
        const lt = theme === "light";
        const p = {
          overlay: "rgba(2,6,23,.62)",
          panel: lt ? "#ffffff" : "#0f172a",
          border: lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)",
          title: lt ? "#0f172a" : "#f1f5f9",
          body: lt ? "#475569" : "rgba(255,255,255,.72)",
          codeBg: lt ? "#f8fafc" : "#0b1220",
          codeText: lt ? "#0f172a" : "#e2e8f0",
          codeBorder: lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.1)",
          link: lt ? "#1d4ed8" : "#60a5fa",
        };
        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 12000, background: p.overlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
            onClick={() => setSchemaView(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: p.panel, border: p.border, borderRadius: 14, boxShadow: "0 24px 60px rgba(2,6,23,.5)", width: "min(760px, 100%)", maxHeight: "86vh", display: "flex", flexDirection: "column", padding: "20px 22px", fontFamily: "'Inter','Mulish',sans-serif" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: p.title }}>JSON-LD schema</div>
                <button onClick={() => setSchemaView(null)} style={{ background: "none", border: "none", color: p.body, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: p.body, marginTop: 8 }}>
                The exact, schema.org-validated JSON-LD this card would add to{" "}
                {schemaView.url ? <a href={schemaView.url} target="_blank" rel="noopener noreferrer" style={{ color: p.link, textDecoration: "underline", wordBreak: "break-all" }}>{schemaView.url}</a> : "the page"}{" "}
                on Approve. Copy it to add or replace the schema on that page manually.
              </div>
              <pre style={{ marginTop: 12, background: p.codeBg, color: p.codeText, border: p.codeBorder, borderRadius: 10, padding: "12px 14px", fontSize: 12, fontFamily: "'Roboto Mono',monospace", lineHeight: 1.5, overflow: "auto", flex: "1 1 auto", whiteSpace: "pre", tabSize: 2 }}>{schemaView.json}</pre>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
                <button
                  onClick={() => { void navigator.clipboard.writeText(schemaView.json).then(() => setSchemaViewCopied(true)); }}
                  style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: schemaViewCopied ? "#16a34a" : "#1d4ed8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {schemaViewCopied ? "✓ Copied" : "Copy schema"}
                </button>
                <button onClick={() => setSchemaView(null)} style={{ padding: "9px 18px", borderRadius: 8, border: p.border, background: "transparent", color: p.body, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {schemaIssues && (() => {
        const lt = theme === "light";
        const p = {
          overlay: "rgba(2,6,23,.62)",
          panel: lt ? "#ffffff" : "#0f172a",
          border: lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)",
          title: lt ? "#0f172a" : "#f1f5f9",
          body: lt ? "#475569" : "rgba(255,255,255,.72)",
          rowBg: lt ? "#fffbeb" : "rgba(217,119,6,.12)",
          rowBorder: lt ? "1px solid #fde68a" : "1px solid rgba(217,119,6,.35)",
          rowText: lt ? "#92400e" : "#fcd34d",
          link: lt ? "#1d4ed8" : "#60a5fa",
        };
        const n = schemaIssues.issues.length;
        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 12000, background: p.overlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
            onClick={() => setSchemaIssues(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: p.panel, border: p.border, borderRadius: 14, boxShadow: "0 24px 60px rgba(2,6,23,.5)", width: "min(680px, 100%)", maxHeight: "86vh", display: "flex", flexDirection: "column", padding: "20px 22px", fontFamily: "'Inter','Mulish',sans-serif" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: p.title }}>{n} schema.org {n === 1 ? "issue" : "issues"} auto-fixed on approve</div>
                <button onClick={() => setSchemaIssues(null)} style={{ background: "none", border: "none", color: p.body, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: p.body, marginTop: 8 }}>
                The suggested schema for{" "}
                {schemaIssues.url ? <a href={schemaIssues.url} target="_blank" rel="noopener noreferrer" style={{ color: p.link, textDecoration: "underline", wordBreak: "break-all" }}>{schemaIssues.url}</a> : "this page"}{" "}
                was validated against the schema.org vocabulary. These {n === 1 ? "issue is" : "issues are"} corrected automatically when you approve the card, so the schema that gets added is clean.
              </div>
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, overflow: "auto", flex: "1 1 auto" }}>
                {schemaIssues.issues.map((msg, i) => (
                  <div key={i} style={{ background: p.rowBg, border: p.rowBorder, borderRadius: 9, padding: "9px 12px", fontSize: 12.5, lineHeight: 1.5, color: p.rowText, wordBreak: "break-word" }}>{msg}</div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                <button onClick={() => setSchemaIssues(null)} style={{ padding: "9px 18px", borderRadius: 8, border: p.border, background: "transparent", color: p.body, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Active section */}
      <section className={`sec on${REFINED_TABS.has(activeSec.id) ? " sec-refined" : ""}`} id={`sec-${activeSec.id}`}>

        {activeSec.id === "contentdrawer" ? (
          <ContentDrawer
            state={state}
            siteBrand={connInfo?.siteTitle}
            auditDate={tabCrawlDates[activeSec.id] || auditDate}
            onApprove={handleApprove}
            onIgnore={handleIgnore}
            onDone={handleDone}
            onReset={handleReset}
            hidden={hidden}
            onToggleHide={handleToggleHide}
            connected={connected}
            fetchPageTags={fetchPageTags}
            onOpenSchemaSetup={() => setSchemaSetup({ fixId: "" })}
            listToolCreatedPages={listToolCreatedPages}
            getClient={() => clientRef.current}
          />
        ) : activeSec.id === "expressschema" ? (
          // Direct-access duplicate of the Schema tab's Bulk Page Schema section. Same props
          // and same component → same localStorage state (lsg_bps_*), so it behaves identically.
          <BulkPageSchema
            state={state}
            siteBrand={connInfo?.siteTitle}
            auditDate={tabCrawlDates[activeSec.id] || auditDate}
            onApprove={handleApprove}
            onIgnore={handleIgnore}
            onDone={handleDone}
            onReset={handleReset}
            hidden={hidden}
            onToggleHide={handleToggleHide}
            connected={connected}
            fetchPageTags={fetchPageTags}
            onOpenSchemaSetup={() => setSchemaSetup({ fixId: "" })}
            onViewSchema={openSchemaView}
            onViewIssues={openSchemaIssues}
            manualSchema={manualSchema}
            onManualUpdate={handleManualUpdate}
            onPredictManual={handlePredictManual}
            getClient={() => clientRef.current}
          />
        ) : activeSec.id === "expressinternal" ? (
          // Direct-access duplicate of the Internal Links tab's Bulk Internal Links section
          // (same component + localStorage state lsg_bil_*).
          <BulkInternalLinks
            state={state}
            siteBrand={connInfo?.siteTitle}
            auditDate={tabCrawlDates[activeSec.id] || auditDate}
            onApprove={handleApprove}
            onIgnore={handleIgnore}
            onDone={handleDone}
            onReset={handleReset}
            hidden={hidden}
            onToggleHide={handleToggleHide}
            onClearFixIds={handleClearFixIds}
            connected={connected}
            fetchPageTags={fetchPageTags}
            onOpenSchemaSetup={() => setSchemaSetup({ fixId: "" })}
          />
        ) : activeSec.id === "expressmeta" ? (
          <BulkMetaData
            state={state}
            siteBrand={connInfo?.siteTitle}
            auditDate={tabCrawlDates[activeSec.id] || auditDate}
            onApprove={handleApprove}
            onIgnore={handleIgnore}
            onDone={handleDone}
            onReset={handleReset}
            hidden={hidden}
            onToggleHide={handleToggleHide}
            connected={connected}
            fetchPageTags={fetchPageTags}
            onOpenSchemaSetup={() => setSchemaSetup({ fixId: "" })}
          />
        ) : activeSec.id === "sitemap" ? (
        <>
          {/* Quick Edit help — title in the tab-title font (Oswald, uppercase, orange) with the
              how-to below it. Sits above the search bar, with space between. */}
          {sitemapHasData && (
            <div style={{ padding: "0 9px", marginBottom: 28 }}>
              <div className="cd-hd-brand" style={{ fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 20, fontWeight: 500, textTransform: "uppercase", color: "#f97316", letterSpacing: ".7px" }}>Quick Edit Sitemap</div>
              <div className="cd-help" style={{ fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: "#64748b", marginTop: 4, lineHeight: 1.5, maxWidth: 620 }}>
                To quick edit a field, hold the left mouse button on the field for one second. Changes will be published to your website when saved.
              </div>
              <div className="cd-help" style={{ fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: "#64748b", marginTop: 6, lineHeight: 1.5, maxWidth: 620 }}>
                Note: a URL shown in <strong style={{ color: "#dc2626" }}>red</strong> is redirected to another page — see the Redirected column for its destination.
              </div>
            </div>
          )}
          {/* Sitemap toolbar: Search + Exclude + Refresh selected + Sync + Run Crawl + save icon. */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end", gap: 10, padding: "3px 9px 0", marginBottom: 18 }}>
            {sitemapHasData && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 14, marginRight: "auto" }}>
                {/* Search field + Title/URL/Status Code toggle */}
                <div>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: "#64748b", fontFamily: "'Inter', 'Mulish', sans-serif", marginBottom: 4 }}>Search</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        value={sitemapSearch}
                        onChange={e => setSitemapSearch(e.target.value)}
                        placeholder={sitemapSearchField === "url" ? "Search URLs…" : sitemapSearchField === "metaTitle" ? "Search meta titles…" : sitemapSearchField === "tag" ? "Search tags…" : sitemapSearchField === "status" ? "Search status codes…" : "Search page titles…"}
                        style={{ width: 220, padding: "8px 28px 8px 12px", border: "1px solid #cbd5e1", borderRadius: 0, fontSize: 12.5, fontFamily: "'Inter', 'Mulish', sans-serif", color: "#0f172a", outline: "none" }}
                      />
                      {sitemapSearch && (
                        <button
                          onClick={() => setSitemapSearch("")}
                          data-tip="Clear search"
                          style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 15, lineHeight: 1, padding: 2 }}
                        >×</button>
                      )}
                    </div>
                    {/* Toggle: which column the search filters */}
                    <div style={{ display: "inline-flex", border: "1px solid #cbd5e1", borderRadius: 0, overflow: "hidden", flexShrink: 0 }} title="Choose which column the search filters">
                      {([["title", "Title"], ["metaTitle", "Meta Title"], ["tag", "Tag"], ["url", "URL"], ["status", "Status Code"]] as const).map(([f, label], i) => {
                        const active = sitemapSearchField === f;
                        return (
                          <button
                            key={f}
                            onClick={() => setSitemapSearchField(f)}
                            style={{ padding: "8px 12px", border: "none", borderLeft: i > 0 ? "1px solid #cbd5e1" : "none", background: active ? "#16a34a" : "#fff", color: active ? "#fff" : "#475569", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter', 'Mulish', sans-serif", whiteSpace: "nowrap" }}
                          >{label}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {/* Exclude field — comma-delimited terms removed from the searched column */}
                <div>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: "#64748b", fontFamily: "'Inter', 'Mulish', sans-serif", marginBottom: 4 }}>Exclude (comma-separated)</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      value={sitemapExclude}
                      onChange={e => setSitemapExclude(e.target.value)}
                      placeholder="e.g. blog, tag, /category/"
                      style={{ width: 220, padding: "8px 28px 8px 12px", border: "1px solid #cbd5e1", borderRadius: 0, fontSize: 12.5, fontFamily: "'Inter', 'Mulish', sans-serif", color: "#0f172a", outline: "none" }}
                    />
                    {sitemapExclude && (
                      <button
                        onClick={() => setSitemapExclude("")}
                        data-tip="Clear exclude"
                        style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 15, lineHeight: 1, padding: 2 }}
                      >×</button>
                    )}
                  </div>
                </div>
                {/* Edit Custom URLs — paste a list of URLs to bulk edit. */}
                <div>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: "transparent", fontFamily: "'Inter', 'Mulish', sans-serif", marginBottom: 4 }}>.</label>
                  <button
                    onClick={() => setSitemapCustomUrlsOpen(true)}
                    disabled={sitemapCrawling}
                    data-tip="Paste a list of URLs to bulk edit"
                    onMouseEnter={e => { if (!sitemapCrawling) e.currentTarget.style.background = "#5b21b6"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#6d28d9"; }}
                    style={{ padding: "8px 14px", background: "#6d28d9", color: "#fff", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 500, cursor: sitemapCrawling ? "not-allowed" : "pointer", fontFamily: "'Inter', 'Mulish', sans-serif", whiteSpace: "nowrap", opacity: sitemapCrawling ? 0.5 : 1, transition: "background .15s" }}
                  >✎ Bulk Edit Custom URLs</button>
                </div>
              </div>
            )}
            {sitemapSelected.size >= 2 && (
              <button
                onClick={() => setSitemapBulkOpen(true)}
                disabled={sitemapCrawling}
                data-tip="Bulk edit the selected pages (tags, status)"
                onMouseEnter={e => { if (!sitemapCrawling) e.currentTarget.style.background = "#2563eb"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#3b82f6"; }}
                style={{ padding: "8px 14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 500, cursor: sitemapCrawling ? "not-allowed" : "pointer", fontFamily: "'Inter', 'Mulish', sans-serif", whiteSpace: "nowrap", opacity: sitemapCrawling ? 0.5 : 1, transition: "background .15s" }}
              >✎ Bulk Edit ({sitemapSelected.size})</button>
            )}
            {sitemapSelected.size > 0 && (() => {
              const refreshingSel = sitemapRefreshing.size > 0;
              const busy = sitemapCrawling || refreshingSel;
              return (
              <button
                onClick={() => void handleRefreshSelectedSitemap()}
                disabled={busy}
                data-tip="Re-crawl the selected pages"
                onMouseEnter={e => { if (!busy) e.currentTarget.style.background = "#2563eb"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#3b82f6"; }}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 500, cursor: busy ? "not-allowed" : "pointer", fontFamily: "'Inter', 'Mulish', sans-serif", whiteSpace: "nowrap", opacity: busy ? 0.7 : 1, transition: "background .15s" }}
              >
                {refreshingSel
                  ? <><span style={{ display: "inline-block", animation: "seo-spin .7s linear infinite", lineHeight: 1 }}>↻</span> Refreshing…</>
                  : <>↻ Refresh Selected ({sitemapSelected.size})</>}
              </button>
              );
            })()}
            <button
              onClick={() => void handleSitemapSync()}
              disabled={sitemapCrawling || sitemapRefreshing.size > 0}
              data-tip="Add newly published pages and remove deleted ones (existing rows are kept)"
              className="sync-wp-btn"
              style={{ padding: "8px 14px", color: "#0f766e", border: "1px solid #0f766e", borderRadius: 0, fontSize: 12.5, fontWeight: 700, cursor: (sitemapCrawling || sitemapRefreshing.size > 0) ? "not-allowed" : "pointer", fontFamily: "'Inter', 'Mulish', sans-serif", whiteSpace: "nowrap", opacity: (sitemapCrawling || sitemapRefreshing.size > 0) ? 0.5 : 1, transition: "background .15s" }}
            >⇄ Sync WP Pages</button>
            <button
              className={`run-crawl-btn${sitemapCrawling ? " crawling" : ""}`}
              onClick={() => { if (sitemapCrawling) handleStopSitemap(); else void handleSitemapCrawl(); }}
              style={{
                padding: "9px 22px",
                background: sitemapCrawling ? "#dc2626" : "linear-gradient(135deg, #ea580c, #f97316)",
                color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, letterSpacing: ".3px",
                cursor: "pointer", fontFamily: "'Inter', 'Mulish', sans-serif", whiteSpace: "nowrap",
                boxShadow: sitemapCrawling ? "0 2px 8px rgba(220,38,38,.45)" : "0 2px 8px rgba(234,88,12,.4)",
                transition: "background .3s ease, box-shadow .15s",
              }}
            >
              {sitemapCrawling ? "⏹ Stop Crawl" : "Run Crawl"}
            </button>
            <div style={{ position: "relative" }} onMouseEnter={() => setShowExportTip(true)} onMouseLeave={() => setShowExportTip(false)}>
              <button
                disabled={!sitemapHasData}
                onClick={() => { if (sitemapHasData) setShowExportMenu(v => !v); }}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  padding: "9px 10px", background: "none", border: "none", borderRadius: 6, lineHeight: 0,
                  color: sitemapHasData ? "#16a34a" : "#94a3b8", cursor: sitemapHasData ? "pointer" : "not-allowed",
                  opacity: sitemapHasData ? 1 : 0.45, transition: "color .15s, transform .15s",
                }}
                onMouseEnter={e => { if (!sitemapHasData) return; e.currentTarget.style.color = "#15803d"; e.currentTarget.style.transform = "scale(1.12)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = sitemapHasData ? "#16a34a" : "#94a3b8"; e.currentTarget.style.transform = "scale(1)"; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sitemapHasData ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
              </button>
              {showExportTip && !showExportMenu && (
                <div style={{ position: "absolute", bottom: "calc(100% + 6px)", right: 0, zIndex: 600, width: 220, background: theme === "light" ? "#ffffff" : "#0f172a", border: theme === "light" ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)", borderRadius: 8, boxShadow: theme === "light" ? "0 8px 24px rgba(15,23,42,.18)" : "0 8px 24px rgba(0,0,0,.45)", padding: "9px 11px", fontSize: 12, lineHeight: 1.5, color: theme === "light" ? "#334155" : "#e2e8f0", fontFamily: "'Inter','Mulish',sans-serif", whiteSpace: "normal", textAlign: "left" }}>
                  {sitemapHasData ? "Export the sitemap table as a PDF, HTML, or CSV." : sitemapCrawling ? "Export becomes available once the crawl finishes." : "Run a crawl to enable export."}
                </div>
              )}
              {showExportMenu && sitemapHasData && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 500, background: theme === "light" ? "#ffffff" : "#0f172a", border: theme === "light" ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)", borderRadius: 8, boxShadow: theme === "light" ? "0 8px 24px rgba(15,23,42,.18)" : "0 8px 24px rgba(0,0,0,.4)", overflow: "hidden", minWidth: 160 }} onMouseLeave={() => setShowExportMenu(false)}>
                  <div style={{ padding: "8px 6px 4px 12px", fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: theme === "light" ? "#94a3b8" : "rgba(255,255,255,.3)", fontFamily: "'Inter','Mulish',sans-serif" }}>Export: Sitemap</div>
                  {[{ label: "📄  Save as PDF", fmt: "pdf" as const }, { label: "🌐  Save as HTML", fmt: "html" as const }, { label: "📊  Save as CSV", fmt: "csv" as const }].map(({ label, fmt }) => (
                    <button key={fmt} onClick={() => handleExportTab(fmt)} style={{ display: "block", width: "100%", padding: "10px 14px", background: "transparent", border: "none", color: theme === "light" ? "#334155" : "#e2e8f0", fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", fontFamily: "'Inter','Mulish',sans-serif", borderTop: theme === "light" ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.06)" }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = theme === "light" ? "#f1f5f9" : "rgba(255,255,255,.08)"; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                    >{label}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {(sitemapSearch.trim() || sitemapExclude.trim()) && sitemapVisibleRows.length === 0 && sitemapRows.length > 0 ? (
            <div style={{ padding: "40px 16px", textAlign: "center", color: "#64748b", fontFamily: "'Inter','Mulish',sans-serif" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>No matching rows</div>
              <div style={{ fontSize: 13 }}>No rows match the current search/exclude. Try different terms, switch the Title/Tag/URL/Status Code toggle, or clear the fields.</div>
            </div>
          ) : (
          <SitemapTab
            connected={connected}
            rows={sitemapVisibleRows}
            crawling={sitemapCrawling}
            progress={sitemapProgress}
            crawledAt={sitemapCrawledAt}
            selected={sitemapSelected}
            refreshing={sitemapRefreshing}
            onToggleSelect={sm_onToggleSelect}
            onToggleAll={sm_onToggleAll}
            onRefreshRow={sm_onRefreshRow}
            onEditCell={sm_onEditCell}
            onEditParent={sm_onEditParent}
            onEditPage={sm_onEditPage}
            redirectByPath={sitemapRedirectByPath}
          />
          )}
          {sitemapEditPage && (() => {
            const row = sitemapRows.find(r => `${r.type}-${r.id}` === sitemapEditPage);
            if (!row) return null;
            return (
              <EditPageDialog
                row={row}
                getClient={() => clientRef.current}
                plugin={connInfo?.plugin ?? "unknown"}
                builder={connInfo?.builder ?? "unknown"}
                siteUrl={connInfo?.siteUrl || creds.siteUrl}
                theme={theme}
                onClose={() => setSitemapEditPage(null)}
                onSaved={() => {
                  const key = sitemapEditPage;
                  setSitemapEditPage(null);
                  setAuditToast({ type: "success", message: "✓ Page updated successfully." });
                  if (key) void refreshSitemapRows([key]);
                  reloadSitemapRedirects(); // keep the Redirected column current after a redirect add/update/remove
                }}
              />
            );
          })()}
          {sitemapBulkOpen && (
            <SitemapBulkEditDialog
              count={sitemapSelected.size}
              pages={sitemapRows.filter(r => r.type === "page").map(r => {
                let label = r.slug || String(r.id);
                try { label = new URL(r.url).pathname.replace(/\/$/, "") || "/"; } catch { /* keep slug fallback */ }
                return { id: r.id, label };
              })}
              theme={theme}
              saving={sitemapBulkSaving}
              onClose={() => { if (!sitemapBulkSaving) setSitemapBulkOpen(false); }}
              onApply={vals => void handleSitemapBulkEdit(vals)}
            />
          )}
          {sitemapCustomUrlsOpen && (
            <SitemapBulkEditDialog
              count={0}
              pages={sitemapRows.filter(r => r.type === "page").map(r => {
                let label = r.slug || String(r.id);
                try { label = new URL(r.url).pathname.replace(/\/$/, "") || "/"; } catch { /* keep slug fallback */ }
                return { id: r.id, label };
              })}
              theme={theme}
              saving={sitemapBulkSaving}
              title="Bulk Edit Custom URLs"
              intro="Paste the URLs to edit (one per line). Each is matched to a page/post in the sitemap; the fields you set are applied to every match."
              showUrls
              onClose={() => { if (!sitemapBulkSaving) setSitemapCustomUrlsOpen(false); }}
              onApply={vals => void handleSitemapCustomUrlsEdit(vals)}
            />
          )}
          {sitemapParentEdit && (() => {
            const row = sitemapRows.find(r => `${r.type}-${r.id}` === sitemapParentEdit);
            const pages = sitemapRows.filter(r => r.type === "page" && `${r.type}-${r.id}` !== sitemapParentEdit).map(r => {
              let label = r.slug || String(r.id);
              try { label = new URL(r.url).pathname.replace(/\/$/, "") || "/"; } catch { /* keep slug fallback */ }
              return { id: r.id, label };
            });
            // Prefill the Redirect Page field if this page already redirects somewhere.
            const rdKey = (u: string) => { try { return (new URL(u).pathname.replace(/\/+$/, "") || "/").toLowerCase(); } catch { return (u.replace(/[#?].*$/, "").replace(/\/+$/, "") || "/").toLowerCase(); } };
            const existingRedirectDest = row ? (sitemapRedirectByPath.get(rdKey(row.url)) ?? "") : "";
            return (
              <SitemapBulkEditDialog
                count={1}
                pages={pages}
                title="Edit Page URL"
                intro={row ? `Change the parent page and/or URL slug for “${row.title || row.url}”. Its URL will change and an old→new 301 redirect is created automatically (and added to the Redirection Manager).` : undefined}
                showTags={false}
                showStatus={false}
                showSlug
                initialSlug={row?.slug ?? ""}
                initialRedirect={existingRedirectDest}
                parentDisabled={row?.type === "post"}
                theme={theme}
                saving={sitemapBulkSaving}
                onClose={() => { if (!sitemapBulkSaving) setSitemapParentEdit(null); }}
                onApply={vals => void handleSitemapParentEdit(vals)}
              />
            );
          })()}
        </>
        ) : activeSec.id === "lowhangingkw" ? (
          <LowHangingKw
            state={state}
            siteUrl={creds.siteUrl}
            siteBrand={connInfo?.siteTitle}
            connected={connected}
            onIgnore={handleIgnore}
            onDone={handleDone}
            onReset={handleReset}
            getClient={() => clientRef.current}
          />
        ) : activeSec.id === "redirectionmanager" ? (
          <>
            {/* Connect from this tab: the same Site Storage + WP Connection panel as the cover,
                in a full-width band that matches the cover's background. Negative margins break
                out of the .sec padding (top -38, sides -52) so the band spans edge-to-edge and
                the Site Storage label sits the same ~18px from the top as it does on the cover. */}
            <div className="rm-connect-band" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: -38, marginLeft: -52, marginRight: -52, marginBottom: 24, padding: "18px 96px 20px 52px" }}>
              <PowerToolHeader label="Redirection Manager" />
              <CredentialsBar
                creds={creds}
                onChange={handleCredsChange}
                info={connInfo}
                connected={connected}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onSwitchSite={onSwitchSite} connecting={autoConnecting}
                sitesRefreshToken={sitesRefreshToken}
                coverMode
                connectOnly
              />
            </div>
            <RedirectionManager getClient={() => clientRef.current} connected={connected} onToast={setAuditToast} initialRows={sitemapRedirects} onRedirectsChanged={reloadSitemapRedirects} />
          </>
        ) : activeSec.id === "llmtracking" ? (
          <LlmMentionTracking siteUrl={creds.siteUrl} siteBrand={connInfo?.siteTitle} />
        ) : activeSec.id === "organicrank" ? (
          <>
            {/* Same connect band as the other Power Tool tabs so users can connect / switch the
                website right from this tab. Negative margins break out of the .sec padding. */}
            <div className="rm-connect-band" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: -38, marginLeft: -52, marginRight: -52, marginBottom: 24, padding: "18px 96px 20px 52px" }}>
              <PowerToolHeader label="Organic Rank Tracker" />
              <CredentialsBar
                creds={creds}
                onChange={handleCredsChange}
                info={connInfo}
                connected={connected}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onSwitchSite={onSwitchSite} connecting={autoConnecting}
                sitesRefreshToken={sitesRefreshToken}
                coverMode
                connectOnly
              />
            </div>
            <OrganicRankTracker siteUrl={creds.siteUrl} siteBrand={connInfo?.siteTitle} refreshToken={sitesRefreshToken} />
          </>
        ) : activeSec.id === "llmvisibility" ? (
          <>
            <div className="rm-connect-band" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: -38, marginLeft: -52, marginRight: -52, marginBottom: 24, padding: "18px 96px 20px 52px" }}>
              <PowerToolHeader label="LLM Visibility Scanner" />
              <CredentialsBar
                creds={creds}
                onChange={handleCredsChange}
                info={connInfo}
                connected={connected}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onSwitchSite={onSwitchSite} connecting={autoConnecting}
                sitesRefreshToken={sitesRefreshToken}
                coverMode
                connectOnly
              />
            </div>
            <LlmVisibilityScanner siteUrl={creds.siteUrl} siteBrand={connInfo?.siteTitle} refreshToken={sitesRefreshToken} />
          </>
        ) : activeSec.id === "gbpreviews" ? (
          <GbpReviewManager
            siteUrl={creds.siteUrl}
            siteBrand={connInfo?.siteTitle}
            onOpenSettings={() => { setSettingsInitialCat("savedsites"); setShowSidebarSettings(true); }}
            refreshToken={sitesRefreshToken}
          />
        ) : activeSec.id === "servicearea" ? (
          <>
            {/* Same connect band as the Redirection Manager tab so users can connect / switch
                the website right from this tab. Negative margins break out of the .sec padding. */}
            <div className="rm-connect-band" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: -38, marginLeft: -52, marginRight: -52, marginBottom: 24, padding: "18px 96px 20px 52px" }}>
              <PowerToolHeader label="Service Area Buildout" />
              <CredentialsBar
                creds={creds}
                onChange={handleCredsChange}
                info={connInfo}
                connected={connected}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onSwitchSite={onSwitchSite} connecting={autoConnecting}
                sitesRefreshToken={sitesRefreshToken}
                coverMode
                connectOnly
              />
            </div>
          <ServiceAreaBuildout
            state={state}
            siteUrl={creds.siteUrl}
            siteBrand={connInfo?.siteTitle}
            auditDate={tabCrawlDates[activeSec.id] || auditDate}
            onApprove={handleApprove}
            onIgnore={handleIgnore}
            onDone={handleDone}
            onReset={handleReset}
            hidden={hidden}
            onToggleHide={handleToggleHide}
            connected={connected}
            fetchPageTags={fetchPageTags}
            onOpenSchemaSetup={() => setSchemaSetup({ fixId: "" })}
            getClient={() => clientRef.current}
          />
          </>
        ) : activeSec.id === "conversational" ? (
          // Full-height chat: the connect band is pinned to the top and the composer is locked to
          // the bottom (inside ConversationalSeo), with the message list as the only scroll region.
          // The wrapper fills the viewport and cancels the .sec padding (-38/-52) so the page itself
          // never scrolls — leaving exactly one scrollbar, the conversation's.
          <div style={{ display: "flex", flexDirection: "column", height: "100vh", margin: "-38px -52px" }}>
            {/* Connect band pinned to the top so it stays visible while the conversation scrolls. */}
            <div className="rm-connect-band" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0, padding: "18px 96px 20px 52px" }}>
              <PowerToolHeader label="Conversational SEO" />
              <CredentialsBar
                creds={creds}
                onChange={handleCredsChange}
                info={connInfo}
                connected={connected}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onSwitchSite={onSwitchSite} connecting={autoConnecting}
                sitesRefreshToken={sitesRefreshToken}
                coverMode
                connectOnly
              />
            </div>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "16px 52px 20px" }}>
              <ConversationalSeo siteUrl={creds.siteUrl} siteBrand={connInfo?.siteTitle} connected={connected} getClient={() => clientRef.current} plugin={connInfo?.plugin} />
            </div>
          </div>
        ) : activeSec.id === "citationcheck" ? (
          <CitationCheck siteUrl={creds.siteUrl} siteBrand={connInfo?.siteTitle} />
        ) : activeSec.id === "nicheadsscraper" ? (
          <>
            <div className="rm-connect-band" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: -38, marginLeft: -52, marginRight: -52, marginBottom: 24, padding: "18px 96px 20px 52px" }}>
              <PowerToolHeader label="Niche Ads Scraper" />
              <CredentialsBar
                creds={creds}
                onChange={handleCredsChange}
                info={connInfo}
                connected={connected}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onSwitchSite={onSwitchSite} connecting={autoConnecting}
                sitesRefreshToken={sitesRefreshToken}
                coverMode
                connectOnly
              />
            </div>
            <NicheAdsScraper siteUrl={creds.siteUrl} siteBrand={connInfo?.siteTitle} refreshToken={sitesRefreshToken} />
          </>
        ) : activeSec.id === "topicalcoverage" ? (
          <>
            <div className="rm-connect-band" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: -38, marginLeft: -52, marginRight: -52, marginBottom: 24, padding: "18px 96px 20px 52px" }}>
              <PowerToolHeader label="Topical Coverage Engine" />
              <CredentialsBar
                creds={creds}
                onChange={handleCredsChange}
                info={connInfo}
                connected={connected}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onSwitchSite={onSwitchSite} connecting={autoConnecting}
                sitesRefreshToken={sitesRefreshToken}
                coverMode
                connectOnly
              />
            </div>
            <TopicalCoverageEngine
              siteUrl={creds.siteUrl}
              state={state}
              siteBrand={connInfo?.siteTitle}
              auditDate={tabCrawlDates[activeSec.id] || auditDate}
              onApprove={handleApprove}
              onIgnore={handleIgnore}
              onDone={handleDone}
              onReset={handleReset}
              hidden={hidden}
              onToggleHide={handleToggleHide}
              connected={connected}
              fetchPageTags={fetchPageTags}
              onOpenSchemaSetup={() => setSchemaSetup({ fixId: "" })}
              getClient={() => clientRef.current}
            />
          </>
        ) : activeSec.id === "history" ? (
          <HistoryTab siteUrl={creds.siteUrl} refreshToken={sitesRefreshToken} />
        ) : (
        <>
        {/* Universal tab toolbar: Run Crawl + Last Audit Date — same position on every tab */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 10, padding: "3px 9px 0", marginBottom: 30,
        }}>
          {tabCrawlDates[activeSec.id] && (
            <span style={{
              fontSize: 10, color: "var(--ink4, rgba(0,0,0,.4))",
              fontFamily: "Roboto Mono, monospace", whiteSpace: "nowrap",
            }}>
              Last Audit Date: {tabCrawlDates[activeSec.id]}
            </span>
          )}
          {(() => {
            // Only THIS tab's button reflects THIS tab's crawl — other tabs stay
            // clickable so multiple tabs can crawl concurrently.
            const thisTabCrawling = crawlingSections.has(activeSec.id)
              || (activeSec.id === "competitors" && competitorsCrawling);
            // Interrupted (window/tab closed or server disconnected mid-crawl) → blue Resume.
            const thisTabInterrupted = !thisTabCrawling && interruptedSections.has(activeSec.id);
            const thisTabQueued = crawlQueue.some(q => q.sectionId === activeSec.id);
            const queuePos = crawlQueue.findIndex(q => q.sectionId === activeSec.id) + 1;
            return (
            <button
              className={`run-crawl-btn${thisTabCrawling ? " crawling" : ""}`}
              title={thisTabQueued ? "Queued — click to remove from the queue" : undefined}
              onClick={async () => {
                // While crawling, the button stops this tab's crawl.
                if (thisTabCrawling) { handleStopCrawl(activeSec.id); return; }
                // Queued → clicking removes it from the queue.
                if (thisTabQueued) { cancelQueuedCrawl(activeSec.id); return; }
                // Interrupted → Resume the saved crawl (no confirm; morphs back to red Stop Crawl).
                if (thisTabInterrupted) { handleResumeCrawl(activeSec.id); return; }
                // At the concurrency cap → queue this crawl (it auto-starts when a slot frees)
                // instead of blocking it.
                if (crawlingSections.size >= MAX_CONCURRENT_CRAWLS && !crawlingSections.has(activeSec.id)) {
                  const ok = await showConfirm(
                    `Up to ${MAX_CONCURRENT_CRAWLS} crawls run at the same time to avoid API rate-limiting. This crawl will be added to the queue and will start automatically as soon as one of the running crawls finishes. Queued crawls run in the order they were started.`,
                    { title: "Add to Crawl Queue", confirmLabel: "Add to Queue", cancelLabel: "Cancel", tone: "info" },
                  );
                  if (ok) enqueueCrawl(activeSec.id, activeSec.label);
                  return;
                }
                setCrawlConfirm({ sectionId: activeSec.id, label: activeSec.label });
              }}
              style={{
                padding: "9px 22px",
                // While crawling, the .crawling CSS class paints an animated indeterminate
                // bar (overrides this background). This solid red is just a fallback.
                background: thisTabCrawling
                  ? "#dc2626"
                  : thisTabQueued
                  ? "linear-gradient(135deg, #d97706, #f59e0b)"
                  : thisTabInterrupted
                  ? "linear-gradient(135deg, #1d4ed8, #2563eb)"
                  : "linear-gradient(135deg, #ea580c, #f97316)",
                color: "#fff",
                border: "none",
                borderRadius: 0,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: ".3px",
                cursor: "pointer",
                fontFamily: "'Inter', 'Mulish', sans-serif",
                whiteSpace: "nowrap",
                opacity: 1,
                boxShadow: thisTabCrawling
                  ? "0 2px 8px rgba(220,38,38,.45)"
                  : thisTabQueued
                  ? "0 2px 8px rgba(217,119,6,.45)"
                  : thisTabInterrupted
                  ? "0 2px 8px rgba(37,99,235,.45)"
                  : "0 2px 8px rgba(234,88,12,.4)",
                transition: "background .3s ease, box-shadow .15s",
              }}
            >
              {thisTabCrawling ? "⏹ Stop Crawl" : thisTabQueued ? `⏳ Queued${queuePos ? ` #${queuePos}` : ""} — Cancel` : thisTabInterrupted ? "↻ Resume" : "Run Crawl"}
            </button>
            );
          })()}

          {/* Export button — disabled until this tab has crawled data */}
          <div style={{ position: "relative" }}
            onMouseEnter={() => setShowExportTip(true)}
            onMouseLeave={() => setShowExportTip(false)}
          >
            <button
              disabled={!activeTabHasData}
              onClick={() => { if (activeTabHasData) setShowExportMenu(v => !v); }}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                padding: "9px 10px", background: "none", border: "none", borderRadius: 6, lineHeight: 0,
                color: activeTabHasData ? "#16a34a" : "#94a3b8",
                cursor: activeTabHasData ? "pointer" : "not-allowed", boxShadow: "none",
                opacity: activeTabHasData ? 1 : 0.45, filter: "none",
                transition: "color .15s, transform .15s",
              }}
              onMouseEnter={e => { if (!activeTabHasData) return; e.currentTarget.style.color = "#15803d"; e.currentTarget.style.transform = "scale(1.12)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = activeTabHasData ? "#16a34a" : "#94a3b8"; e.currentTarget.style.transform = "scale(1)"; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={activeTabHasData ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
            </button>
            {showExportTip && !showExportMenu && (
              <div style={{
                position: "absolute", bottom: "calc(100% + 6px)", right: 0, zIndex: 600, width: 220,
                background: theme === "light" ? "#ffffff" : "#0f172a",
                border: theme === "light" ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)",
                borderRadius: 8, boxShadow: theme === "light" ? "0 8px 24px rgba(15,23,42,.18)" : "0 8px 24px rgba(0,0,0,.45)",
                padding: "9px 11px", fontSize: 12, lineHeight: 1.5, color: theme === "light" ? "#334155" : "#e2e8f0",
                fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, whiteSpace: "normal", textAlign: "left",
              }}>
                {activeTabHasData ? "Export this tab as a PDF or HTML." : activeTabCrawling ? "Export becomes available once the crawl finishes." : "Run a crawl on this tab to enable export."}
              </div>
            )}
            {showExportMenu && activeTabHasData && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 500,
                background: theme === "light" ? "#ffffff" : "#0f172a",
                border: theme === "light" ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)", borderRadius: 8,
                boxShadow: theme === "light" ? "0 8px 24px rgba(15,23,42,.18)" : "0 8px 24px rgba(0,0,0,.4)", overflow: "hidden", minWidth: 160,
              }}
                onMouseLeave={() => setShowExportMenu(false)}
              >
                <div style={{ padding: "8px 6px 4px 12px", fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" as const, color: theme === "light" ? "#94a3b8" : "rgba(255,255,255,.3)", fontFamily: "'Inter','Mulish',sans-serif" }}>
                  Export: {activeSec.label}
                </div>
                {[{ label: "📄  Save as PDF", fmt: "pdf" as const }, { label: "🌐  Save as HTML", fmt: "html" as const }].map(({ label, fmt }) => (
                  <button key={fmt} onClick={() => handleExportTab(fmt)} style={{
                    display: "block", width: "100%", padding: "10px 14px", background: "transparent",
                    border: "none", color: theme === "light" ? "#334155" : "#e2e8f0", fontSize: 13, fontWeight: 500, cursor: "pointer",
                    textAlign: "left" as const, fontFamily: "'Inter','Mulish',sans-serif",
                    borderTop: theme === "light" ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.06)",
                  }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = theme === "light" ? "#f1f5f9" : "rgba(255,255,255,.08)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                  >{label}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        <AuditSection
          id={activeSec.id}
          html={(() => {
            const stored = regeneratedSections[activeSec.id];
            // Only show PLACEHOLDER for the specific tab being crawled — other tabs keep their content.
            // During a full audit, a tab shows the placeholder ONLY until ITS section has streamed in;
            // completed tabs become viewable immediately even while later tabs are still generating.
            const isThisTabCrawling = activeTabCrawling;
            // Never fall back to hardcoded static HTML — always show awaiting-crawl state if no data
            const base = isThisTabCrawling
              ? PLACEHOLDER_HTML
              : (stored && stored.trim().length > 50 ? stored : EMPTY_SECTION_HTML);
            // For the competitors tab (when NOT mid-crawl), ensure the brand &
            // page-count shells exist so the result useEffects can populate them.
            // While crawling we show only the placeholder — never old/blank data.
            if (activeSec.id === "competitors" && !isThisTabCrawling) {
              let out = base;

              // Inject Brand & URL Mention section if missing
              if (!out.includes('id="bm-tbody"')) {
                const marker = '<div class="sub-lbl">Brand &amp; URL Mention Landscape';
                const start  = HTML_COMPETITORS.indexOf(marker);
                const brandSection = start !== -1 ? HTML_COMPETITORS.slice(HTML_COMPETITORS.lastIndexOf('<div class="sub">', start)) : null;
                if (brandSection) {
                  const pageInvIdx = out.indexOf("Page Inventory");
                  const advIdx     = out.indexOf("Top Competitor Advantages");
                  const anchorIdx  = pageInvIdx !== -1 ? pageInvIdx : advIdx;
                  if (anchorIdx !== -1) {
                    const subDivIdx = out.lastIndexOf('<div class="sub"', anchorIdx);
                    if (subDivIdx !== -1) out = out.slice(0, subDivIdx) + brandSection + "\n" + out.slice(subDivIdx);
                    else out = out + "\n" + brandSection;
                  } else {
                    out = out + "\n" + brandSection;
                  }
                }
              }

              return out;
            }
            return base;
          })()}
          state={state}
          siteBrand={connInfo?.siteTitle}
          auditDate={tabCrawlDates[activeSec.id] || auditDate}
          onApprove={handleApprove}
          onIgnore={handleIgnore}
          onDone={handleDone}
          onReset={handleReset}
          hidden={hidden}
          onToggleHide={handleToggleHide}
          connected={connected}
          fetchPageTags={fetchPageTags}
          onOpenSchemaSetup={() => setSchemaSetup({ fixId: "" })}
          onViewSchema={openSchemaView}
          onViewIssues={openSchemaIssues}
          manualSchema={manualSchema}
          onManualUpdate={handleManualUpdate}
          onPredictManual={handlePredictManual}
          getClient={() => clientRef.current}
        />
        {/* Cannibalization tab: GSC-powered keyword-cannibalization + SERP-flux analyses. */}
        {activeSec.id === "cannibal" && !activeTabCrawling && (
          <CannibalizationTools siteUrl={creds.siteUrl} />
        )}
        {/* Schema tab: history of pages this tool has added schema to (via the _audit_schema meta). */}
        {activeSec.id === "schema" && !activeTabCrawling && (
          <SchemaHistory connected={connected} getClient={() => clientRef.current} />
        )}
        {/* Crawl status — rendered inline, centered beneath the "Live Crawl Running"
            placeholder (not as a viewport-fixed toast, which overlapped it and sat
            off-center because of the sidebar). Only while THIS tab shows the placeholder.
            Suppressed for a single-tab Internal Links crawl (the "Generating internal…"
            message is unwanted there); full-audit progress on that tab still shows. */}
        {activeTabCrawling && auditToast && !(activeSec.id === "internal" && crawlingSections.has(activeSec.id)) && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 18, marginBottom: 4 }}>
            {renderAuditStatus(false)}
          </div>
        )}
        {/* Bulk Page Schema, Bulk Internal Links, and Express Meta Data are now reachable
            only via their dedicated Power Tools tabs (Express Schema / Express Internal
            Links / Express Meta Data) — removed from the audit tabs to avoid duplication. */}
        </>
        )}
      </section>
      </div>{/* end app-content */}
    </>
  );
}
